import { createContext, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check, Star, Loader2, X } from 'lucide-react'
import type {
  AccordionBlock, Align, Background, Block, BreadcrumbBlock, ButtonClick, ButtonGroupBlock, ButtonBlock, ButtonSpec,
  AuthFormBlock, AuthMenuBlock, AuthMenuLink, CalloutBlock, CalloutIconPosition, CalloutIconSize, ChatbotBlock,
  CardBlock, CardGridBlock, CardSpec, CardStatus, CardStyle, CartBadgeBlock, CartLinesBlock, CartSummaryBlock,
  WishlistButtonBlock, WishlistBadgeBlock, WishlistLinesBlock,
  ColumnContent, ColumnsBlock,
  CountdownBlock, CountdownLabels,
  ContactFormBlock, ContainerBlock, CtaBannerBlock, FaqBlock,
  FeatureGridBlock, FilterBarBlock, GalleryBlock, HeroBlock, HeroSlide, HeroContentPosition, ImageBlock, LogoCloudBlock,
  MapBlock, NavMenuBlock, NavMenuItem, NewsletterBlock, SiteMenuEntry, SiteMenusSpec, PageCartSource, PopupTrigger, PopupModalPosition, PopupAnimation, PricingBlock, RepeatLayout, SpacerBlock, StatsBlock, TabsBlock,
  TestimonialBlock, TestimonialItem, TimelineBlock, VideoBlock,
} from '@twasta/page-schema'
import { substituteVars, type VarSource } from './actions/substitute'
import type { CartLine } from './cart'
import { applyBindings, type ResolveScope } from './bindings'
import { usePageRuntime } from './actions/runtime'
import { MarkdownText } from './markdown'
import {
  bridgeSdkScript, useCustomCodeBridge, useCustomCodeBridgeCaps,
  type CustomCodeBridgeConfig,
} from './customCodeBridge'
import { buildCustomFrameDoc, useCustomFrame, type CustomComponentSpec } from './customFrame'
import { resolveIcon, normalizeIconName, PageIcon, isVectorIcon } from './iconMap'
import { initFilterBars } from './scripts'
import { addToCart, cartDraft, initCart, readCart, setCartScope } from './cart'
import { initWishlist } from './wishlist'
import { useRepeatCell } from './ThemedPageShell'
import { IdentifyStep, type IdentifyAdapter } from './identifyStep'
import {
  RESPONSIVE_BREAKPOINTS, bgHasFill, navDrawerCss, aspectRatioCss, IMAGE_MASKS,
  imageHoverCss, hasVideoBackground, gradientValue, ITEM_CLASS, SHADOWS, themeRem,
} from './theme'

// ─── Repeat scope ─────────────────────────────────────
// The stack of rows a block sits inside, innermost last. `item_field`
// bindings resolve against it, so EVERY surface that repeats has to
// publish it — the in-app preview kept its own copy of this context and
// was for a long time the only surface that applied `bindings` at all,
// which meant a data-bound card showed its authored placeholder text
// everywhere else, including on the published site.
export const BlockScopeContext = createContext<ResolveScope[]>([])
export function useBlockScopes(): ResolveScope[] { return useContext(BlockScopeContext) }

/**
 * The page vars a REPEATED copy sees: the page's own, plus its row under
 * the repeat's alias.
 *
 * Two things read a row, and both have to work. `bindings` walk the scope
 * stack (`item_field`), while `{{alias.COLUMN}}` tokens inside any text,
 * href or action payload go through `substituteVars` — which only sees
 * PageVars. A surface that pushes the row onto the scope stack but not
 * into the vars renders every `{{p.SLUG}}` literally, which is what the
 * in-app preview did: the published site and the downloaded runtime each
 * had their own copy of this, the in-app repeat had neither, and the
 * catalogue's "View" links read `/{{p.SLUG}}`.
 *
 * Keys are emitted in BOTH cases. An author writes the column as the MODEL
 * spells it (UNIT_PRICE) while the executor returns the form key
 * (unit_price); `item_field` bindings already match either way, and a
 * `{{p.COL}}` token that did not made the two look randomly unreliable.
 */
export function repeatRowKeys(alias: string, row: unknown): Record<string, unknown> {
  const out: Record<string, unknown> = { [alias]: row }
  if (!row || typeof row !== 'object') return out
  for (const [k, v] of Object.entries(row as Record<string, unknown>)) {
    out[`${alias}.${k}`] = v
    out[`${alias}.${k.toUpperCase()}`] = v
    out[`${alias}.${k.toLowerCase()}`] = v
  }
  return out
}

/** Record form, for a surface whose page vars are a plain map (the
 *  published site's SSR, the downloaded runtime). */
export function repeatRowVars(
  alias: string,
  row: unknown,
  vars?: Record<string, unknown>,
): Record<string, unknown> {
  return { ...(vars || {}), ...repeatRowKeys(alias, row) }
}

/**
 * Resolver form, for a surface whose page vars are a FUNCTION.
 *
 * The in-app runtime resolves `{{…}}` through a resolver, not a map — it
 * is what serves `{{action.loading}}`, `{{inputs.x}}` and the rest — so a
 * repeat there cannot simply provide a record in its place without taking
 * all of those away from the blocks inside it. The row answers first,
 * everything else falls through to what the surface already resolved,
 * which is also what chains a nested repeat to its parent's alias.
 */
export function repeatRowSource(alias: string, row: unknown, base: VarSource): VarSource {
  const keys = repeatRowKeys(alias, row)
  return (key: string) => {
    if (Object.prototype.hasOwnProperty.call(keys, key)) return keys[key]
    return typeof base === 'function'
      ? base(key)
      : (base && Object.prototype.hasOwnProperty.call(base, key) ? base[key] : undefined)
  }
}

/** Resolve a block's `bindings` against the page's data and the row stack. */
export function bindBlock<T extends Block>(
  block: T,
  vars: Record<string, unknown> | undefined,
  resources: Record<string, unknown> | undefined,
  scopes: ResolveScope[],
): T {
  if (!block.bindings || Object.keys(block.bindings).length === 0) return block
  try {
    return applyBindings(block, { inputs: vars, resources, scopes })
  } catch {
    // A bad binding drops back to the authored value rather than taking
    // the block — and the page — down with it.
    return block
  }
}

// ─── Variable substitution context ────────────────────
// PageVars is a map (or resolver) of {{key}} → value used by every renderer
// for substitution at render time. Unknown tokens are left in place.

export type PageVars = VarSource

export const PageVarsContext = createContext<PageVars>({})

// Re-export for back-compat with any external importers.
export { substituteVars }

// ─── Site link resolution ─────────────────────────────
// Site-internal navigation links (page buttons + raw anchors in custom_html
// / notepad) are authored as root-relative paths like "/tickets-pricing".
// Each render surface serves the site under a DIFFERENT base path, so a
// hard-coded "/slug" only works on the deployed static site (served at the
// domain root). The in-app preview serves it under
// /site/<user>/<project>/<slug>/, and the standalone runtime under
// /<project>/. `resolveHref` rewrites a site-internal href into the path
// appropriate for the current surface; each surface provides its own. The
// default is identity (deployed/root).

export interface SiteLink {
  resolveHref: (href: string) => string
  /** The URL of a page of this surface by its OBJECT name ("/contact"), or
   *  null when it is not one of the surface's pages. Lets a button that
   *  `navigate`s to a page emit a real href — the only thing that works on
   *  the static site, which runs no page runtime. */
  pageHref?: (objName: string) => string | null
}

export const SiteLinkContext = createContext<SiteLink>({ resolveHref: (h) => h })
export function useSiteLink(): SiteLink { return useContext(SiteLinkContext) }

// True for a same-origin, root-relative path we should rewrite ("/x", "/x/y").
// Skips protocol-relative ("//host"), absolute (http:, mailto:, tel:),
// in-page anchors ("#"), and query-only ("?") links.
export function isInternalHref(href: string): boolean {
  if (!href) return false
  if (href.startsWith('//')) return false
  return href.startsWith('/')
}

// True for an in-page anchor ("#pricing"). These are NOT rewritten by
// `resolveHref` — they never leave the page — but they ARE navigable, which
// is why nav links and buttons have to handle them explicitly. Before anchor
// support existed they were excluded everywhere, so a one-page landing site
// whose nav jumps to sections (the defining pattern of the page style this
// runtime is for) simply could not be built.
export function isAnchorHref(href: string): boolean {
  return !!href && href.startsWith('#') && href.length > 1
}

// Rewrite root-relative <a href="/x"> (and href='/x') in a raw HTML string
// through `resolveHref`. Leaves external/absolute/anchor links untouched.
export function rewriteInternalHrefs(html: string, resolveHref: (h: string) => string): string {
  if (!html) return html
  return html.replace(
    /(\shref\s*=\s*)("|')(\/[^"']*)\2/gi,
    (m, pre: string, q: string, href: string) => {
      if (!isInternalHref(href)) return m
      return `${pre}${q}${resolveHref(href)}${q}`
    },
  )
}

// A site page as known to a render surface (manifest entry / nav node).
export interface SitePageRef {
  slug: string
  obj_name?: string
  page_obj_name?: string
  is_home?: boolean
}

// Resolve a root-relative site link (e.g. "/tickets-pricing") to the page it
// refers to. Tolerant of "nice" shortened slugs that drop the site's
// obj_name prefix — authors and the AI assistant often write "/about" for a
// page whose real slug is "concert-x1-about" / obj_name "concert_x1_about".
// Matching order: exact slug → exact obj_name → slug suffix → obj_name
// suffix. Returns null for external or unmatched links (caller falls back).
export function matchSitePage(href: string, pages: SitePageRef[] | undefined): SitePageRef | null {
  if (!isInternalHref(href) || !pages || !pages.length) return null
  const seg = href.replace(/[?#].*$/, '').replace(/^\/+|\/+$/g, '')
  const objOf = (p: SitePageRef) => p.obj_name || p.page_obj_name || ''
  if (seg === '') return pages.find((p) => p.is_home) || null
  const segObj = seg.replace(/-/g, '_')
  return (
    pages.find((p) => p.slug === seg) ||
    pages.find((p) => objOf(p) === seg || objOf(p) === segObj) ||
    pages.find((p) => !!p.slug && (p.slug === seg || p.slug.endsWith(`-${seg}`))) ||
    pages.find((p) => { const o = objOf(p); return !!o && (o === segObj || o.endsWith(`_${segObj}`)) }) ||
    null
  )
}

// ─── Site navigation menu (hierarchical, multi-level) ─────────────────
// The hierarchical site menu: pages and folder groups (a '-' nav folder
// becomes a `group` with `children`, recursively). Each render surface
// (in-app viewer, SSR, standalone runtime, designer canvas) provides this
// tree via SiteMenuContext so the `nav_menu` block renders the live site
// navigation with dropdown submenus.
export type SiteMenuItem =
  | { type: 'page'; page_obj_name: string; slug: string; label: string }
  | { type: 'group'; label: string; obj_name?: string; children: SiteMenuItem[] }
  | { type: 'link'; label: string; href: string }

export const SiteMenuContext = createContext<SiteMenuItem[]>([])
export function useSiteMenu(): SiteMenuItem[] { return useContext(SiteMenuContext) }

// ─── Named site menus ─────────────────────────────────────────────────
// The site's NAMED menus (`functional_specs.menus` on the External Website
// node), already resolved against the surface's own page list. A `nav_menu`
// with `menu: <name>` renders one of these. Every surface resolves the raw
// spec with `resolveSiteMenus` — the ONE place a menu entry becomes a link —
// and provides the result here.
export type SiteMenus = Record<string, SiteMenuItem[]>
export const SiteMenusContext = createContext<SiteMenus>({})
export function useSiteMenus(): SiteMenus { return useContext(SiteMenusContext) }

/** A routable site page as any surface describes it. The backend manifest
 *  says `page_obj_name` + `nav_label`; the downloadable runtimes say
 *  `obj_name` + `label`. */
export interface SiteMenuPageRef {
  page_obj_name?: string
  obj_name?: string
  slug?: string
  is_home?: boolean
  nav_label?: string
  label?: string
  title?: string
  collection?: { role?: string | null } | null
}

/** Resolve the raw `menus` spec into renderable menus using the surface's own
 *  page list (each surface has its own slug scheme). A page entry naming a
 *  page the surface does not route — a deleted page, a header/footer page, or
 *  a collection TEMPLATE (one page per record, never a destination itself) —
 *  is dropped; an empty dropdown is dropped with it. */
export function resolveSiteMenus(
  raw: SiteMenusSpec | null | undefined,
  pages: SiteMenuPageRef[] | null | undefined,
): SiteMenus {
  const out: SiteMenus = {}
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out
  const byObj = new Map<string, SiteMenuPageRef>()
  for (const p of pages || []) {
    const obj = p?.page_obj_name || p?.obj_name
    if (!obj) continue
    const role = p.collection && typeof p.collection === 'object' ? p.collection.role : undefined
    if (role === 'item' || role === 'taxonomy') continue
    byObj.set(obj, p)
  }
  const walk = (entries: SiteMenuEntry[] | undefined, depth: number): SiteMenuItem[] => {
    const items: SiteMenuItem[] = []
    if (!Array.isArray(entries) || depth > 8) return items
    for (const e of entries) {
      if (!e || typeof e !== 'object') continue
      if (Array.isArray(e.children) && e.children.length && !e.page) {
        const children = walk(e.children, depth + 1)
        if (children.length) items.push({ type: 'group', label: e.label || 'Menu', children })
      } else if (e.page) {
        const p = byObj.get(e.page)
        if (!p) continue
        items.push({
          type: 'page',
          page_obj_name: e.page,
          slug: p.is_home ? '' : (p.slug || ''),
          label: e.label || p.nav_label || p.label || p.title || e.page,
        })
      } else if (e.href) {
        items.push({ type: 'link', label: e.label || e.href, href: e.href })
      }
    }
    return items
  }
  for (const [name, entries] of Object.entries(raw)) {
    if (name) out[name] = walk(entries, 0)
  }
  return out
}

// ─── Auth-menu adapter ────────────────────────────────────────────────
// The `auth_menu` block is presentational: it renders the signed-out CTA or
// the signed-in avatar+dropdown, but the actual session lives on the host
// surface. Each surface (in-app site viewer, standalone runtime) provides an
// adapter via AuthMenuContext so one renderer serves them all. Absent
// provider = signed-out visual-only preview (designer canvas / SSR), mirroring
// how `auth_form` degrades without a SiteAuthContext.
export interface AuthMenuUser {
  username: string
  email?: string | null
  roles: string[]
  isAdmin: boolean
  /** Resolved URL of the user's profile image, if any. */
  avatarUrl?: string | null
}

export interface AuthMenuAdapter {
  /** 'loading' while the session hydrates; drives whether buttons/avatar show. */
  status: 'loading' | 'authenticated' | 'anonymous'
  user: AuthMenuUser | null
  /** Route to the sign-in experience. */
  onLogin: () => void
  /** Route to the sign-up experience (omit to hide the Sign-up button). */
  onSignup?: () => void
  /** End the session. */
  onLogout: () => void | Promise<void>
  /** Self-service password change (omit to hide the form). */
  changePassword?: (oldPassword: string, newPassword: string) => Promise<void>
  /** Upload a profile image; resolves to the new avatar URL. */
  uploadAvatar?: (file: File) => Promise<string>
  /** Identify-first sign-in (email OR mobile number → log in, or create the
   *  account on the spot). Rendered by `IdentifyStep`; absent = not offered. */
  identify?: IdentifyAdapter
}

export const AuthMenuContext = createContext<AuthMenuAdapter | null>(null)
export function useAuthMenu(): AuthMenuAdapter | null { return useContext(AuthMenuContext) }

// Horizontal layout + item chrome live in INLINE styles so the menu lays out
// identically on every surface — inline styles always apply, whereas an
// external <style> rule can silently fail to take effect on some React
// surfaces (it did, in-app). The <style> below is kept ONLY for things inline
// styles can't express: :hover affordances and the no-JS dropdown reveal that
// the STATIC SSR deploy depends on (no hydration there). On interactive
// surfaces the dropdown is driven by JS hover state instead.
const NAV_HOVER_CSS = `
.pg-navmenu a:hover,.pg-navmenu-trigger:hover{background:color-mix(in srgb,currentColor 12%,transparent)}
.pg-navmenu-item:hover>.pg-navmenu-dropdown{display:flex!important}
`

const NAV_ITEM_STYLE: CSSProperties = { position: 'relative', listStyle: 'none' }
const NAV_LINK_STYLE: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 12px',
  borderRadius: 6, textDecoration: 'none', color: 'inherit', fontWeight: 500,
  cursor: 'pointer', whiteSpace: 'nowrap', background: 'none', border: 'none', font: 'inherit',
}
const NAV_TOGGLE_STYLE: CSSProperties = {
  position: 'absolute', opacity: 0, width: 1, height: 1, margin: 0, pointerEvents: 'none',
}
const NAV_DROPDOWN_STYLE: CSSProperties = {
  position: 'absolute', minWidth: 200, flexDirection: 'column', gap: 2, padding: 6,
  background: 'var(--pg-surface,#fff)', color: 'var(--pg-text,inherit)',
  border: '1px solid var(--pg-border,rgba(0,0,0,.12))', borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,.16)', zIndex: 1000,
}

// Uniform internal shape both the auto (site) and custom menus render to.
// Uniform internal shape both the auto (site) and custom menus render to.
// The mega-menu extras (icon / description / badge) only ever come from a
// CUSTOM menu — the site nav tree has no place to author them — so they are
// optional here and simply absent for a site-sourced menu.
interface RenderMenuItem {
  label: string
  href?: string
  /** The page object this entry opens (test hook ``data-tw-link``). */
  page?: string
  children?: RenderMenuItem[]
  icon?: string
  description?: string
  badge?: string
}

function siteToRender(items: SiteMenuItem[]): RenderMenuItem[] {
  return (items || []).map((it) => it.type === 'group'
    ? { label: it.label, children: siteToRender(it.children || []) }
    : it.type === 'link'
      ? { label: it.label, href: it.href }
      : { label: it.label, href: it.slug ? `/${it.slug}` : '/', page: it.page_obj_name })
}
function customToRender(items: NavMenuItem[] | undefined): RenderMenuItem[] {
  return (items || []).map((it) => ({
    label: it.label,
    href: it.href,
    icon: it.icon,
    description: it.description,
    badge: it.badge,
    children: it.children && it.children.length ? customToRender(it.children) : undefined,
  }))
}

// Find the subtree to render when a nav_menu pins itself to a folder `root`
// (the LEGACY "Default Header" / "Default Footer" convention — new sites name
// a site menu with `menu` instead). Matches a group by
// obj_name first, then by case/space-insensitive label, anywhere in the tree.
// Returns the matched group's children; falls back to the whole tree when the
// root is unset or not found.
function slugKey(s: string): string { return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '') }
function findMenuRoot(items: SiteMenuItem[], root: string | undefined): SiteMenuItem[] {
  if (!root) return items
  const want = slugKey(root)
  const walk = (list: SiteMenuItem[]): SiteMenuItem[] | null => {
    for (const it of list) {
      if (it.type === 'group') {
        if (slugKey(it.obj_name || '') === want || slugKey(it.label) === want) return it.children
        const deep = walk(it.children || [])
        if (deep) return deep
      }
    }
    return null
  }
  return walk(items) || items
}

// One menu node. A node with children manages its own hover-open state so
// dropdowns work on interactive surfaces even where the <style> rule didn't
// apply; SSR (no JS) keeps `open` false and relies on the CSS :hover fallback.
function NavMenuNode({ it, depth, mega, megaCols }: {
  it: RenderMenuItem; depth: number; mega?: boolean; megaCols?: number
}) {
  const { resolveHref } = useSiteLink()
  const s = useS()
  const [open, setOpen] = useState(false)
  const hasChildren = !!(it.children && it.children.length)
  // An in-page anchor must NOT go through resolveHref (which rewrites
  // root-relative site paths); it stays a literal hash so the browser and the
  // shared anchor script both handle it.
  const href = it.href ? (isAnchorHref(it.href) ? it.href : resolveHref(it.href)) : '#'

  if (!hasChildren) {
    return (
      <li className={`pg-navmenu-item ${ITEM_CLASS}`} style={NAV_ITEM_STYLE}>
        <a style={NAV_LINK_STYLE} href={href} data-tw-link={it.page || it.href || undefined}>{s(it.label)}</a>
      </li>
    )
  }
  // Top-level dropdowns drop below; nested ones fly out to the side.
  const place: CSSProperties = depth === 0 ? { top: '100%', left: 0 } : { top: 0, left: '100%' }
  const linkStyle: CSSProperties = depth === 0 ? NAV_LINK_STYLE : { ...NAV_LINK_STYLE, width: '100%', justifyContent: 'space-between' }

  // A mega panel is only meaningful at the top level — a nested flyout has
  // no room for a multi-column grid.
  const useMega = !!mega && depth === 0

  return (
    <li
      className="pg-navmenu-item"
      style={NAV_ITEM_STYLE}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {it.href
        ? <a href={href} className="pg-navmenu-trigger" style={linkStyle} data-tw-link={it.page || it.href || undefined}>{s(it.label)}<span aria-hidden>▾</span></a>
        : <span className="pg-navmenu-trigger" style={linkStyle}>{s(it.label)}<span aria-hidden>▾</span></span>}
      {useMega ? (
        <div
          className="pg-navmenu-dropdown pg-mega"
          style={{
            ...NAV_DROPDOWN_STYLE, ...place, margin: 0,
            display: open ? 'grid' : 'none',
            ['--pg-mega-cols' as never]: String(megaCols || 3),
          }}
        >
          {it.children!.map((c, i) => <MegaItem key={i} it={c} />)}
        </div>
      ) : (
        <ul className="pg-navmenu-dropdown" style={{ ...NAV_DROPDOWN_STYLE, ...place, margin: 0, padding: 6, display: open ? 'flex' : 'none' }}>
          {it.children!.map((c, i) => <NavMenuNode key={i} it={c} depth={depth + 1} />)}
        </ul>
      )}
    </li>
  )
}

/** One entry inside a mega panel: icon + label + description + badge. */
function MegaItem({ it }: { it: RenderMenuItem }) {
  const { resolveHref } = useSiteLink()
  const s = useS()
  const href = it.href ? (isAnchorHref(it.href) ? it.href : resolveHref(it.href)) : '#'
  return (
    <a className="pg-mega-item" href={href} data-tw-link={it.page || it.href || undefined}>
      {it.icon ? <PageIcon name={it.icon} size={20} color="var(--pg-accent)" /> : null}
      <span style={{ minWidth: 0 }}>
        <span className="pg-mega-t">
          {s(it.label)}
          {it.badge ? <span className="pg-mega-badge">{s(it.badge)}</span> : null}
        </span>
        {it.description ? <span className="pg-mega-d">{s(it.description)}</span> : null}
      </span>
    </a>
  )
}

function NavMenuList({ items, mega, megaCols }: {
  items: RenderMenuItem[]; mega?: boolean; megaCols?: number
}) {
  return <>{items.map((it, i) => <NavMenuNode key={i} it={it} depth={0} mega={mega} megaCols={megaCols} />)}</>
}

/**
 * Site navigation.
 *
 * Below `mobileBreakpoint` the horizontal link row collapses behind a
 * hamburger that opens a drawer. The open/closed state lives in a hidden
 * CHECKBOX rather than React state — deliberately, because this is the one
 * component a published site cannot afford to have depend on JavaScript:
 * the static export never hydrates, so a `useState` drawer would render a
 * hamburger that does nothing on every deployed phone visit. The shared
 * `navDrawerScript` only adds Escape-to-close, scroll-lock and
 * close-after-navigation on top of a drawer that already works.
 */
/**
 * The small-caps label above a heading.
 *
 * Reads the `--pg-eyebrow-*` tokens, which is the whole point: an author
 * who hand-rolls this as a styled paragraph gets something that stops
 * matching the rest of the page the moment the theme's eyebrow size or
 * tracking changes.
 */
export function EyebrowLabel({ text, color }: { text?: string; color?: string }) {
  const s = useS()
  if (!text) return null
  return (
    <span className="pg-eyebrow" style={{ display: 'block', color: color || 'var(--pg-accent)' }}>
      {s(text)}
    </span>
  )
}

/**
 * The trail at the top of a deep page.
 *
 * The LAST item is the current page: rendered as text and marked
 * `aria-current`, never a link to where you already are. The JSON-LD is
 * what turns a bare URL in a search result into a path, and is the reason
 * this is a block rather than a two-item `nav_menu`.
 */
export function BreadcrumbRenderer({ block }: { block: BreadcrumbBlock }) {
  const s = useS()
  const { resolveHref } = useSiteLink()
  const items = (block.items || []).filter(Boolean)
  if (!items.length) return null
  const sep = block.separator ?? '/'
  const last = items.length - 1

  // Only items with a real href can be positions in a BreadcrumbList; the
  // current page is included by name, which is what Google expects.
  const jsonLd = block.structuredData === false ? null : JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: s(it.label),
      ...(it.href && i !== last ? { item: resolveHref(it.href) } : {}),
    })),
  })

  return (
    <nav aria-label="Breadcrumb" style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
      fontSize: themeRem(0.8), fontWeight: 600,
      justifyContent: block.align === 'center' ? 'center'
        : block.align === 'right' ? 'flex-end' : 'flex-start',
    }}>
      {jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
      {items.map((it, i) => (
        <span key={it.id || i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {i > 0 && <span aria-hidden style={{ color: 'var(--pg-subtle, var(--pg-muted))' }}>{sep}</span>}
          {it.href && i !== last ? (
            <a
              href={isAnchorHref(it.href) ? it.href : resolveHref(it.href)}
              style={{ color: 'var(--pg-muted, var(--pg-text-secondary))', textDecoration: 'none' }}
            >{s(it.label)}</a>
          ) : (
            <span aria-current={i === last ? 'page' : undefined}
                  style={{ color: 'var(--pg-heading, var(--pg-text))' }}>{s(it.label)}</span>
          )}
        </span>
      ))}
    </nav>
  )
}

/**
 * Search / facets / sort over a sibling block's items, client-side.
 *
 * The heavy lifting is `initFilterBars`, shared verbatim with the static
 * site (see scripts.ts) so a published catalogue filters exactly the way
 * the one in the designer does. This component only draws the controls and
 * kicks the shared initialiser once they are in the DOM.
 */
export function FilterBarRenderer({ block }: { block: FilterBarBlock }) {
  const s = useS()
  const ref = useRef<HTMLDivElement | null>(null)

  // Re-init whenever the bar or its target could have changed. The
  // initialiser is idempotent per bar, and reads the items live.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const id = window.requestAnimationFrame(() => {
      try { initFilterBars(document) } catch { /* a filter bar never breaks a page */ }
    })
    return () => window.cancelAnimationFrame(id)
  }, [block])

  const sidebar = (block.layout ?? 'sidebar') === 'sidebar'
  const facets = block.facets || []
  const sorts = block.sort || []

  return (
    <div
      ref={ref}
      data-pg-filter={block.target}
      data-pg-filter-layout={sidebar ? 'sidebar' : 'inline'}
      style={{
        display: 'flex', gap: sidebar ? 18 : 12,
        flexDirection: sidebar ? 'column' : 'row',
        alignItems: sidebar ? 'stretch' : 'flex-end',
        flexWrap: sidebar ? 'nowrap' : 'wrap',
      }}
    >
      {block.search && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: sidebar ? undefined : '1 1 220px' }}>
          <span className="pg-eyebrow" style={{ color: 'var(--pg-muted, var(--pg-text-secondary))' }}>
            {s(block.search.label) || 'Search'}
          </span>
          <input
            type="search"
            data-pg-filter-search=""
            placeholder={s(block.search.placeholder) || 'Search'}
            style={{
              width: '100%', boxSizing: 'border-box', height: 42, padding: '0 14px',
              border: '1px solid var(--pg-border)', borderRadius: 'var(--pg-radius, 8px)',
              background: 'var(--pg-surface)', color: 'var(--pg-text)', font: 'inherit',
            }}
          />
        </label>
      )}

      {facets.map((f) => (
        <div key={f.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: sidebar ? undefined : '0 1 auto' }}>
          <span className="pg-eyebrow" style={{ color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{s(f.label)}</span>
          <div className="pg-facet-list" style={{ display: 'flex', flexDirection: sidebar ? 'column' : 'row', gap: 6, flexWrap: 'wrap' }}>
            {/* The "all" choice plus one chip per distinct value. The
                values come from the ITEMS, so a facet can never offer a
                value nothing has and the author keeps no second list. */}
            <FacetChoice facetKey={f.from} value="" label={s(f.allLabel) || 'All'} pressed showCounts={f.showCounts !== false} />
            {/* The per-value chips are cloned from this template by the
                shared script, once it has read the distinct values off the
                items. Doing it in a React effect worked in the app and
                left a published page with only the "All" chip — the static
                prerender runs no effects. */}
            <span data-pg-facet-values={f.from} style={{ display: 'contents' }} />
            <span
              data-pg-facet-chip-template={f.from}
              data-pg-counts={f.showCounts !== false ? '1' : undefined}
              style={{ display: 'none' }}
            >
              <FacetChoice facetKey={f.from} value="" label="" showCounts={f.showCounts !== false} />
            </span>
          </div>
        </div>
      ))}

      {sorts.length > 0 && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span className="pg-eyebrow" style={{ color: 'var(--pg-muted, var(--pg-text-secondary))' }}>Sort</span>
          <select
            data-pg-filter-sort=""
            style={{
              height: 42, padding: '0 12px', border: '1px solid var(--pg-border)',
              borderRadius: 'var(--pg-radius, 8px)', background: 'var(--pg-surface)',
              color: 'var(--pg-text)', font: 'inherit',
            }}
          >
            <option value="">{'Default'}</option>
            {sorts.map((o) => (
              <option key={o.id} value={`${o.by}:${o.dir || 'asc'}`}>{s(o.label)}</option>
            ))}
          </select>
        </label>
      )}

      {block.resultLabel && (
        <span
          data-pg-filter-count={block.resultLabel}
          style={{ fontSize: themeRem(0.85), color: 'var(--pg-muted, var(--pg-text-secondary))' }}
        >{s(block.resultLabel).replace('{n}', '…')}</span>
      )}
    </div>
  )
}

function FacetChoice({ facetKey, value, label, pressed, showCounts }: {
  facetKey: string; value: string; label: string; pressed?: boolean; showCounts?: boolean
}) {
  return (
    <button
      type="button"
      data-pg-facet-pick={`${facetKey}=${value}`}
      aria-pressed={pressed ? 'true' : 'false'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        font: 'inherit', fontSize: themeRem(0.86), cursor: 'pointer',
        padding: '7px 13px', borderRadius: 999,
        border: '1px solid var(--pg-border)', background: 'var(--pg-surface)',
        color: 'var(--pg-text)',
      }}
    >
      {label}
      {showCounts && (
        <span
          data-pg-count-for={`${facetKey}=${value}`}
          style={{
            fontSize: themeRem(0.74), fontWeight: 700, minWidth: 18, textAlign: 'center',
            padding: '1px 6px', borderRadius: 999,
            background: 'var(--pg-surface-alt)', color: 'var(--pg-muted, var(--pg-text-secondary))',
          }}
        />
      )}
    </button>
  )
}

// ─── Cart blocks ─────────────────────────────────────
//
// All three render a SHELL that `initCart` fills from the store — one
// implementation, so a cart on a published page behaves exactly like the
// one in the designer. See cart.ts.

/**
 * Boot the page cart on a React surface: name which project's basket this
 * is, then wire it.
 *
 * The cart's click handling is DELEGATED from the document, so it belongs
 * to the page, not to any one block — which is how the published site
 * already treats it, injecting `cartScript()` into every page it writes.
 * The React surfaces used to kick it from the three cart BLOCKS alone, so
 * every "Add to cart" on a page with no cart block — a product page, a
 * catalogue grid — was silently inert. Each React page host calls this
 * once instead; the block-level `useCartWiring` below stays for a cart
 * shell that mounts after the page has already booted.
 *
 * `scope` keeps two projects' baskets apart where they share an origin,
 * which the in-app viewer and the downloaded runtime both do. A published
 * site has an origin of its own and passes nothing.
 */
export function usePageCart(
  scope?: string,
  inputs?: { name: string; cart?: PageCartSource }[],
  setVar?: (name: string, value: unknown) => void,
) {
  useEffect(() => {
    if (typeof document === 'undefined') return
    const go = () => {
      try {
        // Scope BEFORE wiring: `initCart` renders the cart shells on its
        // way out, and doing that under the wrong key would paint another
        // project's basket for a frame.
        setCartScope(scope)
        initCart()
        // The wishlist shares the cart's scope and boots with it: a heart
        // on a product card must work on a page with no wishlist block.
        initWishlist()
      } catch { /* a cart never takes the page down */ }
    }
    go()
    window.requestAnimationFrame(go)
  }, [scope])

  // Feed every page variable declared with a `cart` source. This is the
  // ONLY route out of the basket: without it the cart is write-only — a
  // visitor fills it, and the order form it is meant to become opens
  // blank. `pg:cart` fires on every basket change, so the draft tracks the
  // basket instead of being a snapshot taken once at mount.
  const cartVars = useMemo(
    () => (inputs || []).filter((i) => i && i.cart && i.name),
    [inputs],
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !setVar || cartVars.length === 0) return
    const push = () => {
      let lines: CartLine[]
      try { lines = readCart() } catch { return }
      for (const v of cartVars) setVar(v.name, cartDraft(lines, v.cart!))
    }
    push()
    window.addEventListener('pg:cart', push)
    window.addEventListener('storage', push)
    return () => {
      window.removeEventListener('pg:cart', push)
      window.removeEventListener('storage', push)
    }
    // `scope` decides WHICH basket `readCart` reads, so a scope change has
    // to re-push or the draft would keep the previous project's lines.
  }, [cartVars, setVar, scope])
}

/**
 * Fill THIS cart shell once its markup is in the DOM.
 *
 * The page-level boot (`usePageCart`) owns the scope and the delegated
 * click handling; this exists for the shell that mounts after it — a cart
 * block inside a tab, or a page whose data source resolves late.
 *
 * It runs immediately (an effect sees a committed DOM) and again on the
 * next frame for a sibling block still painting, and it deliberately does
 * NOT cancel that frame on cleanup: `dep` is the block, which
 * `applyBindings` clones on every render, so a cancelling cleanup let a
 * re-rendering page starve the cart of the only frame it had.
 */
function useCartWiring(dep: unknown) {
  useEffect(() => {
    if (typeof document === 'undefined') return
    const go = () => {
      try { initCart() } catch { /* a cart never takes the page down */ }
    }
    go()
    window.requestAnimationFrame(go)
  }, [dep])
}

/** Link + live count shared by the cart and wishlist badges. `countAttrs`
 *  are the data attributes the cart / wishlist script fills, so the markup
 *  stays the one contract every surface (static site included) relies on. */
function CountBadgeLink({ href, label, name, icon, variant, iconOnly, hide, countAttrs }: {
  /** Visible text ('' = none); `name` is always the link's accessible name. */
  href: string; label: string; name: string; icon: React.ReactNode; variant: ButtonSpec['variant']
  iconOnly?: boolean; hide: boolean; countAttrs: Record<string, string>
}) {
  const count = (
    <span
      {...countAttrs}
      style={{
        minWidth: iconOnly ? 18 : 22, height: iconOnly ? 18 : 22, padding: iconOnly ? '0 5px' : '0 6px',
        borderRadius: 999, alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
        fontSize: themeRem(iconOnly ? 0.65 : 0.72), fontWeight: 700, lineHeight: 1,
        background: 'var(--pg-accent)', color: 'var(--pg-on-accent, #fff)',
        ...(iconOnly ? { position: 'absolute', top: 2, right: 0, pointerEvents: 'none' } : {}),
        // Starts hidden so an empty count never flashes a "0" on first paint,
        // before the script has read the store.
        display: hide ? 'none' : 'inline-flex',
      }}
    >0</span>
  )
  if (iconOnly) {
    return (
      <a href={href} aria-label={name} title={name}
         style={{
           position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
           width: 44, height: 44, borderRadius: 999, textDecoration: 'none',
           ...BTN_VARIANT_STYLE[variant], padding: 0,
         }}>
        {icon}
        {count}
      </a>
    )
  }
  return (
    <a href={href} aria-label={label ? undefined : name}
       style={{
         display: 'inline-flex', alignItems: 'center', gap: 8,
         borderRadius: 999, fontWeight: 600, textDecoration: 'none',
         ...BTN_VARIANT_STYLE[variant], ...BTN_SIZE_STYLE.md,
       }}>
      {icon}
      {label && <span>{label}</span>}
      {count}
    </a>
  )
}

export function CartBadgeRenderer({ block }: { block: CartBadgeBlock }) {
  const s = useS()
  const { resolveHref } = useSiteLink()
  useCartWiring(block)
  const hide = block.hideWhenEmpty !== false
  return (
    <CountBadgeLink
      href={resolveHref(block.href || '/cart')}
      label={block.iconOnly ? '' : (s(block.label) || 'Cart')}
      name={s(block.label) || 'Cart'}
      icon={<PageIcon name={block.icon || 'shopping-cart'} size={block.iconOnly ? 20 : 16} />}
      variant={block.variant || 'primary'} iconOnly={block.iconOnly} hide={hide}
      countAttrs={{ 'data-pg-cart-count': '', ...(hide ? { 'data-pg-cart-hide-empty': '' } : {}) }}
    />
  )
}

export function CartLinesRenderer({ block }: { block: CartLinesBlock }) {
  const s = useS()
  const { resolveHref } = useSiteLink()
  useCartWiring(block)
  const editable = block.editable !== false
  const stepBtn: React.CSSProperties = {
    width: 34, height: 34, font: 'inherit', fontSize: 17, cursor: 'pointer',
    border: 0, background: 'none', color: 'var(--pg-text)', lineHeight: 1,
  }
  return (
    <div data-pg-cart-lines="" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div data-pg-cart-list="" style={{ display: 'flex', flexDirection: 'column' }} />

      {/* The row the script clones, once per line. Kept in the markup
          rather than built in JS so it themes with the rest of the page. */}
      <div
        data-pg-cart-row-template=""
        style={{
          display: 'none', alignItems: 'center', gap: 16, padding: '14px 4px',
          borderBottom: '1px solid var(--pg-border)',
        }}
      >
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flex: '0 0 84px', height: 72, background: 'var(--pg-surface-alt)',
          borderRadius: 10, padding: 6,
        }}>
          <img data-pg-cart-image="" alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </span>
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <a data-pg-cart-name="" href="#" style={{ fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))', textDecoration: 'none' }} />
          {/* A booking line's range — "Mon 12 Oct → Thu 15 Oct · 3 nights". */}
          <span data-pg-cart-dates="" style={{ display: 'none', fontSize: themeRem(0.82), fontWeight: 600, color: 'var(--pg-text)' }} />
          <span data-pg-cart-meta="" style={{ fontSize: themeRem(0.8), color: 'var(--pg-muted, var(--pg-text-secondary))' }} />
          <span data-pg-cart-unit="" style={{ fontSize: themeRem(0.8), color: 'var(--pg-muted, var(--pg-text-secondary))' }} />
        </span>
        {editable && (
          <span style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--pg-border)', borderRadius: 999, height: 36 }}>
            <button type="button" data-pg-cart-step="-1" aria-label="Decrease quantity" style={stepBtn}>&minus;</button>
            <span data-pg-cart-qty="" style={{ width: 32, textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} />
            <button type="button" data-pg-cart-step="1" aria-label="Increase quantity" style={stepBtn}>+</button>
          </span>
        )}
        <span data-pg-cart-line-total="" style={{ width: 110, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--pg-heading, var(--pg-text))' }} />
        {editable && (
          <button type="button" data-pg-cart-remove="" aria-label="Remove item" style={{ ...stepBtn, width: 32, color: 'var(--pg-muted, var(--pg-text-secondary))' }}>×</button>
        )}
      </div>

      <div data-pg-cart-empty="" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '24px 4px' }}>
        <span style={{ color: 'var(--pg-muted, var(--pg-text-secondary))' }}>
          {s(block.emptyText) || 'Your cart is empty.'}
        </span>
        {block.continueHref && (
          <a
            href={resolveHref(block.continueHref)}
            style={{ alignSelf: 'flex-start', textDecoration: 'none', borderRadius: 999, ...BTN_VARIANT_STYLE.outline, ...BTN_SIZE_STYLE.md }}
          >{s(block.continueLabel) || 'Continue browsing'}</a>
        )}
      </div>
    </div>
  )
}

export function CartSummaryRenderer({ block }: { block: CartSummaryBlock }) {
  const s = useS()
  const { resolveHref } = useSiteLink()
  useCartWiring(block)
  const row = (label: string, attr: string) => (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: themeRem(0.9), color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{label}</span>
      <span {...{ [attr]: '' }} style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }} />
    </div>
  )
  return (
    <div
      data-pg-cart-summary=""
      // The rules are DECLARED on the block: a static page has no server to
      // ask what tax or postage costs. These figures are for the visitor;
      // the order is priced again server-side before anything is charged.
      data-pg-tax-percent={block.taxPercent ?? undefined}
      data-pg-ship-flat={block.shippingFlat ?? undefined}
      data-pg-ship-free-over={block.shippingFreeOver ?? undefined}
      data-pg-free-label={block.freeLabel || 'Free'}
      style={{
        display: 'flex', flexDirection: 'column', gap: 12,
        background: 'var(--pg-surface)', border: '1px solid var(--pg-border)',
        borderRadius: 'var(--pg-radius, 12px)', padding: 22,
      }}
    >
      {block.heading && (
        <h3 style={{ margin: 0, fontSize: themeRem(1.15), fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>
          {s(block.heading)}
        </h3>
      )}
      {row(s(block.subtotalLabel) || 'Subtotal', 'data-pg-cart-subtotal')}
      {block.taxPercent ? row(s(block.taxLabel) || `Tax at ${block.taxPercent}%`, 'data-pg-cart-tax') : null}
      {block.shippingFlat ? row(s(block.shippingLabel) || 'Shipping', 'data-pg-cart-shipping') : null}
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 12, borderTop: '1px solid var(--pg-border)', paddingTop: 12,
      }}>
        <span style={{ fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>
          {s(block.totalLabel) || 'Total'}
        </span>
        <span data-pg-cart-total="" style={{
          fontSize: themeRem(1.5), fontWeight: 800, letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums', color: 'var(--pg-heading, var(--pg-text))',
        }} />
      </div>
      {block.checkoutHref && (
        <a
          data-pg-cart-checkout=""
          href={resolveHref(block.checkoutHref)}
          style={{
            textAlign: 'center', textDecoration: 'none', borderRadius: 999,
            justifyContent: 'center', ...BTN_VARIANT_STYLE.primary, ...BTN_SIZE_STYLE.lg,
          }}
        >{s(block.checkoutLabel) || 'Proceed to checkout'}</a>
      )}
      {block.note && (
        <p style={{ margin: 0, fontSize: themeRem(0.78), color: 'var(--pg-muted, var(--pg-text-secondary))', textAlign: 'center' }}>
          {s(block.note)}
        </p>
      )}
    </div>
  )
}

// ─── Wishlist blocks ─────────────────────────────────
//
// The cart's sibling: shells that `initWishlist` (wishlist.ts) fills from
// the visitor's browser, so a published page with no React behaves the
// same as the canvas. The saved/unsaved look of a heart is CSS keyed off
// `data-pg-wished`, which the script sets — never a React state.

const WISH_CSS =
  '[data-pg-wish-toggle] .pg-wish-heart{transition:fill .15s,transform .15s}'
  + '[data-pg-wish-toggle][data-pg-wished] .pg-wish-heart{fill:currentColor}'
  // !important: the button's inline colour would otherwise win.
  + '[data-pg-wish-toggle][data-pg-wished]{color:var(--pg-error,#e11d48)!important}'
  + '[data-pg-wish-toggle]:active .pg-wish-heart{transform:scale(.88)}'

function HeartIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false"
         style={{ display: 'block', flex: '0 0 auto' }}>
      <path
        className="pg-wish-heart" fill="none"
        d="M12 20.5s-7.5-4.6-9.3-9.2C1.5 8.1 3.4 4.8 6.8 4.6c2-.1 3.6 1 5.2 2.9 1.6-1.9 3.2-3 5.2-2.9 3.4.2 5.3 3.5 4.1 6.7-1.8 4.6-9.3 9.2-9.3 9.2z"
        stroke="currentColor" strokeWidth={1.8} strokeLinejoin="round"
      />
    </svg>
  )
}

/** Boot the wishlist for a block that mounts after the page has booted. */
function useWishlistWiring(dep: unknown) {
  useEffect(() => {
    if (typeof document === 'undefined') return
    const go = () => { try { initWishlist() } catch { /* a wishlist never takes the page down */ } }
    go()
    window.requestAnimationFrame(go)
  }, [dep])
}

/**
 * A heart that saves `item` to the wishlist — beside an Add-to-cart button,
 * on a product card, or as the `wishlist_button` block. `display: 'icon'`
 * is a round icon button (the label becomes its accessible name);
 * `'button'` shows the label beside the heart.
 */
export function WishlistHeart({ item, addLabel, removeLabel, display = 'icon', variant, size = 'md' }: {
  item: { sku: string; name: string; price: number; prefix: string; image?: string; href?: string; meta?: string }
  addLabel?: string
  removeLabel?: string
  display?: 'icon' | 'button'
  variant?: ButtonSpec['variant']
  size?: NonNullable<ButtonSpec['size']>
}) {
  useWishlistWiring(item.sku)
  const add = addLabel || 'Add to wishlist'
  const remove = removeLabel || 'Saved to wishlist'
  const common = {
    type: 'button' as const,
    'data-pg-wish-toggle': JSON.stringify(item),
    'data-pg-wish-add': add,
    'data-pg-wish-remove': remove,
    'aria-pressed': 'false' as const,
    'aria-label': add,
    title: add,
  }
  const css = <style dangerouslySetInnerHTML={{ __html: WISH_CSS }} />
  if (display === 'button') {
    return (
      <>
        {css}
        <button
          {...common}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            font: 'inherit', borderRadius: 'var(--pg-button-radius, 8px)', fontWeight: 600,
            ...BTN_VARIANT_STYLE[variant || 'outline'], ...BTN_SIZE_STYLE[size],
          }}
        >
          <HeartIcon size={size === 'lg' ? 20 : 18} />
          <span data-pg-wish-label="">{add}</span>
        </button>
      </>
    )
  }
  const d = size === 'sm' ? 32 : size === 'lg' ? 46 : 40
  return (
    <>
      {css}
      <button
        {...common}
        style={{
          width: d, height: d, borderRadius: 999, cursor: 'pointer', padding: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
          border: '1px solid var(--pg-border)', background: 'var(--pg-surface)',
          color: 'var(--pg-text)', boxShadow: '0 1px 3px rgba(0,0,0,.08)',
        }}
      >
        <HeartIcon size={Math.round(d * 0.48)} />
      </button>
    </>
  )
}

export function WishlistButtonRenderer({ block }: { block: WishlistButtonBlock }) {
  const s = useS()
  const price = s(String(block.price ?? '')) || ''
  const item = {
    sku: s(block.sku) || block.sku,
    name: s(block.name) || block.sku,
    price: Number(price.replace(/[^0-9.]/g, '')) || 0,
    prefix: (price.match(/^[^0-9.]*/) || [''])[0].trim(),
    image: block.image ? s(block.image) : undefined,
    href: block.href ? s(block.href) : undefined,
    meta: block.meta ? s(block.meta) : undefined,
  }
  return (
    <WishlistHeart
      item={item}
      addLabel={s(block.addLabel) || undefined}
      removeLabel={s(block.removeLabel) || undefined}
      display={block.display || 'button'}
      variant={block.variant}
      size={block.buttonSize || 'md'}
    />
  )
}

export function WishlistBadgeRenderer({ block }: { block: WishlistBadgeBlock }) {
  const s = useS()
  const { resolveHref } = useSiteLink()
  useWishlistWiring(block)
  const hide = block.hideWhenEmpty !== false
  const size = block.iconOnly ? 20 : 16
  // An explicitly empty label keeps the old "icon + count" look; the link's
  // accessible name is still "Wishlist".
  const shown = block.label === undefined || block.label ? (s(block.label) || 'Wishlist') : ''
  return (
    <CountBadgeLink
      href={resolveHref(block.href || '/wishlist')}
      label={block.iconOnly ? '' : shown}
      name={s(block.label) || 'Wishlist'}
      icon={block.icon && block.icon !== 'heart' ? <PageIcon name={block.icon} size={size} /> : <HeartIcon size={size} />}
      variant={block.variant || 'outline'} iconOnly={block.iconOnly} hide={hide}
      countAttrs={{ 'data-pg-wish-count': '', ...(hide ? { 'data-pg-wish-hide-empty': '' } : {}) }}
    />
  )
}

export function WishlistLinesRenderer({ block }: { block: WishlistLinesBlock }) {
  const s = useS()
  const { resolveHref } = useSiteLink()
  useWishlistWiring(block)
  const showMove = block.showMove !== false
  return (
    <div
      data-pg-wish-lines=""
      {...(block.keepAfterMove ? { 'data-pg-wish-keep': '' } : {})}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div data-pg-wish-list="" style={{ display: 'flex', flexDirection: 'column' }} />

      {/* The row the script clones, once per saved item. */}
      <div
        data-pg-wish-row-template=""
        style={{
          display: 'none', alignItems: 'center', gap: 16, padding: '14px 4px', flexWrap: 'wrap',
          borderBottom: '1px solid var(--pg-border)',
        }}
      >
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flex: '0 0 84px', height: 72, background: 'var(--pg-surface-alt)',
          borderRadius: 10, padding: 6,
        }}>
          <img data-pg-wish-image="" alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </span>
        <span style={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <a data-pg-wish-name="" href="#" style={{ fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))', textDecoration: 'none' }} />
          <span data-pg-wish-meta="" style={{ fontSize: themeRem(0.8), color: 'var(--pg-muted, var(--pg-text-secondary))' }} />
        </span>
        <span data-pg-wish-price="" style={{ minWidth: 90, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--pg-heading, var(--pg-text))' }} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {showMove && (
            <button
              type="button" data-pg-wish-move=""
              style={{ cursor: 'pointer', font: 'inherit', borderRadius: 'var(--pg-button-radius, 8px)', fontWeight: 600, border: 'none', ...BTN_VARIANT_STYLE.primary, ...BTN_SIZE_STYLE.sm }}
            >{s(block.moveLabel) || 'Move to cart'}</button>
          )}
          <button
            type="button" data-pg-wish-remove=""
            style={{ cursor: 'pointer', font: 'inherit', borderRadius: 'var(--pg-button-radius, 8px)', fontWeight: 600, ...BTN_VARIANT_STYLE.ghost, ...BTN_SIZE_STYLE.sm }}
          >{s(block.removeLabel) || 'Remove'}</button>
        </span>
      </div>

      <div data-pg-wish-empty="" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '24px 4px' }}>
        <span style={{ color: 'var(--pg-muted, var(--pg-text-secondary))' }}>
          {s(block.emptyText) || 'Your wishlist is empty.'}
        </span>
        {block.continueHref && (
          <a
            href={resolveHref(block.continueHref)}
            style={{ alignSelf: 'flex-start', textDecoration: 'none', borderRadius: 999, ...BTN_VARIANT_STYLE.outline, ...BTN_SIZE_STYLE.md }}
          >{s(block.continueLabel) || 'Continue browsing'}</a>
        )}
      </div>
    </div>
  )
}

export function NavMenuRenderer({ block }: { block: NavMenuBlock }) {
  const siteMenu = useSiteMenu()
  const siteMenus = useSiteMenus()
  const source = block.source || 'site'
  // A named site menu wins. On a site that DEFINES menus, a name it does not
  // define renders NO links (never the whole site — that silent fallback is
  // what made a renamed folder turn into a dropdown). A site with no menus at
  // all (a freshly generated one, before its menus are written) still gets a
  // working header: the whole tree. Then the legacy folder pin, then the tree.
  const hasNamedMenus = Object.keys(siteMenus).length > 0
  const rooted = source !== 'site'
    ? siteMenu
    : block.menu
      ? (siteMenus[block.menu] || (hasNamedMenus ? [] : findMenuRoot(siteMenu, block.root)))
      : findMenuRoot(siteMenu, block.root)
  let items: RenderMenuItem[] = source === 'custom' ? customToRender(block.items) : siteToRender(rooted)
  if (source === 'site' && block.includeHome === false) {
    items = items.filter((it) => it.href !== '/')
  }
  const align = block.align || 'left'
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  const mega = block.dropdownLayout === 'mega'

  const drawer = (block.mobile || 'drawer') !== 'none'
  const bp = block.mobileBreakpoint ?? 768
  const side = block.drawerSide || 'right'
  // Scope the responsive rules to THIS menu: a header nav and a footer nav on
  // the same page can legitimately collapse at different widths.
  const cls = `pg-nav-${block.id}`
  const toggleId = `pg-nav-t-${block.id}`

  const list = (
    <ul
      className="pg-nav-list"
      style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, listStyle: 'none', margin: 0, padding: 0 }}
    >
      <NavMenuList items={items} mega={mega} megaCols={block.megaColumns} />
    </ul>
  )

  return (
    <nav
      className={['pg-navmenu', drawer ? cls : ''].filter(Boolean).join(' ')}
      style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, justifyContent: justify, color: 'var(--pg-text)', position: 'relative' }}
    >
      {items.length ? (
        drawer ? (
          <>
            {/* Order matters: the checkbox must PRECEDE both the list and the
                scrim so the `:checked ~` sibling selectors can reach them.
                Hidden INLINE as well as by NAV_CSS: the checkbox is never
                meant to be seen, and on a surface that renders this block
                without the shared stylesheet it showed up as a tick box in
                front of the first menu item. */}
            <input
              id={toggleId}
              className="pg-nav-toggle"
              style={NAV_TOGGLE_STYLE}
              type="checkbox"
              data-bp={bp}
              aria-label={block.drawerLabel || 'Menu'}
            />
            <label className="pg-nav-burger" htmlFor={toggleId} aria-label={block.drawerLabel || 'Menu'}>
              <span />
            </label>
            {list}
            <label className="pg-nav-scrim" htmlFor={toggleId} aria-hidden />
            <style dangerouslySetInnerHTML={{ __html: navDrawerCss(cls, bp, side) }} />
          </>
        ) : list
      ) : null}
      <style>{NAV_HOVER_CSS}</style>
    </nav>
  )
}

function useS() {
  const vars = useContext(PageVarsContext)
  return (text?: string) => substituteVars(text, vars)
}

/**
 * Public name for the `{{var}}` substitution hook every renderer uses.
 * Exported so renderers living in sibling modules (marketingRenderers) get
 * the same substitution semantics instead of re-implementing them.
 */
export function usePageVarsSub() {
  return useS()
}

// Optional child-render seam for layout blocks (columns / container / tabs /
// accordion). A host that supports nested blocks (the in-app preview, the
// standalone runtime) passes a recursive renderer; when omitted (e.g. the
// static SSR text path) the renderer keeps its legacy text/body fallback, so
// existing behavior is unchanged.
export type RenderChildren = (children: Block[], scopeKey: string) => ReactNode

// ─── Responsive grid helper ──────────────────────────
// Targets `cols` columns when the container is wide enough, but each
// column must be at least `minPx` wide. When the viewport narrows
// below `cols * minPx`, the grid wraps automatically. `auto-fit`
// collapses empty tracks so a single item fills the row instead of
// leaving phantom columns. Mirrors canvasRenderers.responsiveGrid().
export function responsiveGrid(cols: number, minPx: number, gap = 16): string {
  const safeCols = Math.max(1, cols || 1)
  return `repeat(auto-fit, minmax(max(${minPx}px, calc((100% - ${(safeCols - 1) * gap}px) / ${safeCols})), 1fr))`
}

/**
 * The wrapper a REPEAT lays its copies out in.
 *
 * A repeat used to emit a flat fragment, so a card bound to a product table
 * came out as one card per row down the page — the reason a catalogue could
 * not be data-bound and still look like a catalogue. Every surface that
 * repeats calls this so they cannot disagree about the breakpoints, and it
 * reuses `responsiveGrid` so a bound grid and an authored `card_grid` step
 * down at the same widths.
 *
 * Returns `null` for the default flow layout, which the caller renders as a
 * bare fragment exactly as before.
 */
export function repeatLayoutStyle(layout?: RepeatLayout): React.CSSProperties | null {
  // board / positioned are arranged by the shared RepeatFrame, not a grid.
  if (!layout || layout.mode !== 'grid') return null
  const gap = layout.gap ?? 20
  return {
    display: 'grid',
    gridTemplateColumns: responsiveGrid(layout.columns ?? 3, layout.minColumnWidth ?? 220, gap),
    gap,
    alignItems: 'stretch',
  }
}

// ─── Background helper ───────────────────────────────

export function backgroundStyle(bg?: Background): React.CSSProperties {
  if (!bg || bg.kind === 'none') return {}
  // Explicit transparent: emit the declaration so it overrides any base
  // surface fill (a plain `none` returns {} and leaves the base in place).
  if (bg.kind === 'transparent') return { background: 'transparent' }
  if (bg.kind === 'color') {
    return { background: bg.color || 'var(--pg-surface)' }
  }
  if (bg.kind === 'gradient' || bg.kind === 'gradient_animated') {
    // `gradientValue` also handles multi-stop and radial gradients; the
    // two-stop shorthand below is its fallback when neither is authored.
    return { background: gradientValue(bg) }
  }
  if (bg.kind === 'video') {
    // The poster paints the element, so the band is never blank; the
    // <video> layer (VideoBackdrop) is drawn on top when motion is allowed.
    if (!bg.posterUrl) return { background: bg.color || 'var(--pg-surface)' }
    return { background: `url(${bg.posterUrl}) center/cover no-repeat` }
  }
  if (bg.kind === 'image' && bg.imageUrl) {
    // A gradient wash wins over a flat one when both are set: it is the
    // more specific instruction, and the two would fight.
    const g = bg.overlayGradient
    const overlay = g
      ? `linear-gradient(${g.angle ?? 180}deg, ${
          g.stops && g.stops.length >= 2
            ? g.stops.map((st) => `${st.color}${st.at === undefined ? '' : ` ${st.at}%`}`).join(', ')
            : `${g.from}, ${g.to}`
        }), `
      : bg.overlay ? `linear-gradient(${bg.overlay}, ${bg.overlay}), ` : ''
    // Parallax: pin the image to the viewport (`fixed`) so the page scrolls
    // over it. The image still paints on the element, so it never goes blank.
    const attach = bg.parallax ? ' fixed' : ''
    return {
      background: `${overlay}url(${bg.imageUrl}) center/cover${attach} no-repeat`,
    }
  }
  return {}
}

/**
 * The `<video>` layer for a `kind: 'video'` background.
 *
 * Everything about it is defensive, because a background video is the single
 * easiest way to ship a broken-looking page:
 *
 *   • The POSTER is painted by `backgroundStyle` on the element itself, so
 *     the band is never blank — before the video buffers, on the static
 *     prerender, and if the file 404s.
 *   • Nothing renders at all under `prefers-reduced-motion`; the poster is
 *     the whole background for that visitor.
 *   • `muted` is forced as a DOM PROPERTY, not just an attribute — Chrome
 *     gates muted-autoplay on the property, and React doesn't always set it.
 *   • `playsInline` stops iOS hijacking the page into fullscreen playback.
 *
 * Returns null unless the background really is a video, so callers can drop
 * it into any band unconditionally.
 */
export function VideoBackdrop({ background, blockId }: { background?: Background; blockId?: string }) {
  const [allowed, setAllowed] = useState(false)

  useEffect(() => {
    // Decided in an effect, not at render: the SSR pass has no matchMedia,
    // and deciding at render would produce markup the client disagrees with.
    if (typeof window === 'undefined') return
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    setAllowed(!mq || !mq.matches)
  }, [])

  if (!hasVideoBackground(background) || !allowed) return null
  return (
    <video
      // The id is the handle a `play_media` click intent targets.
      id={blockId ? `pg-media-${blockId}` : undefined}
      src={background!.videoUrl}
      poster={background!.posterUrl}
      autoPlay muted loop playsInline
      aria-hidden
      ref={(el) => {
        if (!el) return
        el.muted = true
        const p = el.play()
        // A rejected play() is normal (autoplay policy, a data-saver mode) —
        // the poster stays, which is exactly the intended fallback.
        if (p) p.catch(() => {})
      }}
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: 'cover', zIndex: 0, pointerEvents: 'none',
      }}
    />
  )
}

/**
 * Classes a band needs for its background kind: the drift animation for an
 * animated gradient, and the stacking fix for a video backdrop.
 */
export function backgroundClass(bg?: Background): string | undefined {
  const out: string[] = []
  if (bg?.kind === 'gradient_animated') out.push('pg-bg-drift')
  if (hasVideoBackground(bg)) out.push('pg-has-video')
  return out.join(' ') || undefined
}

// ─── Button rendering ────────────────────────────────

// Button variants reference theme tokens so they remain readable on any
// preset. `primary` uses the brand color with its guaranteed-readable
// `--pg-on-primary` foreground; `secondary` uses the surface color with
// theme text instead of the previous hardcoded white-on-black that
// inverted on dark themes.
const BTN_VARIANT_STYLE: Record<ButtonSpec['variant'], React.CSSProperties> = {
  primary:   { background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', border: '1px solid var(--pg-primary)' },
  secondary: { background: 'var(--pg-surface)', color: 'var(--pg-on-surface, var(--pg-text))', border: '1px solid var(--pg-border)' },
  outline:   { background: 'transparent', color: 'currentColor', border: '1.5px solid currentColor' },
  ghost:     { background: 'transparent', color: 'currentColor', border: '1px solid transparent' },
  link:      { background: 'transparent', color: 'var(--pg-link, var(--pg-primary))', border: 'none', textDecoration: 'underline' },
}

const BTN_SIZE_STYLE: Record<NonNullable<ButtonSpec['size']>, React.CSSProperties> = {
  sm: { padding: '6px 12px',  fontSize: themeRem(0.8)  },
  md: { padding: '9px 18px',  fontSize: themeRem(0.9)  },
  lg: { padding: '12px 24px', fontSize: themeRem(1)    },
}

// Resolve a ButtonSpec down to its current click intent. Falls back to the
// legacy top-level `url` field when on_click is absent (back-compat — never
// written back; new buttons always set on_click via the editor). Tolerates a
// missing spec: a button with nothing to do renders and does nothing, rather
// than taking the whole page down.
function resolveClick(btn: ButtonSpec | undefined) {
  if (!btn) return { kind: 'none' as const }
  if (btn.on_click) return btn.on_click
  if (btn.url) return { kind: 'external_url' as const, url: btn.url, newTab: btn.newTab }
  return { kind: 'none' as const }
}

// A `button` block can be authored FLAT — `label` / `variant` / `on_click`
// sitting on the BLOCK instead of inside `button` — by a generator or by
// hand. That shape used to crash the whole page (`block.button.variant` on
// undefined), so coerce it here, at the ONE place both this renderer and the
// designer canvas read a button block. Never written back: the editor always
// saves the canonical `{ button: { … } }` shape.
export function buttonSpecOf(block: ButtonBlock, fallbackId = 'btn'): ButtonSpec {
  const spec = block?.button as ButtonSpec | undefined
  const flat = (block || {}) as unknown as Partial<ButtonSpec>
  return {
    id:       spec?.id       ?? block?.id ?? fallbackId,
    label:    spec?.label    ?? flat.label    ?? '',
    variant:  spec?.variant  ?? flat.variant  ?? 'primary',
    size:     spec?.size     ?? flat.size,
    icon:     spec?.icon     ?? flat.icon,
    url:      spec?.url      ?? flat.url,
    newTab:   spec?.newTab   ?? flat.newTab,
    on_click: spec?.on_click ?? flat.on_click,
  }
}

// ─── Click dispatch ──────────────────────────────────────
// THE single place a ButtonClick is executed. Every click surface routes
// through it: buttons, whole-block `on_click` intents, card CTAs, popup
// triggers. Keeping it one function is what made adding `scroll_to`,
// `open_popup`, `toggle_var`, `copy` and `play_media` a five-case change
// rather than a hunt through the renderers.

/** Opens a `popup`-tagged section. Surfaces that host popups register here. */
export const PopupOpenContext = createContext<((sectionId: string) => void) | null>(null)

export interface ClickDeps {
  runtime: ReturnType<typeof usePageRuntime>
  resolveHref: (h: string) => string
  openPopup?: ((sectionId: string) => void) | null
  vars?: PageVars
  /** Transient feedback for `copy` (falls back to a title attribute swap). */
  onFeedback?: (message: string) => void
  /** The surface's session, for `sign_out`. Absent = nothing to sign out of. */
  auth?: AuthMenuAdapter | null
}

export async function dispatchClick(click: ButtonClick, deps: ClickDeps): Promise<void> {
  const { runtime, resolveHref, openPopup } = deps
  switch (click.kind) {
    case 'external_url': {
      // Resolve site-internal root-relative URLs to the current surface's
      // base path so a "/tickets-pricing" nav button works in preview,
      // the standalone runtime, and the deployed site alike. An in-page
      // anchor is handled as a scroll, not a navigation.
      const raw = substituteVars(click.url || '#', deps.vars || {}) || '#'
      if (isAnchorHref(raw)) return scrollToAnchor(raw.slice(1))
      const url = resolveHref(raw)
      if (url === '#' || !url) return
      if (click.newTab) window.open(url, '_blank', 'noreferrer')
      else window.location.href = url
      return
    }
    case 'add_to_cart': {
      // A BLOCK-level add-to-cart (a whole clickable card). Buttons take
      // the data-attribute path in `PageButton` so they work with no React
      // on the page; this is the same store either way.
      const price = String(click.price ?? '')
      addToCart({
        sku: click.sku,
        name: click.name || click.sku,
        price: Number(price.replace(/[^0-9.]/g, '')) || 0,
        prefix: (price.match(/^[^0-9.]*/) || [''])[0].trim(),
        image: click.image, href: click.href, meta: click.meta,
        qty: click.qty || 1,
      })
      deps.onFeedback?.(click.addedLabel || 'Added to cart')
      return
    }
    case 'navigate':
      runtime.navigateTo(click.obj_type, click.obj_name)
      return
    case 'set_inputs':
      // Values may name the clicked row (`{{line._txn_id}}`) or any variable;
      // a value with no tokens is stored exactly as before.
      for (const [name, value] of Object.entries(click.values || {})) {
        runtime.setInput(name, typeof value === 'string' && value.includes('{{')
          ? substituteVars(value, deps.vars || {})
          : value)
      }
      return
    case 'run_action':
      // The variables at the click — inside a repeat, the clicked ROW — go
      // with the action, so its steps can say `{{line._txn_id}}`.
      await runtime.runActionByName(click.actionName, deps.vars)
      return
    case 'scroll_to':
      scrollToAnchor(click.target, click.offset, click.smooth)
      return
    case 'open_popup':
      if (openPopup) openPopup(click.sectionId)
      return
    case 'toggle_var': {
      const on = click.on ?? 'true'
      const off = click.off ?? 'false'
      // `vars` may be a resolver function rather than a plain map, so read
      // defensively — an unreadable current value simply turns the variable ON.
      const current = typeof deps.vars === 'function'
        ? deps.vars(click.name)
        : deps.vars && typeof deps.vars === 'object'
          ? (deps.vars as Record<string, unknown>)[click.name]
          : undefined
      runtime.setInput(click.name, String(current ?? '') === on ? off : on)
      return
    }
    case 'copy': {
      const text = substituteVars(click.text, deps.vars || {}) || ''
      try {
        await navigator.clipboard.writeText(text)
        deps.onFeedback?.(click.confirm || 'Copied')
      } catch {
        // Clipboard access can be denied (insecure origin, permissions).
        // Say so rather than silently appearing to have copied.
        deps.onFeedback?.('Could not copy')
      }
      return
    }
    case 'play_media': {
      const el = document.getElementById(`pg-media-${click.blockId}`) as HTMLVideoElement | null
      if (!el || typeof el.play !== 'function') return
      const action = click.action || 'toggle'
      if (action === 'pause' || (action === 'toggle' && !el.paused)) el.pause()
      else { const p = el.play(); if (p) p.catch(() => { /* autoplay policy */ }) }
      return
    }
    case 'sign_out': {
      if (!deps.auth) return
      try { await deps.auth.onLogout() } catch { /* idempotent: already signed out */ }
      const to = click.redirect ? resolveHref(click.redirect) : ''
      if (to) window.location.href = to
      else window.location.reload()
      return
    }
    case 'none':
    default:
      return
  }
}

/**
 * Smooth-scroll to an anchor, clearing a sticky header.
 *
 * Delegates to `window.pgScrollTo` when the shared anchor script has loaded
 * (it owns the header-offset measurement and the focus handoff), and falls
 * back to a direct scroll otherwise — so this works in the designer canvas
 * and the in-app preview, which don't inline the static-site scripts.
 */
export function scrollToAnchor(target: string, offset?: number, smooth?: boolean): void {
  if (typeof window === 'undefined') return
  const api = (window as unknown as { pgScrollTo?: (t: string, o?: number, s?: boolean) => void }).pgScrollTo
  if (api) return api(target, offset, smooth)
  const id = String(target || '').replace(/^#/, '')
  if (!id) return
  const el = document.getElementById(id)
  if (!el) return
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  const header = document.querySelector('.pg-sticky-header') as HTMLElement | null
  const off = typeof offset === 'number' ? offset : (header?.offsetHeight || 0) + 12
  const y = el.getBoundingClientRect().top + window.scrollY - off
  window.scrollTo({ top: Math.max(0, y), behavior: smooth === false || reduce ? 'auto' : 'smooth' })
}

/** Hook form of the dispatcher — the standard way a renderer runs a click. */
export function useClickDispatch() {
  const runtime = usePageRuntime()
  const { resolveHref } = useSiteLink()
  const openPopup = useContext(PopupOpenContext)
  const vars = useContext(PageVarsContext)
  const auth = useContext(AuthMenuContext)
  return (click: ButtonClick, onFeedback?: (m: string) => void) =>
    dispatchClick(click, { runtime, resolveHref, openPopup, vars, onFeedback, auth })
}

type AddToCartClick = Extract<ButtonClick, { kind: 'add_to_cart' }>

/**
 * The line an add-to-cart click puts in the basket — and the entry a heart
 * beside it saves to the wishlist, so the two always describe one product.
 * Every field goes through substitution, so a button inside a REPEAT can
 * template its line from the row (`"₹{{p.UNIT_PRICE}}"`, `"/{{p.SLUG}}"`).
 */
function cartItemOf(click: AddToCartClick, s: (t?: string) => string) {
  const price = s(String(click.price ?? '')) || ''
  return {
    sku: s(click.sku) || click.sku,
    name: s(click.name) || click.sku,
    // Split the authored price once, here: the digits do the arithmetic,
    // the leading symbol does the formatting.
    price: Number(price.replace(/[^0-9.]/g, '')) || 0,
    prefix: (price.match(/^[^0-9.]*/) || [''])[0].trim(),
    image: click.image ? s(click.image) : undefined,
    href: click.href ? s(click.href) : undefined,
    meta: click.meta ? s(click.meta) : undefined,
  }
}

/**
 * The control an Add button turns into once its item is in the basket — a
 * hidden TEMPLATE the shared cart script reveals and fills (cart.ts
 * `paintAdd`), because a published page runs no React effects. `stepper`
 * replaces the button with − "In cart · n" +, editing that cart line (0
 * removes it); `note` just says so beside the button.
 */
function InCartControl({ mode, size }: { mode: 'stepper' | 'note'; size: NonNullable<ButtonSpec['size']> }) {
  const h = size === 'sm' ? 32 : size === 'lg' ? 46 : 40
  const stepBtn: React.CSSProperties = {
    width: h - 4, height: h - 2, font: 'inherit', fontSize: themeRem(1.15), cursor: 'pointer',
    border: 0, background: 'none', color: 'inherit', lineHeight: 1, flex: '0 0 auto',
  }
  if (mode === 'note') {
    return (
      <span
        data-pg-in-cart-ui="note" hidden aria-live="polite"
        style={{
          display: 'none', alignItems: 'center', gap: 6, fontSize: themeRem(0.85), fontWeight: 600,
          color: 'var(--pg-success, #16a34a)',
        }}
      >
        <Check size={14} aria-hidden />
        <span data-pg-in-cart-text="" />
      </span>
    )
  }
  return (
    <span
      data-pg-in-cart-ui="stepper" hidden role="group"
      style={{
        display: 'none', alignItems: 'center', height: h, borderRadius: 'var(--pg-button-radius, 8px)',
        background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', fontWeight: 600,
        ...BTN_SIZE_STYLE[size], padding: 0,
      }}
    >
      <button type="button" data-pg-cart-step="-1" aria-label="Decrease quantity" style={stepBtn}>&minus;</button>
      <span data-pg-in-cart-text="" aria-live="polite" style={{ padding: '0 4px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} />
      <button type="button" data-pg-cart-step="1" aria-label="Increase quantity" style={stepBtn}>+</button>
    </span>
  )
}

/**
 * An Add button with what the visitor chooses before pressing it: how many
 * (`qtyPicker`) and, for a stay or a rental, which dates (`booking`).
 *
 * This renders a SHELL only — the stepper, the calendar and the validation
 * are the shared cart script's (`initCart`), so a published page with no
 * React behaves exactly like the designer canvas. Everything it needs is
 * declared on the wrapper: `data-pg-book` carries the booking rules, and the
 * script keeps the visitor's choice on the same element.
 */
function AddToCartChooser({ click, startQty, button }: {
  click: AddToCartClick; startQty: number; button: React.ReactNode
}) {
  const s = useS()
  const b = click.booking
  const book = b ? JSON.stringify({
    unit: b.unit === 'day' ? 'day' : 'night',
    min: b.minUnits, max: b.maxUnits, adv: b.maxAdvanceDays,
    // Substituted first, so a page variable (sold-out days from the
    // application's data) can supply the list.
    blocked: (s(b.blockedDates || '') || '').split(/[\s,;|]+/)
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)),
    start: b.defaultStart ? s(b.defaultStart) : '',
    end: b.defaultEnd ? s(b.defaultEnd) : '',
    startLabel: b.startLabel ? s(b.startLabel) : undefined,
    endLabel: b.endLabel ? s(b.endLabel) : undefined,
  }) : undefined
  const maxQty = Math.max(startQty, Math.floor(Number(click.maxQty) || 99))
  const stepBtn: React.CSSProperties = {
    width: 36, height: 38, font: 'inherit', fontSize: 18, cursor: 'pointer',
    border: 0, background: 'none', color: 'var(--pg-text)', lineHeight: 1,
  }
  const qtyLabel = click.qtyLabel ? s(click.qtyLabel) : ''
  return (
    <div
      data-pg-cart-buy=""
      {...(book ? { 'data-pg-book': book } : {})}
      style={{
        display: 'flex', flexDirection: 'column', gap: 10,
        // A FIXED width: inside a shrink-to-fit button group, a fluid one
        // resized with the summary text and moved the days under the cursor.
        ...(b ? { width: 340, maxWidth: '100%' } : {}),
      }}
    >
      {b && (
        <>
          <div
            data-pg-book-cal=""
            style={{
              border: '1px solid var(--pg-border)', borderRadius: 'var(--pg-radius, 12px)',
              padding: 10, background: 'var(--pg-surface)', minHeight: 250,
            }}
          />
          <div
            data-pg-book-summary=""
            aria-live="polite"
            style={{ fontSize: themeRem(0.85), fontWeight: 600, color: 'var(--pg-heading, var(--pg-text))' }}
          />
        </>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {click.qtyPicker && (
          <span role="group" aria-label={qtyLabel || 'Quantity'} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {qtyLabel && (
              <span style={{ fontSize: themeRem(0.85), fontWeight: 600, color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{qtyLabel}</span>
            )}
            <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--pg-border)', borderRadius: 999, height: 40, background: 'var(--pg-surface)' }}>
              <button type="button" data-pg-buy-step="-1" aria-label="Decrease quantity" style={stepBtn}>&minus;</button>
              <input
                type="text" inputMode="numeric" pattern="[0-9]*"
                data-pg-buy-qty="" defaultValue={String(startQty)} min={1} max={maxQty}
                aria-label={qtyLabel || 'Quantity'}
                style={{
                  width: 40, textAlign: 'center', border: 0, background: 'none',
                  font: 'inherit', fontWeight: 700, color: 'var(--pg-text)',
                  fontVariantNumeric: 'tabular-nums', outline: 'none',
                }}
              />
              <button type="button" data-pg-buy-step="1" aria-label="Increase quantity" style={stepBtn}>+</button>
            </span>
          </span>
        )}
        {button}
      </div>
      <div
        data-pg-book-msg=""
        role="alert"
        style={{ display: 'none', fontSize: themeRem(0.8), color: 'var(--pg-error, #c0392b)' }}
      />
    </div>
  )
}

export function PageButton({ btn: spec }: { btn: ButtonSpec | undefined }) {
  // A renderer may hand us nothing at all (a card CTA cleared in the editor,
  // a hand-authored block). Render a harmless button rather than throwing
  // out of the whole page tree.
  const btn: ButtonSpec = spec ?? { id: 'btn', label: '', variant: 'primary' }
  const s = useS()
  const runtime = usePageRuntime()
  const { resolveHref, pageHref } = useSiteLink()
  const dispatch = useClickDispatch()
  const click = resolveClick(btn)
  const [toast, setToast] = useState<string | null>(null)

  // A picker or a booking calendar beside an Add button is drawn by the
  // shared cart script. The page-level boot may already have run by the
  // time this button mounts (a product page whose record resolves late), so
  // the button asks for it itself; `initCart` is idempotent.
  const needsCartWiring = click.kind === 'add_to_cart' && (!!click.qtyPicker || !!click.booking)
  useEffect(() => {
    if (!needsCartWiring || typeof document === 'undefined') return
    const go = () => { try { initCart() } catch { /* a cart never takes the page down */ } }
    go()
    window.requestAnimationFrame(go)
  }, [needsCartWiring])

  // Loading state for run_action while in flight; disables the button.
  const actionState = click.kind === 'run_action'
    ? runtime.getActionState(click.actionName)
    : undefined
  const loading = !!actionState?.loading

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (loading) return
    await dispatch(click, (m) => {
      setToast(m)
      window.setTimeout(() => setToast(null), 1600)
    })
  }

  // Emit a REAL href wherever the intent resolves to one, so the link is
  // crawlable, middle-clickable, and works on the static site with no JS.
  // Substituted first: a CTA inside a REPEAT points at its own row
  // (`/{{p.SLUG}}`), which is how a bound card links to the record it was
  // rendered from without needing a binding per card.
  const clickUrl = click.kind === 'external_url' ? s(click.url || '') : ''
  // A `navigate` to a page of this site is a link to its URL too (the live
  // click is still handled by the runtime; this is what the static site uses).
  const navUrl = click.kind === 'navigate' && (!click.obj_type || click.obj_type === 'S') && pageHref
    ? pageHref(click.obj_name) : null
  const href =
    click.kind === 'external_url'
      ? (isAnchorHref(clickUrl) ? clickUrl : resolveHref(clickUrl || '#'))
      : click.kind === 'scroll_to'
        ? `#${String(click.target || '').replace(/^#/, '')}`
        : navUrl ? resolveHref(navUrl) : '#'
  const target = click.kind === 'external_url' && click.newTab ? '_blank' : undefined

  const style: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    borderRadius: 'var(--pg-button-radius, 8px)', fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
    transition: 'transform 0.05s, opacity 0.15s',
    textDecoration: btn.variant === 'link' ? 'underline' : 'none',
    opacity: loading ? 0.7 : 1,
    pointerEvents: loading ? 'none' : 'auto',
    ...BTN_VARIANT_STYLE[btn.variant],
    ...BTN_SIZE_STYLE[btn.size || 'md'],
  }
  const icon = loading
    ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
    : (btn.icon ? <PageIcon name={btn.icon} size={16} /> : null)

  // An add-to-cart button is a real <button> carrying its line as a data
  // attribute, so the ONE shared cart handler drives it identically on the
  // canvas, the preview, the deployed runtime and a published page — where
  // there is no React at all. It is deliberately not an <a>: it goes
  // nowhere, and a link that goes nowhere is a lie to a keyboard user.
  if (click.kind === 'add_to_cart') {
    // Every field goes through substitution, so a button inside a REPEAT
    // can template its line from the row — `"₹{{p.UNIT_PRICE}}"`,
    // `"/{{p.SLUG}}"` — instead of needing a binding per field.
    const startQty = Math.max(1, Math.floor(Number(click.qty) || 1))
    const item = cartItemOf(click, s)
    const label = s(btn.label)
    // The in-cart state: a − n + stepper that REPLACES the button (default),
    // or — where the button already has its own chooser (a quantity picker,
    // a booking calendar) — a quiet note beside it. Both are hidden
    // templates the shared cart script reveals; see cart.ts `paintAdd`.
    const inCartLabel = click.inCartLabel === undefined ? 'In cart · {n}' : s(click.inCartLabel)
    const hasChooser = !!click.qtyPicker || !!click.booking
    const stepper = !!inCartLabel && (click.inCartStepper ?? !hasChooser) && !click.booking
    const toastCfg = click.toast === false ? undefined : JSON.stringify({
      text: s(click.toastLabel) || 'Added to cart',
      link: s(click.toastLinkLabel) || 'View cart',
      href: click.cartHref ? resolveHref(s(click.cartHref)) : undefined,
    })
    const button = (
      <button
        type="button"
        data-pg-add-cart={JSON.stringify({ ...item, qty: startQty })}
        data-pg-added-label={s(click.addedLabel) || 'Added to cart'}
        data-pg-label={label}
        data-pg-in-cart-label={inCartLabel || undefined}
        data-pg-cart-toast={toastCfg}
        style={{ ...style, border: style.border ?? 'none' }}
      >
        {icon}
        <span data-pg-cart-label="">{label}</span>
      </button>
    )
    const inCartUi = inCartLabel ? (
      <InCartControl mode={stepper ? 'stepper' : 'note'} size={btn.size || 'md'} />
    ) : null
    const heart = click.wishlist ? (
      <WishlistHeart
        item={item}
        addLabel={s(click.wishlistAddLabel) || undefined}
        removeLabel={s(click.wishlistRemoveLabel) || undefined}
      />
    ) : null
    const control = (inCartUi || heart) ? (
      <span data-pg-cart-ctl="" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {button}
        {inCartUi}
        {heart}
      </span>
    ) : button
    if (!hasChooser) return control
    return <AddToCartChooser click={click} startQty={startQty} button={control} />
  }

  return (
    <a
      href={href}
      target={target}
      rel={target === '_blank' ? 'noreferrer' : undefined}
      onClick={handleClick}
      style={style}
      aria-disabled={loading || undefined}
    >
      {icon}
      <span>{toast || s(btn.label)}</span>
    </a>
  )
}

// ─── Hero ───────────────────────────────────────────

const HERO_HEIGHT: Record<NonNullable<HeroBlock['height']>, string> = {
  compact: '320px', normal: '460px', tall: '600px', screen: '80vh',
}

// Pick the right default text color for a hero/CTA band. The block has a
// "real" background when it sets a color/gradient/image — in that case
// the typography needs to read against the band, so we use the theme's
// `--pg-inverted` (white on dark themes, near-black on warm/light ones).
// When the background is none, the band uses the page background and
// the regular `--pg-text` is correct.
function heroTextDefault(bg: Background | undefined): string {
  // No opaque fill (none / transparent / unset) → the band shows the page
  // background, so the regular text color reads correctly.
  if (!bgHasFill(bg)) return 'var(--pg-text, #1e293b)'
  return 'var(--pg-inverted, #ffffff)'
}

// True when the hero should render a media carousel (1+ slides).
export function hasHeroSlides(block: HeroBlock): boolean {
  return !!(block.slides && block.slides.length > 0)
}

// Flex justify/align for placing the content block over a full-bleed carousel.
export function heroContentJustify(pos: HeroContentPosition | undefined, align: Align): React.CSSProperties {
  const x = pos?.x || align
  const y = pos?.y || 'center'
  return {
    justifyContent: x === 'left' ? 'flex-start' : x === 'right' ? 'flex-end' : 'center',
    alignItems: y === 'top' ? 'flex-start' : y === 'bottom' ? 'flex-end' : 'center',
  }
}

function heroArrowStyle(side: 'left' | 'right'): React.CSSProperties {
  return {
    position: 'absolute', zIndex: 3, top: '50%', transform: 'translateY(-50%)',
    ...(side === 'left' ? { left: 12 } : { right: 12 }),
    width: 38, height: 38, borderRadius: 999, border: 'none', cursor: 'pointer',
    background: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: 24, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }
}

function HeroSlideMedia({ slide }: { slide: HeroSlide }) {
  const fill: React.CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }
  if (slide.type === 'video') {
    const provider = slide.provider || 'file'
    if (provider === 'youtube') {
      const id = youtubeId(slide.url)
      if (id) return (
        <iframe
          src={`https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&playsinline=1`}
          allow="autoplay; encrypted-media; picture-in-picture" style={fill}
        />
      )
    } else if (provider === 'vimeo') {
      const id = vimeoId(slide.url)
      if (id) return (
        <iframe
          src={`https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1&background=1`}
          allow="autoplay; fullscreen" style={fill}
        />
      )
    }
    return (
      <video
        src={slide.url}
        autoPlay muted loop playsInline
        // Force the `muted` DOM property (not just the attribute) and kick off
        // playback — React doesn't always set the property, and Chrome gates
        // muted-autoplay on the property being true. No-op during SSR.
        ref={(el) => { if (el) { el.muted = true; const p = el.play(); if (p) p.catch(() => {}) } }}
        style={{ ...fill, objectFit: 'cover', background: '#000' }}
      />
    )
  }
  return <img src={slide.url} alt="" style={{ ...fill, objectFit: 'cover' }} />
}

// ─── Hero media carousel ─────────────────────────────
// Shared across all surfaces: renders the slides full-bleed behind the hero
// content, with auto-advance + dot/arrow controls. State (current slide,
// autoplay) lives here via hooks, so the designer canvas, in-app preview and
// deployed runtime are all interactive. On the static SSR site the hooks don't
// run (renderToStaticMarkup) — ServerHero in renderSite.tsx emits a tiny
// vanilla script that drives the SAME DOM, matched by the stable class names
// (`pg-hero-slide`/`pg-hero-dot` + `is-active`) and data-* hooks below. The
// opacity-fade CSS is emitted inline, scoped to this hero's id, so it needs no
// global stylesheet.
export function HeroCarousel({ block, minHeight, children, disableAutoplay }: { block: HeroBlock; minHeight: string; children: React.ReactNode; disableAutoplay?: boolean }) {
  const slides = block.slides || []
  const multi = slides.length > 1
  const autoplay = block.autoplay !== false
  const interval = Math.max(1500, block.autoplayInterval || 5000)
  const showDots = block.showDots !== false && multi
  const showArrows = block.showArrows !== false && multi
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    // `disableAutoplay` is set on the designer canvas so the slide doesn't
    // re-advance (and re-render the editable content) while the author types.
    if (!autoplay || !multi || disableAutoplay) return
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), interval)
    return () => clearInterval(t)
  }, [autoplay, multi, interval, slides.length, disableAutoplay])

  const go = (e: React.MouseEvent, i: number) => {
    e.stopPropagation()
    setIdx(((i % slides.length) + slides.length) % slides.length)
  }
  const rootId = `pg-hero-${block.id}`
  const css =
    `#${rootId} .pg-hero-slide{position:absolute;inset:0;opacity:0;transition:opacity .7s ease;pointer-events:none}` +
    `#${rootId} .pg-hero-slide.is-active{opacity:1}`

  return (
    <section
      id={rootId}
      data-hero-carousel=""
      data-autoplay={autoplay && multi ? interval : 0}
      style={{
        position: 'relative', minHeight, borderRadius: heroRadius(block), overflow: 'hidden', display: 'flex',
        ...backgroundStyle(block.background),
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        {slides.map((sl, i) => (
          <div key={sl.id} className={`pg-hero-slide${i === idx ? ' is-active' : ''}`} data-slide-index={i}>
            <HeroSlideMedia slide={sl} />
          </div>
        ))}
      </div>
      {block.scrim && <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: block.scrim, pointerEvents: 'none' }} />}
      <div style={{
        position: 'relative', zIndex: 2, flex: 1, display: 'flex',
        ...heroContentJustify(block.contentPosition, block.align),
        padding: '48px 44px', pointerEvents: 'none',
        // A bleeding hero's picture spans the viewport, but its text stays on
        // the page's content column — lined up with every other section.
        ...(((block as { style?: { bleed?: boolean } }).style?.bleed) ? {
          paddingLeft: 'max(44px, calc((100% - var(--pg-content-max, 1200px)) / 2 + var(--pg-page-padding-x, 24px)))',
          paddingRight: 'max(44px, calc((100% - var(--pg-content-max, 1200px)) / 2 + var(--pg-page-padding-x, 24px)))',
        } : {}),
      }}>
        <div style={{
          maxWidth: 720, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, pointerEvents: 'auto',
          alignItems: block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'flex-start',
          textAlign: block.align,
        }}>
          {children}
        </div>
      </div>
      {showArrows && (
        <>
          <button type="button" data-arrow="prev" aria-label="Previous slide" onClick={(e) => go(e, idx - 1)} style={heroArrowStyle('left')}>‹</button>
          <button type="button" data-arrow="next" aria-label="Next slide" onClick={(e) => go(e, idx + 1)} style={heroArrowStyle('right')}>›</button>
        </>
      )}
      {showDots && (
        <div style={{ position: 'absolute', zIndex: 3, left: 0, right: 0, bottom: 14, display: 'flex', justifyContent: 'center', gap: 8 }}>
          {slides.map((sl, i) => (
            <button
              key={sl.id} type="button" data-dot-index={i} aria-label={`Go to slide ${i + 1}`}
              className={`pg-hero-dot${i === idx ? ' is-active' : ''}`}
              onClick={(e) => go(e, i)}
              style={{
                width: 9, height: 9, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 0,
                background: i === idx ? '#fff' : 'rgba(255,255,255,0.5)', boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
              }}
            />
          ))}
        </div>
      )}
    </section>
  )
}

/** The hero headline's size + weight. It scales with the viewport between a
 *  phone floor and the authored size, so a 60px desktop headline does not
 *  wrap to five lines on a phone. */
export function heroTitleSize(block: Pick<HeroBlock, 'titleSize' | 'titleWeight'>): React.CSSProperties {
  const px = Number(block.titleSize)
  const max = Number.isFinite(px) && px > 0 ? `${px}px` : themeRem(2.6)
  // Reaches the authored size at a 1440px-wide viewport.
  const fluid = Number.isFinite(px) && px > 0 ? `${(px / 14.4).toFixed(2)}vw` : '3.6vw'
  return {
    fontSize: `clamp(${themeRem(1.9)}, ${fluid}, ${max})`,
    fontWeight: Number(block.titleWeight) > 0 ? Number(block.titleWeight) : 800,
    // The theme's heading face — without it a hero headline fell back to the
    // host app's own h1 font.
    fontFamily: 'var(--pg-heading-font, var(--pg-font, inherit))',
  }
}

// The eyebrow/title/subtitle/CTA stack — shared by the slide + non-slide hero
// layouts. `text` carries the resolved text color.
function HeroContent({ block, text }: { block: HeroBlock; text: React.CSSProperties }) {
  const s = useS()
  const align = block.align
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  return (
    <>
      {block.eyebrow && (
        <span className="pg-eyebrow" style={{
          ...text, opacity: 0.85, padding: '4px 10px',
          background: 'rgba(255,255,255,0.12)', borderRadius: 999,
        }}>{s(block.eyebrow)}</span>
      )}
      <h1 style={{ ...text, ...heroTitleSize(block), lineHeight: 1.1, margin: 0, maxWidth: 760 }}>
        {s(block.title)}
      </h1>
      {block.subtitle && (
        <p style={{ ...text, fontSize: themeRem(1.05), lineHeight: 1.5, margin: 0, opacity: 0.92, maxWidth: 620 }}>
          {s(block.subtitle)}
        </p>
      )}
      {(block.primaryCta || block.secondaryCta) && (
        <div style={{ display: 'flex', gap: 10, marginTop: 8, ...text, flexWrap: 'wrap', justifyContent: justify }}>
          {block.primaryCta && <PageButton btn={block.primaryCta} />}
          {block.secondaryCta && <PageButton btn={block.secondaryCta} />}
        </div>
      )}
    </>
  )
}

/** The hero's own corner: the block's `style.borderRadius` when set (0 for
 * an edge-to-edge band), else the rounded card it has always been — and
 * square whenever it bleeds to the viewport edges. */
export function heroRadius(block: HeroBlock): number {
  const st = (block as { style?: { borderRadius?: number; bleed?: boolean } }).style
  if (typeof st?.borderRadius === 'number') return st.borderRadius
  return st?.bleed ? 0 : 12
}

export function HeroRenderer({ block }: { block: HeroBlock }) {
  const align = block.align
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  // Over a carousel default to white text (images are usually busy/dark);
  // otherwise pick the contrast-safe color for the band vs. the page bg.
  const text: React.CSSProperties = { color: block.textColor || (hasHeroSlides(block) ? '#ffffff' : heroTextDefault(block.background)) }

  if (hasHeroSlides(block)) {
    return (
      <HeroCarousel block={block} minHeight={HERO_HEIGHT[block.height || 'normal']}>
        <HeroContent block={block} text={text} />
      </HeroCarousel>
    )
  }

  const hasSideImage = block.imageSide && block.imageSide !== 'none' && block.imageUrl
  return (
    <section
      className={backgroundClass(block.background)}
      style={{
        position: 'relative',
        ...backgroundStyle(block.background),
        minHeight: HERO_HEIGHT[block.height || 'normal'],
        borderRadius: heroRadius(block),
        overflow: 'hidden',
        padding: '48px 36px',
        display: 'flex',
        alignItems: 'center',
        gap: 32,
        // Wrap so the text + image stack on narrow viewports instead
        // of squeezing into two columns at ~150px each.
        flexWrap: 'wrap',
        flexDirection: hasSideImage && block.imageSide === 'right' ? 'row' : 'row-reverse',
      }}
    >
      <VideoBackdrop background={block.background} blockId={block.id} />
      {hasSideImage && (
        // `flex: 1 1 280px` lets the column shrink down to 280px before
        // wrapping to a new row — keeps the image side from collapsing
        // to nothing on tablet, then stacks below the text on mobile.
        <div style={{ flex: '1 1 280px', display: 'flex', justifyContent: 'center', minWidth: 0, position: 'relative', zIndex: 1 }}>
          <img
            src={block.imageUrl}
            alt=""
            style={{ maxWidth: '100%', maxHeight: 380, borderRadius: 12, boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}
          />
        </div>
      )}
      <div style={{
        flex: '1 1 280px', minWidth: 0, position: 'relative', zIndex: 1,
        display: 'flex', flexDirection: 'column', gap: 16,
        alignItems: justify, textAlign: align,
      }}>
        <HeroContent block={block} text={text} />
      </div>
    </section>
  )
}

// ─── CTA Banner ──────────────────────────────────────

export function CtaBannerRenderer({ block }: { block: CtaBannerBlock }) {
  const s = useS()
  const align = block.align
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  // Hero/CTA sit on a brand-colored band — `--pg-inverted` is the
  // contrast-safe text color for that band (white on dark themes,
  // near-black on warm/light gradients like Bold). Falls back to
  // `--pg-text` when the band is using the page background, then
  // a sensible literal so the canvas never renders fully invisible
  // text even before the theme vars are wired in.
  const text: React.CSSProperties = { color: block.textColor || heroTextDefault(block.background) }
  return (
    <section
      className={backgroundClass(block.background)}
      style={{
        position: 'relative',
        ...backgroundStyle(block.background),
        borderRadius: 12, overflow: 'hidden',
        padding: '32px 28px',
        display: 'flex', flexDirection: 'column', gap: 12,
        alignItems: justify, textAlign: align,
      }}
    >
      <VideoBackdrop background={block.background} blockId={block.id} />
      {block.eyebrow && (
        <span className="pg-eyebrow" style={{ ...text, opacity: 0.9 }}>
          {s(block.eyebrow)}
        </span>
      )}
      <h2 style={{ ...text, fontSize: themeRem(1.8), fontWeight: 800, margin: 0, lineHeight: 1.2 }}>{s(block.title)}</h2>
      {block.subtitle && (
        <p style={{ ...text, fontSize: themeRem(1), margin: 0, opacity: 0.9, maxWidth: 600 }}>{s(block.subtitle)}</p>
      )}
      {(block.primaryCta || block.secondaryCta) && (
        <div style={{ display: 'flex', gap: 10, marginTop: 6, ...text, flexWrap: 'wrap', justifyContent: justify }}>
          {block.primaryCta && <PageButton btn={block.primaryCta} />}
          {block.secondaryCta && <PageButton btn={block.secondaryCta} />}
        </div>
      )}
    </section>
  )
}

// ─── Spacer ─────────────────────────────────────────

export function SpacerRenderer({ block }: { block: SpacerBlock }) {
  return (
    <div
      style={{
        height: block.height,
        position: 'relative',
        border: '1px dashed transparent',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--pg-border)' }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'transparent' }}
      title={`Spacer · ${block.height}px`}
    />
  )
}

// ─── Columns ────────────────────────────────────────

// Normalize a per-column width to a CSS track size. Numbers → px; a bare
// number-like string → px; "30%"/"180px"/"auto"/etc. pass through; empty /
// undefined → null (meaning "fill the remaining space" = 1fr).
/** True when any list item carries its own icon (bullets give way to them). */
export function listHasIcons(items: { iconUrl?: string }[] | undefined): boolean {
  return !!items && items.some((i) => !!i.iconUrl)
}

/** The picture a list item shows in place of its bullet. */
export function ListItemIcon({ url }: { url: string }) {
  return (
    <img
      src={url}
      alt=""
      aria-hidden
      style={{ display: 'inline-block', width: '1.15em', height: '1.15em', objectFit: 'contain', verticalAlign: '-0.2em', marginRight: 8, flex: '0 0 auto' }}
    />
  )
}

/**
 * The ONE small-screen rule for a `columns` row, shared by every surface
 * (page-runtime ColumnsRenderer, the in-app preview, the static SSR).
 *
 * Explicit-width tracks don't reflow, so they stack at the tablet
 * breakpoint; an equal split stacks at the phone breakpoint. `mobileOrder:
 * 'reverse'` flips the stacked order — an image-left | text-right row then
 * reads text-first on a phone, which is how a source site that alternates
 * image sides presents itself on mobile.
 */
export function columnsStackCss(
  blockId: string, opts: {
    hasExplicit: boolean; count: number; mobileOrder?: 'normal' | 'reverse'
    mobileLayout?: 'stack' | 'wrap'
    /** `align: 'stretch'` — columns as tall as the row, their blocks filling them. */
    stretch?: boolean
  },
): { className: string; css: string } {
  const cls = `pg-cols-${String(blockId).replace(/[^A-Za-z0-9_-]/g, '')}`
  // Each surface wraps a column's blocks in one or two elements of its own
  // (cell → list → block wrapper → block), so the fill is passed down three
  // levels; a level that is not there matches nothing.
  const fill = opts.stretch
    ? `.${cls}{align-items:stretch!important}.${cls}>*{display:flex!important;flex-direction:column}`
      + `.${cls}>*>*{flex:1 1 auto;display:flex;flex-direction:column}`
      + `.${cls}>*>*>*{flex:1 1 auto}.${cls}>*>*>*>section{flex:1 1 auto}`
    : ''
  const out = (r: { className: string; css: string }) => ({ className: r.className, css: fill + r.css })
  if (opts.mobileLayout === 'wrap') {
    // Side by side at natural width; a full row wraps instead of stacking.
    return out({
      className: cls,
      css: `@media (max-width:${RESPONSIVE_BREAKPOINTS.mobile}px){.${cls}{display:flex!important;flex-wrap:wrap;align-items:center}`
        + `.${cls}>*{flex:0 1 auto;min-width:0}}`,
    })
  }
  const bp = opts.hasExplicit ? RESPONSIVE_BREAKPOINTS.tablet : RESPONSIVE_BREAKPOINTS.mobile
  let rules = `.${cls}{grid-template-columns:1fr!important;justify-content:stretch!important}`
  if (opts.mobileOrder === 'reverse' && opts.count > 1) {
    for (let i = 1; i <= opts.count; i++) rules += `.${cls}>:nth-child(${i}){order:${opts.count - i}}`
  }
  return out({ className: cls, css: `@media (max-width:${bp}px){${rules}}` })
}

export function columnTrack(width: string | number | undefined): string | null {
  if (width === undefined || width === null) return null
  if (typeof width === 'number') return Number.isFinite(width) ? `${width}px` : null
  const w = width.trim()
  if (!w) return null
  if (/^\d+(\.\d+)?$/.test(w)) return `${w}px`   // "180" → "180px"
  return w                                        // "30%", "180px", "auto", "1fr"
}

// A column counts as "present" when it actually has content (nested blocks
// or text). Empty padding columns and unfilled side columns are not.
function columnHasContent(c: ColumnContent): boolean {
  return (!!c.blocks && c.blocks.length > 0) || (!!c.text && c.text.trim().length > 0)
}

export function ColumnsRenderer({ block, renderChildren }: { block: ColumnsBlock; renderChildren?: RenderChildren }) {
  const s = useS()
  const all = (block.columns || []).slice(0, block.count)
  // pad columns to count
  while (all.length < block.count) {
    all.push({ id: `pad_${all.length}`, text: '' })
  }

  // When the row uses explicit fixed widths, an empty side column shouldn't
  // reserve its fixed track — drop empties so a fill (1fr) column expands
  // into the freed space (e.g. a header whose right column is hidden lets the
  // center nav stretch). The equal-split path keeps every column (auto-fit
  // already collapses blank tracks) so unstyled column layouts are unchanged.
  const anyExplicit = all.some((c) => columnTrack(c.width) !== null)
  const present = all.filter(columnHasContent)
  const items = anyExplicit && present.length ? present : all

  // Per-column sizing: any column with an explicit width opts the whole row
  // out of the responsive equal-split grid into an explicit track template.
  // Unsized columns become `1fr` and share the slack equally.
  const tracks = items.map((c) => columnTrack(c.width))
  const hasExplicit = tracks.some((t) => t !== null)
  const hasFill = tracks.some((t) => t === null)
  const justifyMap = { start: 'start', center: 'center', end: 'end', between: 'space-between' } as const
  const gridTemplateColumns = hasExplicit
    ? tracks.map((t) => t ?? '1fr').join(' ')
    : responsiveGrid(block.count, 200, block.gap ?? 24)

  // Explicit fixed-width tracks don't reflow on their own, so a header built
  // from them overflows on small screens. Stack into a single column at/below
  // the tablet breakpoint so the layout stays responsive. (The auto-fit
  // `responsiveGrid` path already reflows, so it needs no extra rule.)
  // Auto-fit already reflows an equal split; only explicit tracks (or a
  // reversed mobile order) need the shared small-screen rule.
  const stack = columnsStackCss(block.id, { hasExplicit, count: items.length, mobileOrder: block.mobileOrder, mobileLayout: block.mobileLayout, stretch: block.align === 'stretch' })
  const stackCls = stack.className
  const stackCss = hasExplicit || block.mobileOrder === 'reverse' ? stack.css : ''

  return (
    <>
      {stackCss ? <style dangerouslySetInnerHTML={{ __html: stackCss }} /> : null}
      <div
        className={stackCls}
        style={{
          display: 'grid',
          gridTemplateColumns,
          gap: block.gap ?? 24,
          alignItems: block.align === 'center' ? 'center' : block.align === 'bottom' ? 'end' : block.align === 'stretch' ? 'stretch' : 'start',
          // When all sized columns leave slack (no fill column), honor `justify`.
          ...(hasExplicit && !hasFill ? { justifyContent: justifyMap[block.justify || 'start'] } : null),
        }}
      >
        {items.map((col) => {
        // v2: a column hosts nested blocks. Render them via the host's
        // recursive renderer in a clean stacked cell (no designer chrome).
        if (renderChildren && col.blocks && col.blocks.length) {
          return (
            <div key={col.id} className={ITEM_CLASS} style={{ display: 'grid', gap: 'var(--pg-block-gap, 16px)', minWidth: 0 }}>
              {renderChildren(col.blocks, col.id)}
            </div>
          )
        }
        return (
          <div
            key={col.id}
            style={{
              padding: 12,
              borderRadius: 8,
              background: 'var(--pg-bg)',
              border: '1px dashed var(--pg-border)',
              whiteSpace: 'pre-wrap',
              color: 'var(--pg-text)',
              fontSize: themeRem(0.9),
              lineHeight: 1.5,
              minHeight: 60,
            }}
          >
            {col.text ? s(col.text) : <span style={{ color: 'var(--pg-text-secondary)', opacity: 0.6 }}>Column content…</span>}
          </div>
        )
        })}
      </div>
    </>
  )
}

// ─── Container ──────────────────────────────────────

const CONTAINER_JUSTIFY: Record<string, CSSProperties['justifyContent']> = {
  start: 'flex-start', center: 'center', end: 'flex-end', between: 'space-between',
}

/**
 * A container's height / vertical placement / width box — ONE rule for every
 * surface (shared renderer, site viewer, static site). `outer` goes on the
 * container element, `inner` on the element wrapping its children.
 */
export function containerLayout(block: Pick<ContainerBlock, 'minHeight' | 'justify' | 'maxWidth'>): {
  outer: CSSProperties; inner: CSSProperties
} {
  const outer: CSSProperties = {}
  const inner: CSSProperties = {}
  const mw = Number(block.maxWidth)
  if (Number.isFinite(mw) && mw > 0) {
    Object.assign(outer, { maxWidth: mw, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box' })
  }
  const mh = block.minHeight
  const tall = mh === 'screen' || (typeof mh === 'number' && mh > 0)
  if (tall) outer.minHeight = mh === 'screen' ? '100svh' : (mh as number)
  if (tall || (block.justify && block.justify !== 'start')) {
    Object.assign(outer, { display: 'flex', flexDirection: 'column' })
    Object.assign(inner, {
      flex: 1, display: 'flex', flexDirection: 'column',
      justifyContent: CONTAINER_JUSTIFY[block.justify || 'start'] || 'flex-start',
    })
  }
  return { outer, inner }
}

export function ContainerRenderer({ block, renderChildren }: { block: ContainerBlock; renderChildren?: RenderChildren }) {
  const s = useS()
  const kids = block.children
  const box = containerLayout(block)
  return (
    <section
      className={backgroundClass(block.background)}
      style={{
        ...backgroundStyle(block.background),
        padding: block.padding ?? 24,
        borderRadius: block.rounded ? 12 : 0,
        textAlign: block.align || 'left',
        color: block.textColor || 'inherit',
        whiteSpace: kids && kids.length && renderChildren ? 'normal' : 'pre-wrap',
        lineHeight: 1.55,
        ...box.outer,
      }}
    >
      <VideoBackdrop background={block.background} blockId={block.id} />
      {renderChildren && kids && kids.length ? (
        <div style={{ display: 'grid', gap: 'var(--pg-block-gap, 16px)', ...box.inner }}>
          {renderChildren(kids, block.id)}
        </div>
      ) : block.text ? s(block.text) : <span style={{ opacity: 0.6 }}>Container content…</span>}
    </section>
  )
}

// ─── Card / Card Grid ───────────────────────────────

/**
 * Render a card's price.
 *
 * Verbatim by default — an authored price is already written the way the
 * author wants it. A BOUND price is a bare number off the master, which is
 * why the format is declarable: `3200` has to reach the page as `₹3,200`,
 * grouped the way the currency's own locale groups.
 */
/** The "was" price and its saving, or null when there is none to show — a
 *  compare price that is not ABOVE the price would advertise a markup. */
function cardSavings(card: CardSpec, price: string, was: string): { was: string; pct: number } | null {
  if (!was) return null
  const num = (v: string) => Number(String(v).replace(/[^0-9.-]/g, ''))
  const p = num(price), w = num(was)
  if (isFinite(p) && isFinite(w) && price !== '' && w > 0) {
    if (w <= p) return null
    return { was: formatCardPrice(card, was), pct: Math.round((1 - p / w) * 100) }
  }
  return { was: formatCardPrice(card, was), pct: 0 }
}

function formatCardPrice(card: CardSpec, value: string): string {
  const mode = card.priceFormat || 'text'
  if (mode === 'text' || !value) return value
  const n = Number(String(value).replace(/[^0-9.-]/g, ''))
  if (!isFinite(n)) return value          // not a number after all: show it as written
  const locale = card.priceLocale || (card.priceCurrency === 'INR' ? 'en-IN' : undefined)
  try {
    return mode === 'currency'
      ? new Intl.NumberFormat(locale, {
          style: 'currency', currency: card.priceCurrency || 'USD',
          maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
        }).format(n)
      : new Intl.NumberFormat(locale).format(n)
  } catch {
    return value                           // unknown currency / locale
  }
}

/** The small state pill on a card — "In stock", "Sold out". */
function CardStatusPill({ status }: { status: CardStatus }) {
  const s = useS()
  const tone = status.tone || 'success'
  // Each tone maps to the theme's own semantic pair, so a pill is readable
  // on every preset and in a dark section without the author picking a hex.
  const TONES: Record<string, { fg: string; bg: string }> = {
    neutral: { fg: 'var(--pg-muted, var(--pg-text-secondary))', bg: 'var(--pg-surface-alt)' },
    success: { fg: 'var(--pg-success, #16a34a)', bg: 'var(--pg-success-bg, rgba(22,163,74,.10))' },
    warning: { fg: 'var(--pg-warning, #d97706)', bg: 'var(--pg-warning-bg, rgba(217,119,6,.10))' },
    error:   { fg: 'var(--pg-error, #dc2626)',   bg: 'var(--pg-error-bg, rgba(220,38,38,.10))' },
    info:    { fg: 'var(--pg-info, #2563eb)',    bg: 'var(--pg-info-bg, rgba(37,99,235,.10))' },
  }
  const { fg, bg } = TONES[tone] || TONES.success
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: themeRem(0.74), fontWeight: 600, color: fg,
      background: bg, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      <span aria-hidden style={{
        width: 6, height: 6, borderRadius: 999, background: fg, flex: '0 0 6px',
      }} />
      {s(status.label)}
    </span>
  )
}

/** Validated CSS aspect ratio ("1/1", "4 / 3", "1.5") or undefined. */
function cssAspect(v: string | undefined): string | undefined {
  const t = String(v || '').trim()
  return /^\d+(\.\d+)?(\s*\/\s*\d+(\.\d+)?)?$/.test(t) ? t : undefined
}

// The hover swap is CSS only — a published page has no React — and is
// keyed off the card (`pg-card`), so hovering anywhere on it shows the
// second picture, as a storefront does. `hover: hover` keeps a touch
// screen, where a tap is a click, on the first picture.
const CARD_HOVER_SWAP_CSS =
  '@media (hover:hover){.pg-card:hover .pg-card-media--swap .pg-card-img{opacity:0!important}'
  + '.pg-card:hover .pg-card-media--swap .pg-card-img-alt{opacity:1!important}}'

/**
 * A card's picture: the frame (a fixed height, or an aspect ratio that
 * scales with the card), the fit (crop to fill, or the whole picture), and
 * an optional second picture that cross-fades in on hover.
 */
function CardMedia({ card, look }: { card: CardSpec; look: CardStyle }) {
  const s = useS()
  const fit = card.imageFit || look.imageFit || 'cover'
  const aspect = cssAspect(card.imageAspect) || cssAspect(look.imageAspect)
  const hover = card.hoverImageUrl ? s(card.hoverImageUrl) : ''
  const layer: React.CSSProperties = {
    position: 'absolute', inset: 0, width: '100%', height: '100%',
    objectFit: fit, display: 'block', transition: 'opacity 0.35s ease',
  }
  return (
    <div
      className={hover ? 'pg-card-media pg-card-media--swap' : 'pg-card-media'}
      style={{
        position: 'relative', width: '100%', overflow: 'hidden', flex: '0 0 auto',
        ...(aspect ? { aspectRatio: aspect } : { height: look.imageHeight ?? 160 }),
        // A contained picture leaves margins; give them the card's surface
        // rather than whatever is behind the card.
        ...(fit === 'contain' ? { background: card.background || 'var(--pg-surface)' } : {}),
      }}
    >
      {/* `alt` defaults to the title: a card grid used to publish every
          image with alt="", which on a catalogue page is most of the
          images on the site. An author who means "decorative" sets "". */}
      <img
        className="pg-card-img"
        src={s(card.imageUrl) || card.imageUrl}
        alt={s(card.imageAlt ?? card.title) || ''}
        style={layer}
      />
      {hover && (
        <>
          <img className="pg-card-img-alt" src={hover} alt="" aria-hidden="true" loading="lazy"
               style={{ ...layer, opacity: 0 }} />
          <style dangerouslySetInnerHTML={{ __html: CARD_HOVER_SWAP_CSS }} />
        </>
      )}
    </div>
  )
}

/**
 * What a card's heart saves: the card's own add-to-cart line when it has
 * one (so the wishlist and the cart name one product the same way),
 * otherwise the card itself — `wishlistSku`, else its title, as the code.
 */
function cardWishItem(card: CardSpec, s: (t?: string) => string) {
  for (const b of [card.cta, card.secondaryCta]) {
    const c = b ? resolveClick(b) : null
    if (c && c.kind === 'add_to_cart') return cartItemOf(c, s)
  }
  const price = card.price ? s(String(card.price)) : ''
  const link = card.cta ? resolveClick(card.cta) : null
  return {
    sku: s(card.wishlistSku) || s(card.title) || card.id,
    name: s(card.title) || card.id,
    price: Number(price.replace(/[^0-9.]/g, '')) || 0,
    prefix: (price.match(/^[^0-9.]*/) || [''])[0].trim(),
    image: card.imageUrl ? s(card.imageUrl) : undefined,
    href: link && link.kind === 'external_url' && link.url ? s(link.url) : undefined,
    meta: card.subtitle ? s(card.subtitle) : undefined,
  }
}

function CardView({ card, look, fill }: { card: CardSpec; look?: CardStyle; fill?: boolean }) {
  const s = useS()
  const lk = look || {}
  const wish = card.wishlist ? cardWishItem(card, s) : null
  const pad = lk.padding ?? 16
  const center = card.align === 'center'
  const ink = card.textColor
  return (
    // `pg-item` marks this as ONE repeated element, so an item-scoped hover
    // (BlockStyle.hoverScope) lifts the card under the cursor rather than the
    // whole grid. See ITEM_BEARING_BLOCKS in the schema.
    <div
      className={`${ITEM_CLASS} pg-card`}
      // A `filter_bar` reads these: the facet values it groups by, the
      // numbers it sorts by, and the text it searches. Deriving them from
      // the card means a facet can never offer a value no card has, and
      // nothing has to be authored twice.
      //
      // SUBSTITUTED, like everything else the card shows. A card inside a
      // repeat can carry its row as `{{p.PRODUCT_NAME}}` instead of a
      // binding, and these used to take the raw text — so searching such a
      // grid matched the literal token on every card (i.e. never), the
      // facet offered one bogus value, and sort-by-price read `₹{{p` as no
      // number at all.
      data-pg-text={[card.title, card.subtitle, card.body, card.eyebrow, card.badge]
        .map((t) => s(t)).filter(Boolean).join(' ').toLowerCase() || undefined}
      data-pg-facet-eyebrow={s(card.eyebrow) || undefined}
      data-pg-facet-badge={s(card.badge) || undefined}
      data-pg-facet-status={s(card.status?.label) || undefined}
      data-pg-sort-price={card.price
        ? s(String(card.price)).replace(/[^0-9.]/g, '') || undefined
        : undefined}
      data-pg-sort-title={s(card.title) || undefined}
      style={{
        display: 'flex', flexDirection: 'column',
        background: card.background || 'var(--pg-surface)',
        // Only `border: false` removes the outline. A card's own background
        // used to drop it too, so pastel product cards on a white page had
        // no edge at all.
        border: lk.border === false ? 'none' : '1px solid var(--pg-border)',
        borderRadius: lk.radius ?? 12, overflow: 'hidden',
        position: wish ? 'relative' : undefined,
        ...(fill ? { flex: '1 1 auto', height: '100%', boxSizing: 'border-box' } : {}),
        boxShadow: lk.shadow ? SHADOWS[lk.shadow] : undefined,
        transition: 'transform 0.15s, box-shadow 0.15s',
        textAlign: center ? 'center' : undefined,
        ...(ink ? { color: ink, ['--pg-heading' as never]: ink, ['--pg-text' as never]: ink, ['--pg-muted' as never]: ink } : {}),
      }}
    >
      {card.imageUrl && <CardMedia card={card} look={lk} />}
      {wish && (
        <span style={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
          <WishlistHeart item={wish} />
        </span>
      )}
      <div style={{ padding: pad, display: 'flex', flexDirection: 'column', gap: 6, flex: 1, alignItems: center ? 'center' : undefined }}>
        {card.logoUrl && (
          // A logo/mark keeps its own aspect — never cropped like a cover.
          <img
            src={card.logoUrl}
            alt=""
            style={{ height: card.logoHeight ?? 48, width: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block', marginBottom: 6 }}
          />
        )}
        {card.badge && (
          <span style={{
            alignSelf: 'flex-start',
            fontSize: themeRem(0.65), fontWeight: 700, padding: '2px 8px',
            borderRadius: 999,
            // Tinted accent badge: derive a soft accent backdrop from the
            // theme's accent-bg token (filled by theme defaults), with
            // accent text on top — keeps the badge readable on every preset.
            background: 'var(--pg-info-bg, var(--pg-surface-alt))',
            color: 'var(--pg-accent)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{s(card.badge)}</span>
        )}
        {card.eyebrow && (
          <span className="pg-eyebrow" style={{ color: 'var(--pg-accent)' }}>{s(card.eyebrow)}</span>
        )}
        <h3 style={{ margin: 0, fontSize: lk.titleSize ? `${lk.titleSize}px` : themeRem(1.05), fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>{s(card.title)}</h3>
        {card.subtitle && (
          <div style={{ fontSize: themeRem(0.78), color: 'var(--pg-muted, var(--pg-text-secondary))', fontWeight: 500 }}>{s(card.subtitle)}</div>
        )}
        {card.body && (
          <p style={{ margin: '4px 0 0', fontSize: lk.bodySize ? `${lk.bodySize}px` : themeRem(0.88), color: 'var(--pg-text)', lineHeight: 1.55, flex: 1, whiteSpace: 'pre-line' }}>
            {s(card.body)}
          </p>
        )}
        {(card.price || card.status) && (
          // The footer row: the figure that decides, and the state it is
          // in. `marginTop: auto` pins it to the bottom so cards of
          // different body lengths line their prices up.
          <div style={{
            marginTop: 'auto', paddingTop: 10,
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            gap: 10, flexWrap: 'wrap',
          }}>
            {card.price && (
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                <span style={{
                  fontSize: themeRem(1.3), fontWeight: 800, letterSpacing: '-0.02em',
                  fontVariantNumeric: 'tabular-nums',
                  color: 'var(--pg-heading, var(--pg-text))',
                }}>{formatCardPrice(card, s(card.price))}</span>
                {(() => {
                  const sv = cardSavings(card, s(card.price), s(card.compareAtPrice))
                  if (!sv) return null
                  return (
                    <>
                      <s style={{ fontSize: themeRem(0.85), color: 'var(--pg-muted, var(--pg-text-secondary))',
                                  fontVariantNumeric: 'tabular-nums' }}
                         aria-label={`was ${sv.was}`}>{sv.was}</s>
                      {card.showSavings && sv.pct > 0 && (
                        <span style={{ fontSize: themeRem(0.78), fontWeight: 700, color: 'var(--pg-success, #15803d)' }}>
                          {sv.pct}% off
                        </span>
                      )}
                    </>
                  )
                })()}
                {card.priceNote && (
                  <span style={{ fontSize: themeRem(0.74), color: 'var(--pg-muted, var(--pg-text-secondary))' }}>
                    {s(card.priceNote)}
                  </span>
                )}
              </span>
            )}
            {card.status && <CardStatusPill status={card.status} />}
          </div>
        )}
        {(card.cta || card.secondaryCta) && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: center ? 'center' : undefined }}>
            {card.cta && <PageButton btn={card.cta} />}
            {card.secondaryCta && <PageButton btn={card.secondaryCta} />}
          </div>
        )}
      </div>
    </div>
  )
}

export function CardRenderer({ block }: { block: CardBlock }) {
  // A card repeated in a GRID fills its cell — equal heights, the price row
  // pinned along the bottom (CardView's `marginTop: auto`). The 360px cap is
  // for a standalone card, unless it opts out with `fill`.
  const inCell = useRepeatCell(block.id)
  const fill = inCell || !!block.fill
  return (
    <div style={fill
      ? { width: '100%', height: inCell ? '100%' : undefined, display: 'flex', flexDirection: 'column' }
      : { maxWidth: 360 }}
    >
      <CardView card={block.card} look={block.cardStyle} fill={fill} />
    </div>
  )
}

export function CardGridRenderer({ block }: { block: CardGridBlock }) {
  const gap = block.gap ?? 16
  return (
    // `data-pg-block` is how a `filter_bar` finds the grid it filters.
    // Emitted here rather than left to the generic wrapper, because that
    // wrapper is elided when a block needs no styling of its own — so a
    // plain grid, which is exactly the common case, would have no handle.
    <div
      data-pg-block={block.id}
      style={{
        display: 'grid',
        gridTemplateColumns: responsiveGrid(block.columns, 240, gap),
        gap,
      }}
    >
      {(block.cards || []).map((c) => <CardView key={c.id} card={c} look={block.cardStyle} />)}
    </div>
  )
}

// ─── Feature grid ────────────────────────────────────

export function FeatureGridRenderer({ block }: { block: FeatureGridBlock }) {
  const s = useS()
  const align = block.align || 'left'
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {(block.eyebrow || block.heading || block.subheading) && (
        <header style={{ textAlign: align, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {block.eyebrow && (
            <span className="pg-eyebrow">{s(block.eyebrow)}</span>
          )}
          {block.heading && (
            <h2 style={{ margin: 0, fontSize: themeRem(1.7), fontWeight: 800, color: 'var(--pg-text)' }}>
              {s(block.heading)}
            </h2>
          )}
          {block.subheading && (
            <p style={{ margin: 0, fontSize: themeRem(0.95), color: 'var(--pg-text-secondary)', maxWidth: 640, alignSelf: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start' }}>
              {s(block.subheading)}
            </p>
          )}
        </header>
      )}
      <div style={{
        display: 'grid', gridTemplateColumns: responsiveGrid(block.columns, 220, 20),
        gap: 20,
      }}>
        {(block.items || []).map((it) => (
          <div key={it.id} className={ITEM_CLASS} style={{
            display: 'flex', flexDirection: 'column', gap: 8,
            textAlign: align,
          }}>
            <div style={{
              width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: themeRem(1.4),
              // Soft accent tile that adapts to the theme. `--pg-info-bg`
              // is a theme-derived tinted surface; falls back to the
              // surface-alt step in the surface stack.
              background: block.iconStyle === 'plain' ? 'transparent' : 'var(--pg-info-bg, var(--pg-surface-alt))',
              color: 'var(--pg-accent)',
              borderRadius: block.iconStyle === 'square' ? 8 : block.iconStyle === 'circle' ? 999 : 0,
              alignSelf: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
            }}>
              {/* `PageIcon`, not `resolveIcon`: every other icon surface was
                  moved to the vector set, and this one kept rendering the
                  legacy emoji table — so a feature grid authored with the
                  documented names ('shield', 'truck') came out as 🛡 or, for
                  a name the small emoji table never had, as nothing at all.
                  PageIcon still passes a short literal through, so an item
                  whose "icon" is really a label ('410') is unaffected. */}
              {it.icon ? <PageIcon name={it.icon} size={22} color="var(--pg-accent)" /> : null}
            </div>
            <h3 style={{ margin: 0, fontSize: themeRem(1), fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>{s(it.title)}</h3>
            {it.description && (
              <p style={{ margin: 0, fontSize: themeRem(0.85), color: 'var(--pg-text)', lineHeight: 1.55 }}>
                {s(it.description)}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── Stats ───────────────────────────────────────────

export function StatsRenderer({ block }: { block: StatsBlock }) {
  const s = useS()
  return (
    <section style={{
      ...backgroundStyle(block.background),
      borderRadius: bgHasFill(block.background) ? 12 : 0,
      padding: bgHasFill(block.background) ? '28px 24px' : 0,
      display: 'flex', flexDirection: 'column', gap: 16,
    }}>
      {block.heading && (
        <h2 style={{ margin: 0, textAlign: block.align || 'center', fontSize: themeRem(1.4), fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>
          {s(block.heading)}
        </h2>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: responsiveGrid(block.columns, 140, 16),
        gap: 16,
      }}>
        {(block.items || []).map((it) => (
          <div key={it.id} className={ITEM_CLASS} style={{ textAlign: block.align || 'center', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: themeRem(2.2), fontWeight: 800, color: 'var(--pg-primary)', lineHeight: 1.1 }}>
              {block.countUp
                ? <CountValue value={s(it.value)} duration={block.countUpDuration} />
                : s(it.value)}
              {it.suffix && <span style={{ fontSize: themeRem(1.4), opacity: 0.85 }}>{s(it.suffix)}</span>}
            </div>
            <div style={{ fontSize: themeRem(0.85), fontWeight: 600, color: 'var(--pg-text)' }}>{s(it.label)}</div>
            {it.description && (
              <div style={{ fontSize: themeRem(0.75), color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{s(it.description)}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── Countdown ───────────────────────────────────────
// A live countdown to `block.target` (absolute ISO datetime). The numbers
// recompute every second against the viewer's clock. The presentational
// `CountdownView` is shared with the SSR renderer (renderSite.tsx) so a
// published page looks identical; only the ticking lives here.

export interface CountdownParts {
  days: number
  hours: number
  minutes: number
  seconds: number
  expired: boolean
}

const DEFAULT_COUNTDOWN_LABELS: Required<CountdownLabels> = {
  days: 'Days', hours: 'Hours', minutes: 'Minutes', seconds: 'Seconds',
}

/** Parse a target into epoch ms. Returns NaN for missing/invalid input. */
export function parseCountdownTarget(target?: string): number {
  if (!target) return NaN
  const ms = new Date(target).getTime()
  return Number.isNaN(ms) ? NaN : ms
}

/** Split the remaining time between `nowMs` and `targetMs` into units. */
export function countdownParts(targetMs: number, nowMs: number): CountdownParts {
  const expired = targetMs - nowMs <= 0
  let rem = Math.max(0, Math.floor((targetMs - nowMs) / 1000))
  const days = Math.floor(rem / 86400); rem -= days * 86400
  const hours = Math.floor(rem / 3600); rem -= hours * 3600
  const minutes = Math.floor(rem / 60); rem -= minutes * 60
  return { days, hours, minutes, seconds: rem, expired }
}

const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`)

// Presentational only — no hooks, no clock. Both the live renderer below
// and the SSR ServerCountdown feed it pre-computed parts. The per-unit
// value spans carry stable class names (`pg-cd-days`, …) so the deployed
// static page's tick script can update them in place.
export function CountdownView({
  heading, align = 'center', columns = 4, background, parts, labels, showSeconds = true,
  expiredText, rootProps,
}: {
  heading?: string
  align?: Align
  columns?: number
  background?: Background
  parts: CountdownParts
  labels?: CountdownLabels
  showSeconds?: boolean
  expiredText?: string
  rootProps?: React.HTMLAttributes<HTMLDivElement> & Record<string, unknown>
}) {
  const lab = { ...DEFAULT_COUNTDOWN_LABELS, ...(labels || {}) }
  const units: Array<{ key: string; cls: string; value: string; label: string }> = [
    { key: 'days', cls: 'pg-cd-days', value: String(parts.days), label: lab.days },
    { key: 'hours', cls: 'pg-cd-hours', value: pad2(parts.hours), label: lab.hours },
    { key: 'minutes', cls: 'pg-cd-minutes', value: pad2(parts.minutes), label: lab.minutes },
  ]
  if (showSeconds) units.push({ key: 'seconds', cls: 'pg-cd-seconds', value: pad2(parts.seconds), label: lab.seconds })
  const hasBg = bgHasFill(background)
  return (
    <section
      {...rootProps}
      className={rootProps?.className}
      style={{
        ...backgroundStyle(background),
        borderRadius: hasBg ? 12 : 0,
        padding: hasBg ? '28px 24px' : 0,
        display: 'flex', flexDirection: 'column', gap: 16,
      }}
    >
      {heading && (
        <h2 style={{ margin: 0, textAlign: align, fontSize: themeRem(1.4), fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>
          {heading}
        </h2>
      )}
      {parts.expired && expiredText ? (
        <div className="pg-cd-expired" style={{ textAlign: align, fontSize: themeRem(1.6), fontWeight: 800, color: 'var(--pg-primary)' }}>
          {expiredText}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: responsiveGrid(showSeconds ? (columns || 4) : Math.min(columns || 3, 3), 100, 16),
          gap: 16,
        }}>
          {units.map((u) => (
            <div key={u.key} className={ITEM_CLASS} style={{ textAlign: align, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div className={`pg-cd-val ${u.cls}`} style={{ fontSize: themeRem(2.6), fontWeight: 800, color: 'var(--pg-primary)', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                {u.value}
              </div>
              <div style={{ fontSize: themeRem(0.85), fontWeight: 600, color: 'var(--pg-text)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{u.label}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

export function CountdownRenderer({ block }: { block: CountdownBlock }) {
  const s = useS()
  const targetMs = parseCountdownTarget(s(block.target))
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (Number.isNaN(targetMs)) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [targetMs])

  // Invalid/missing target: degrade to a static zeroed countdown rather
  // than crashing the page render.
  const parts = Number.isNaN(targetMs)
    ? { days: 0, hours: 0, minutes: 0, seconds: 0, expired: false }
    : countdownParts(targetMs, now)

  return (
    <CountdownView
      heading={s(block.heading)}
      align={block.align || 'center'}
      columns={block.columns}
      background={block.background}
      parts={parts}
      labels={block.labels}
      showSeconds={block.showSeconds !== false}
      expiredText={s(block.expiredText)}
    />
  )
}

// ─── Timeline ────────────────────────────────────────

export function TimelineRenderer({ block }: { block: TimelineBlock }) {
  const s = useS()
  const headerAlign = block.align || 'left'
  const layout = block.layout || 'vertical'
  const markerStyle = block.markerStyle || 'dot'
  const accent = block.accentColor || 'var(--pg-primary)'
  const items = block.items || []
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {(block.eyebrow || block.heading || block.subheading) && (
        <header style={{ textAlign: headerAlign, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {block.eyebrow && (
            <span className="pg-eyebrow" style={{ color: accent }}>{s(block.eyebrow)}</span>
          )}
          {block.heading && (
            <h2 style={{ margin: 0, fontSize: themeRem(1.7), fontWeight: 800, color: 'var(--pg-heading, var(--pg-text))' }}>
              {s(block.heading)}
            </h2>
          )}
          {block.subheading && (
            <p style={{ margin: 0, fontSize: themeRem(0.95), color: 'var(--pg-text-secondary)', maxWidth: 640, alignSelf: headerAlign === 'center' ? 'center' : headerAlign === 'right' ? 'flex-end' : 'flex-start' }}>
              {s(block.subheading)}
            </p>
          )}
        </header>
      )}
      <ol
        className={`pg-timeline pg-timeline-${layout}`}
        style={{
          listStyle: 'none', margin: 0, padding: 0,
          ['--pg-tl-accent' as any]: accent,
          position: 'relative',
        }}
      >
        {items.map((it, i) => (
          <TimelineItemRow
            key={it.id}
            item={it}
            index={i}
            layout={layout}
            markerStyle={markerStyle}
            substitute={s}
          />
        ))}
      </ol>
      <style>{TIMELINE_CSS}</style>
    </section>
  )
}

function TimelineItemRow({ item, index, layout, markerStyle, substitute }: {
  item: TimelineBlock['items'][number]
  index: number
  layout: NonNullable<TimelineBlock['layout']>
  markerStyle: NonNullable<TimelineBlock['markerStyle']>
  substitute: (s?: string) => string
}) {
  const itemAccent = item.accent || 'var(--pg-tl-accent)'
  const markerContent =
    markerStyle === 'icon' ? (item.icon || '•') :
    markerStyle === 'number' ? String(index + 1) :
    ''
  const side = layout === 'alternating' ? (index % 2 === 0 ? 'left' : 'right') : 'right'
  return (
    <li className={`pg-tl-row pg-tl-side-${side} ${ITEM_CLASS}`}>
      <div className="pg-tl-marker" style={{ background: itemAccent, color: 'var(--pg-on-primary, #fff)' }}>
        {markerStyle !== 'dot' && <span className="pg-tl-marker-text">{markerContent}</span>}
      </div>
      <div className="pg-tl-card">
        {item.date && <span className="pg-tl-date" style={{ color: itemAccent }}>{substitute(item.date)}</span>}
        <div className="pg-tl-title-row">
          <h3 className="pg-tl-title">{substitute(item.title)}</h3>
          {item.badge && <span className="pg-tl-badge">{substitute(item.badge)}</span>}
        </div>
        {item.description && <p className="pg-tl-desc">{substitute(item.description)}</p>}
      </div>
    </li>
  )
}

const TIMELINE_CSS = `
.pg-timeline { position: relative; }
.pg-timeline::before {
  content: ''; position: absolute; top: 6px; bottom: 6px; width: 2px;
  background: linear-gradient(180deg,
    color-mix(in srgb, var(--pg-tl-accent) 70%, transparent) 0%,
    color-mix(in srgb, var(--pg-tl-accent) 25%, transparent) 100%);
  border-radius: 2px;
}
.pg-timeline-vertical::before { left: 17px; }
.pg-timeline-alternating::before { left: 50%; transform: translateX(-50%); }

.pg-tl-row { position: relative; display: grid; gap: 12px; padding: 4px 0 24px; }
.pg-tl-row:last-child { padding-bottom: 0; }

.pg-timeline-vertical .pg-tl-row { grid-template-columns: 36px 1fr; align-items: start; }
.pg-timeline-alternating .pg-tl-row {
  grid-template-columns: 1fr 36px 1fr;
  align-items: start;
}
.pg-timeline-alternating .pg-tl-side-left .pg-tl-card { grid-column: 1; text-align: right; }
.pg-timeline-alternating .pg-tl-side-left .pg-tl-marker { grid-column: 2; }
.pg-timeline-alternating .pg-tl-side-right .pg-tl-marker { grid-column: 2; }
.pg-timeline-alternating .pg-tl-side-right .pg-tl-card { grid-column: 3; }
.pg-timeline-alternating .pg-tl-side-left .pg-tl-title-row { justify-content: flex-end; }

.pg-tl-marker {
  width: 36px; height: 36px; border-radius: 999px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.05rem; font-weight: 700;
  box-shadow:
    0 0 0 4px var(--pg-surface, #fff),
    0 4px 12px color-mix(in srgb, var(--pg-tl-accent) 30%, transparent);
  position: relative; z-index: 1;
  transition: transform 0.18s ease;
}
.pg-tl-row:hover .pg-tl-marker { transform: scale(1.06); }
.pg-tl-marker-text { line-height: 1; }

.pg-tl-card {
  display: flex; flex-direction: column; gap: 6px;
  padding: 12px 14px;
  background: var(--pg-surface);
  border: 1px solid var(--pg-border);
  border-radius: 10px;
  box-shadow: 0 1px 2px color-mix(in srgb, var(--pg-text) 6%, transparent);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}
.pg-tl-row:hover .pg-tl-card {
  border-color: color-mix(in srgb, var(--pg-tl-accent) 50%, var(--pg-border));
  box-shadow: 0 6px 18px color-mix(in srgb, var(--pg-tl-accent) 14%, transparent);
  transform: translateY(-1px);
}

.pg-tl-date {
  font-size: 0.7rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
}
.pg-tl-title-row {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.pg-tl-title {
  margin: 0; font-size: 1.05rem; font-weight: 700;
  color: var(--pg-heading, var(--pg-text));
}
.pg-tl-badge {
  font-size: 0.68rem; font-weight: 700;
  padding: 2px 8px; border-radius: 999px;
  background: color-mix(in srgb, var(--pg-tl-accent) 14%, transparent);
  color: var(--pg-tl-accent);
  text-transform: uppercase; letter-spacing: 0.05em;
}
.pg-tl-desc {
  margin: 0; font-size: 0.9rem; line-height: 1.55;
  color: var(--pg-text-secondary);
}

@media (max-width: 720px) {
  .pg-timeline-alternating::before { left: 17px; transform: none; }
  .pg-timeline-alternating .pg-tl-row { grid-template-columns: 36px 1fr; }
  .pg-timeline-alternating .pg-tl-side-left .pg-tl-card,
  .pg-timeline-alternating .pg-tl-side-right .pg-tl-card { grid-column: 2; text-align: left; }
  .pg-timeline-alternating .pg-tl-side-left .pg-tl-marker,
  .pg-timeline-alternating .pg-tl-side-right .pg-tl-marker { grid-column: 1; }
  .pg-timeline-alternating .pg-tl-side-left .pg-tl-title-row { justify-content: flex-start; }
}
`

// ─── Testimonial ─────────────────────────────────────

/** One quote card — the unit every testimonial mode composes. */
function TestimonialCard({ item, align }: { item: TestimonialItem; align: Align }) {
  const s = useS()
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  return (
    <figure className={ITEM_CLASS} style={{
      margin: 0, padding: 28,
      background: 'var(--pg-surface)',
      border: '1px solid var(--pg-border)',
      borderRadius: 12,
      display: 'flex', flexDirection: 'column', gap: 14,
      alignItems: justify, textAlign: align,
      height: '100%', boxSizing: 'border-box',
    }}>
      {item.rating !== undefined && item.rating > 0 && (
        <div style={{ display: 'flex', gap: 2, color: '#fbbf24' }} aria-label={`${item.rating} out of 5`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} size={16} fill={i < (item.rating || 0) ? '#fbbf24' : 'transparent'} />
          ))}
        </div>
      )}
      <blockquote style={{
        margin: 0, fontSize: themeRem(1.05), lineHeight: 1.6,
        color: 'var(--pg-text)', fontStyle: 'italic',
        maxWidth: 720, flex: 1,
      }}>“{s(item.quote)}”</blockquote>
      <figcaption style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
        {item.authorAvatarUrl ? (
          <img
            src={item.authorAvatarUrl} alt={item.authorName}
            {...imgLoadingProps(false)}
            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, flexShrink: 0,
          }}>
            {(item.authorName || '?').slice(0, 1).toUpperCase()}
          </div>
        )}
        <div style={{ textAlign: 'left', minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: themeRem(0.9), color: 'var(--pg-heading, var(--pg-text))' }}>{s(item.authorName)}</div>
          {item.authorRole && (
            <div style={{ fontSize: themeRem(0.75), color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{s(item.authorRole)}</div>
          )}
        </div>
        {item.companyLogoUrl && (
          <img src={item.companyLogoUrl} alt="" {...imgLoadingProps(false)} style={{ marginLeft: 'auto', height: 24, opacity: 0.75 }} />
        )}
      </figcaption>
    </figure>
  )
}

/**
 * Testimonials in one of three shapes.
 *
 * `items` is the multi-quote field; when it is absent the block falls back to
 * the legacy single-quote fields, so every page saved before multi-item
 * support renders exactly as it did.
 */
export function TestimonialRenderer({ block }: { block: TestimonialBlock }) {
  const align = block.align || 'center'
  const items: TestimonialItem[] = block.items?.length
    ? block.items
    : [{
        id: block.id, quote: block.quote, authorName: block.authorName,
        authorRole: block.authorRole, authorAvatarUrl: block.authorAvatarUrl,
        companyLogoUrl: block.companyLogoUrl, rating: block.rating,
      }]

  const mode = block.mode || (items.length > 1 ? 'wall' : 'single')

  if (mode === 'wall' && items.length > 1) {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: responsiveGrid(block.columns || 3, 280, 16),
        gap: 16, alignItems: 'stretch',
      }}>
        {items.map((it) => <TestimonialCard key={it.id} item={it} align={align} />)}
      </div>
    )
  }

  if (mode === 'carousel' && items.length > 1) {
    return (
      <SlideCarousel
        id={block.id}
        autoplay={block.autoplay !== false}
        interval={block.autoplayInterval ?? 6000}
        count={items.length}
      >
        {items.map((it) => <TestimonialCard key={it.id} item={it} align={align} />)}
      </SlideCarousel>
    )
  }

  return <TestimonialCard item={items[0]} align={align} />
}

/**
 * Shared slide carousel shell.
 *
 * One implementation behind the standalone `carousel` block and the
 * testimonial slider. The interactive surfaces drive it with the hook state
 * below; the static site drives the SAME DOM with `carouselScript`, matched
 * by the stable `pg-car-slide` / `pg-car-dot` + `is-active` class names —
 * the pattern the hero carousel established.
 */
export function SlideCarousel({ id, autoplay, interval, count, children, minHeight, showDots = true, showArrows = true }: {
  id: string
  autoplay: boolean
  interval: number
  count: number
  children: ReactNode[]
  minHeight?: number
  // Either control can be switched off by the author. Leaving them out keeps
  // both, which is what every caller got before these existed. The static
  // `carouselScript` looks each control up and skips a missing one, so an
  // omitted button needs no script change.
  showDots?: boolean
  showArrows?: boolean
}) {
  const [idx, setIdx] = useState(0)
  const many = count > 1
  const ms = Math.max(1500, interval)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (!autoplay || !many || paused) return
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    const t = setInterval(() => setIdx((i) => (i + 1) % count), ms)
    return () => clearInterval(t)
  }, [autoplay, many, ms, count, paused])

  const go = (e: React.MouseEvent, i: number) => {
    e.preventDefault(); e.stopPropagation()
    setIdx(((i % count) + count) % count)
  }

  return (
    <div
      className="pg-car"
      data-pg-carousel=""
      data-autoplay={autoplay && many ? ms : 0}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{ minHeight, position: 'relative' }}
    >
      <div className="pg-car-track" style={{ position: 'relative' }}>
        {children.map((child, i) => (
          <div key={i} className={`pg-car-slide ${ITEM_CLASS}${i === idx ? ' is-active' : ''}`}>{child}</div>
        ))}
      </div>
      {many && showArrows && (
        <>
          <button type="button" className="pg-car-arrow" data-arrow="prev" aria-label="Previous" onClick={(e) => go(e, idx - 1)}>‹</button>
          <button type="button" className="pg-car-arrow" data-arrow="next" aria-label="Next" onClick={(e) => go(e, idx + 1)}>›</button>
        </>
      )}
      {many && showDots && (
        <div className="pg-car-dots">
          {children.map((_, i) => (
            <button
              key={i} type="button"
              className={`pg-car-dot${i === idx ? ' is-active' : ''}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={(e) => go(e, i)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Pricing ─────────────────────────────────────────

/**
 * Pricing tiers, optionally with a monthly/yearly period switch.
 *
 * BOTH prices are rendered into the DOM (one hidden by CSS) and the switch
 * is a checkbox whose `:checked` state flips a data attribute on the root.
 * That means the toggle works on the static site with no JS, the page is
 * complete for a crawler, and the switch is instant — no re-render, no
 * flash of the wrong price.
 */
export function PricingRenderer({ block }: { block: PricingBlock }) {
  const s = useS()
  const toggle = !!block.showPeriodToggle && block.tiers.some((t) => !!t.altPrice)
  const switchId = `pg-price-${block.id}`

  const priceFor = (t: PricingBlock['tiers'][number]) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
      <span className={toggle && t.altPrice ? 'pg-price-main' : undefined} style={PRICE_STYLE}>{s(t.price)}</span>
      {toggle && t.altPrice ? (
        <span className="pg-price-alt" style={PRICE_STYLE}>{s(t.altPrice)}</span>
      ) : null}
      {t.period && (
        <span className={toggle && t.altPeriod ? 'pg-price-main' : undefined} style={PERIOD_STYLE}>{s(t.period)}</span>
      )}
      {toggle && t.altPeriod ? <span className="pg-price-alt" style={PERIOD_STYLE}>{s(t.altPeriod)}</span> : null}
    </div>
  )

  return (
    <section
      className="pg-pricing"
      data-alt={toggle && block.defaultPeriod === 'alt' ? '' : undefined}
      style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
    >
      {(block.heading || block.subheading) && (
        <header style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {block.heading && (
            <h2 style={{ margin: 0, fontSize: themeRem(1.7), fontWeight: 800, color: 'var(--pg-heading, var(--pg-text))' }}>
              {s(block.heading)}
            </h2>
          )}
          {block.subheading && (
            <p style={{ margin: 0, fontSize: themeRem(0.95), color: 'var(--pg-muted, var(--pg-text-secondary))' }}>
              {s(block.subheading)}
            </p>
          )}
        </header>
      )}
      {toggle && (
        <div className="pg-price-switch">
          <label htmlFor={switchId}>{s(block.periodLabel) || 'Monthly'}</label>
          <input
            id={switchId}
            className="pg-price-toggle"
            type="checkbox"
            defaultChecked={block.defaultPeriod === 'alt'}
            aria-label={`${s(block.periodLabel) || 'Monthly'} / ${s(block.altPeriodLabel) || 'Yearly'}`}
          />
          <label className="pg-price-track" htmlFor={switchId} aria-hidden />
          <label htmlFor={switchId}>
            {s(block.altPeriodLabel) || 'Yearly'}
            {block.altPeriodBadge ? (
              <span style={{
                marginLeft: 6, fontSize: themeRem(0.65), fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                background: 'var(--pg-success-bg)', color: 'var(--pg-success)',
              }}>{s(block.altPeriodBadge)}</span>
            ) : null}
          </label>
        </div>
      )}
      <div style={{
        display: 'grid', gridTemplateColumns: responsiveGrid(block.columns, 240, 16),
        gap: 16, alignItems: 'stretch',
      }}>
        {(block.tiers || []).map((t) => (
          <div key={t.id} className={ITEM_CLASS} style={{
            display: 'flex', flexDirection: 'column', gap: 12,
            padding: 22,
            border: t.highlighted ? '2px solid var(--pg-primary)' : '1px solid var(--pg-border)',
            borderRadius: 12,
            // Highlighted tier uses the theme's `surface-alt` step so the
            // emphasis still reads cleanly on dark themes (where a 6%
            // accent rgba would barely register against the dark surface).
            background: t.highlighted ? 'var(--pg-surface-alt, var(--pg-surface))' : 'var(--pg-surface)',
            position: 'relative',
            transform: t.highlighted ? 'translateY(-4px)' : 'none',
            boxShadow: t.highlighted ? '0 8px 24px rgba(15,23,42,0.10)' : 'none',
          }}>
            {t.badge && (
              <span style={{
                position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                padding: '3px 10px', fontSize: themeRem(0.65), fontWeight: 700,
                background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)',
                borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>{s(t.badge)}</span>
            )}
            <div style={{ fontSize: themeRem(0.95), fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>{s(t.name)}</div>
            {priceFor(t)}
            {t.description && (
              <p style={{ margin: 0, fontSize: themeRem(0.82), color: 'var(--pg-muted, var(--pg-text-secondary))', lineHeight: 1.55 }}>
                {s(t.description)}
              </p>
            )}
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              {(t.features || []).map((f, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: themeRem(0.85), color: 'var(--pg-text)' }}>
                  <Check size={16} style={{ marginTop: 2, color: 'var(--pg-primary)', flexShrink: 0 }} />
                  <span>{s(f)}</span>
                </li>
              ))}
            </ul>
            {t.cta && <div style={{ marginTop: 'auto' }}><PageButton btn={t.cta} /></div>}
          </div>
        ))}
      </div>
    </section>
  )
}

const PRICE_STYLE: CSSProperties = { fontSize: themeRem(2.2), fontWeight: 800, color: 'var(--pg-heading, var(--pg-text))' }
const PERIOD_STYLE: CSSProperties = { fontSize: themeRem(0.85), color: 'var(--pg-muted, var(--pg-text-secondary))' }

// ─── FAQ ─────────────────────────────────────────────

export function FaqRenderer({ block }: { block: FaqBlock }) {
  const s = useS()
  const [openId, setOpenId] = useState<string | null>(null)
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {block.heading && (
        <h2 style={{ margin: 0, fontSize: themeRem(1.5), fontWeight: 800, color: 'var(--pg-heading, var(--pg-text))' }}>
          {s(block.heading)}
        </h2>
      )}
      <div style={{
        border: '1px solid var(--pg-border)', borderRadius: 12, overflow: 'hidden',
        background: 'var(--pg-surface)',
      }}>
        {(block.items || []).map((it, i) => {
          const open = openId === it.id
          return (
            <div key={it.id} className={ITEM_CLASS} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--pg-border)' }}>
              <button
                onClick={(e) => { e.stopPropagation(); setOpenId(open ? null : it.id) }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 18px', background: 'transparent', border: 'none', cursor: 'pointer',
                  textAlign: 'left', fontWeight: 600, fontSize: themeRem(0.95), color: 'var(--pg-heading, var(--pg-text))',
                }}
              >
                <span>{s(it.question)}</span>
                <ChevronDown size={18} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', color: 'var(--pg-muted, var(--pg-text-secondary))' }} />
              </button>
              {open && (
                <div style={{
                  padding: '0 18px 16px', fontSize: themeRem(0.88),
                  color: 'var(--pg-text)', lineHeight: 1.6, whiteSpace: 'pre-wrap',
                }}>
                  {s(it.answer)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ─── Tabs ────────────────────────────────────────────

export function TabsRenderer({ block, renderChildren }: { block: TabsBlock; renderChildren?: RenderChildren }) {
  const s = useS()
  const [activeId, setActiveId] = useState<string>(block.items[0]?.id || '')
  const active = block.items.find((i) => i.id === activeId) || block.items[0]
  const variant = block.variant || 'underline'
  const overflow = block.overflow || 'wrap'

  return (
    <div>
      <div style={{
        display: 'flex', gap: variant === 'pill' ? 4 : 0,
        flexWrap: overflow === 'wrap' ? 'wrap' : 'nowrap',
        overflowX: overflow === 'scroll' ? 'auto' : 'visible',
        borderBottom: variant === 'underline' ? '1px solid var(--pg-border)' : 'none',
        background: variant === 'pill' ? 'var(--pg-surface)' : 'transparent',
        padding: variant === 'pill' ? 4 : 0,
        borderRadius: variant === 'pill' ? 8 : 0,
        width: variant === 'pill' && overflow === 'wrap' ? 'fit-content' : '100%',
        maxWidth: '100%',
      }}>
        {(block.items || []).map((it) => {
          const isActive = it.id === active?.id
          return (
            <button
              key={it.id}
              className={ITEM_CLASS}
              onClick={(e) => { e.stopPropagation(); setActiveId(it.id) }}
              style={{
                padding: variant === 'pill' ? '6px 14px' : '10px 14px',
                background: variant === 'pill'
                  ? (isActive ? 'var(--pg-primary)' : 'transparent')
                  : 'transparent',
                color: variant === 'pill'
                  ? (isActive ? 'var(--pg-on-primary, #fff)' : 'var(--pg-muted, var(--pg-text-secondary))')
                  : (isActive ? 'var(--pg-primary)' : 'var(--pg-muted, var(--pg-text-secondary))'),
                fontWeight: isActive ? 700 : 500,
                border: 'none',
                borderRadius: variant === 'pill' ? 6 : 0,
                borderBottom: variant === 'underline'
                  ? (isActive ? '2px solid var(--pg-primary)' : '2px solid transparent')
                  : 'none',
                cursor: 'pointer', fontSize: themeRem(0.88),
                transition: 'all 0.15s',
                flexShrink: 0, whiteSpace: overflow === 'scroll' ? 'nowrap' : 'normal',
              }}
            >
              {s(it.label)}
            </button>
          )
        })}
      </div>
      <div style={{
        padding: '16px 4px', fontSize: themeRem(0.92),
        color: 'var(--pg-text)', lineHeight: 1.6,
        whiteSpace: renderChildren && active?.blocks?.length ? 'normal' : 'pre-wrap',
      }}>
        {renderChildren && active?.blocks?.length ? (
          <div style={{ display: 'grid', gap: 'var(--pg-block-gap, 16px)' }}>
            {renderChildren(active.blocks, active.id)}
          </div>
        ) : s(active?.body)}
      </div>
    </div>
  )
}

// ─── Accordion ───────────────────────────────────────

export function AccordionRenderer({ block, renderChildren }: { block: AccordionBlock; renderChildren?: RenderChildren }) {
  const s = useS()
  const [openIds, setOpenIds] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    block.items.forEach((it) => { if (it.defaultOpen) init[it.id] = true })
    return init
  })
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {(block.items || []).map((it) => {
        const open = openIds[it.id]
        return (
          <div key={it.id} className={ITEM_CLASS} style={{
            border: '1px solid var(--pg-border)', borderRadius: 8,
            background: 'var(--pg-surface)', overflow: 'hidden',
          }}>
            <button
              onClick={(e) => { e.stopPropagation(); setOpenIds((p) => ({ ...p, [it.id]: !p[it.id] })) }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
                textAlign: 'left', fontWeight: 600, fontSize: themeRem(0.92), color: 'var(--pg-heading, var(--pg-text))',
              }}
            >
              <span>{s(it.title)}</span>
              <ChevronDown size={16} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', color: 'var(--pg-muted, var(--pg-text-secondary))' }} />
            </button>
            {open && (
              <div style={{
                padding: '0 16px 14px', fontSize: themeRem(0.86),
                color: 'var(--pg-text)', lineHeight: 1.55,
                whiteSpace: renderChildren && it.blocks?.length ? 'normal' : 'pre-wrap',
              }}>
                {renderChildren && it.blocks?.length ? (
                  <div style={{ display: 'grid', gap: 'var(--pg-block-gap, 16px)' }}>
                    {renderChildren(it.blocks, it.id)}
                  </div>
                ) : s(it.body)}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Logo cloud ──────────────────────────────────────

/**
 * Logo cloud.
 *
 * `mode: 'marquee'` turns the static row into a continuously scrolling
 * ticker — pure CSS keyframes, so it runs on the published static site.
 * `marqueeDuplicate` clones the items once at runtime so the -50% translate
 * loops seamlessly; the clone is `aria-hidden` so the duplication is
 * invisible to crawlers and screen readers. Under `prefers-reduced-motion`
 * the CSS hides the clone and lets the row wrap normally, so the logos are
 * still all there, just still.
 */
export function LogoCloudRenderer({ block }: { block: LogoCloudBlock }) {
  const s = useS()
  const marquee = block.mode === 'marquee'
  const h = block.logoHeight ?? 32

  const logos = (block.items || []).map((lg) => (
    lg.url ? (
      <img
        key={lg.id} src={lg.url} alt={lg.name || ''}
        className={ITEM_CLASS}
        {...imgLoadingProps(false)}
        style={{
          height: h, width: 'auto', opacity: 0.8,
          filter: block.monochrome ? 'grayscale(100%)' : 'none',
        }}
      />
    ) : (
      <div key={lg.id} className={ITEM_CLASS} style={{
        padding: '6px 14px', borderRadius: 6,
        background: 'var(--pg-surface)',
        border: '1px dashed var(--pg-border)',
        fontSize: themeRem(0.85), fontWeight: 600,
        color: 'var(--pg-text-secondary)',
      }}>{lg.name || 'Logo'}</div>
    )
  ))

  return (
    <section style={{
      ...backgroundStyle(block.background),
      borderRadius: bgHasFill(block.background) ? 12 : 0,
      padding: bgHasFill(block.background) ? '24px' : '8px 0',
      display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center',
    }}>
      {block.title && (
        <p className="pg-eyebrow" style={{ margin: 0, color: 'var(--pg-text-secondary)' }}>
          {s(block.title)}
        </p>
      )}
      {marquee ? (
        <div
          className={[
            'pg-marquee',
            block.marqueeDirection === 'right' ? 'pg-marquee-rev' : '',
            'pg-marquee-pause',
            'pg-marquee-fade',
          ].filter(Boolean).join(' ')}
          style={{ ['--pg-mq-speed' as never]: `${block.marqueeSpeed ?? 30}s` }}
        >
          <div className="pg-marquee-track" style={{ ['--pg-mq-gap' as never]: '48px' }}>
            {logos}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'center', justifyContent: 'center' }}>
          {logos}
        </div>
      )}
    </section>
  )
}

/**
 * A number that counts up when it scrolls into view.
 *
 * The element's text is the FINAL value at render time — the animation only
 * ever replaces text that was already correct. A crawler, a reduced-motion
 * visitor and a browser without IntersectionObserver therefore all read the
 * real figure; the count-up is strictly decorative.
 */
export function CountValue({ value, duration, decimals, separator, style, className }: {
  value: string | number
  duration?: number
  decimals?: number
  separator?: boolean
  style?: CSSProperties
  className?: string
}) {
  const raw = String(value)
  // Only a value that PARSES as a number can be animated. "$49" or "Unlimited"
  // render as-is rather than counting from zero to nonsense.
  const numeric = raw.replace(/[, ]/g, '')
  const n = Number(numeric)
  const animatable = numeric !== '' && Number.isFinite(n)
  if (!animatable) return <span className={className} style={style}>{raw}</span>
  return (
    <span
      className={className}
      style={style}
      data-pg-count={n}
      data-pg-count-ms={duration || 1600}
      data-pg-count-dec={decimals ?? (numeric.includes('.') ? (numeric.split('.')[1] || '').length : 0)}
      data-pg-count-sep={separator === false ? '0' : '1'}
    >
      {raw}
    </span>
  )
}

// ─── Image (enhanced) ────────────────────────────────

/**
 * Loading / decoding attributes for a content image.
 *
 * Every content `<img>` gets these. Before this, the ONLY lazy attribute
 * anywhere in the page runtime was on map iframes, so a gallery-heavy page
 * fetched every image up front. `priority` opts an above-the-fold image (a
 * hero) out — lazy-loading the LCP image actively hurts it.
 */
export function imgLoadingProps(priority?: boolean): {
  loading: 'lazy' | 'eager'; decoding: 'async'; fetchPriority?: 'high'
} {
  return priority
    ? { loading: 'eager', decoding: 'async', fetchPriority: 'high' }
    : { loading: 'lazy', decoding: 'async' }
}

export function ImageRenderer({ block }: { block: ImageBlock }) {
  const s = useS()
  if (!block.url) {
    return (
      <div style={{
        padding: '24px 18px', background: 'var(--pg-bg)',
        border: '1px dashed var(--pg-border)', borderRadius: 8,
        color: 'var(--pg-text-secondary)', fontSize: themeRem(0.85), textAlign: 'center',
      }}>📷 Add image URL in property panel</div>
    )
  }
  const align = block.align || 'center'
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  const ratio = aspectRatioCss(block.aspect)
  const mask = IMAGE_MASKS[block.mask || 'none']
  const hoverCls = block.hoverEffect && block.hoverEffect !== 'none' ? `pg-img-${block.id}` : ''

  return (
    <figure
      className={hoverCls || undefined}
      style={{ margin: 0, display: 'flex', flexDirection: 'column', alignItems: justify }}
    >
      {hoverCls ? <style dangerouslySetInnerHTML={{ __html: imageHoverCss(hoverCls, block.hoverEffect) }} /> : null}
      <img
        src={block.url}
        alt={block.alt || ''}
        // Intrinsic dimensions reserve the box before the bytes arrive; with
        // an explicit aspect ratio the CSS box is already locked, so they are
        // belt-and-braces for browsers that only honour the attributes.
        width={block.naturalWidth || undefined}
        height={block.naturalHeight || undefined}
        {...imgLoadingProps(block.priority)}
        style={{
          maxWidth: block.maxWidth ? `${block.maxWidth}px` : '100%',
          width: '100%',
          // Without an aspect the image keeps its natural height (the
          // historical behaviour); with one the box is fixed and object-fit
          // decides how the picture fills it.
          ...(ratio ? { aspectRatio: ratio, height: 'auto' } : {}),
          objectFit: block.objectFit || (ratio ? 'cover' : undefined),
          objectPosition: block.objectPosition || undefined,
          borderRadius: block.rounded ? 10 : 0,
          boxShadow: block.shadow ? '0 8px 24px rgba(0,0,0,0.15)' : 'none',
          clipPath: mask,
          display: 'block',
        }}
      />
      {block.caption && (
        <figcaption style={{
          marginTop: 6, fontSize: themeRem(0.78), color: 'var(--pg-text-secondary)', textAlign: 'center',
        }}>{s(block.caption)}</figcaption>
      )}
    </figure>
  )
}

// ─── Video ───────────────────────────────────────────

function youtubeId(url: string): string | null {
  // Supports v=, youtu.be/, /embed/
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/)
  return m ? m[1] : null
}
function vimeoId(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  return m ? m[1] : null
}

export function VideoRenderer({ block }: { block: VideoBlock }) {
  const s = useS()
  if (!block.url) {
    return (
      <div style={{
        padding: '24px 18px', background: 'var(--pg-bg)',
        border: '1px dashed var(--pg-border)', borderRadius: 8,
        color: 'var(--pg-text-secondary)', fontSize: themeRem(0.85), textAlign: 'center',
      }}>▶ Add a video URL in property panel</div>
    )
  }
  const aspect = block.aspect || '16:9'
  const [w, h] = aspect.split(':').map(Number)
  const padding = `${(h / w) * 100}%`

  let inner: React.ReactNode = null
  if (block.provider === 'youtube') {
    const id = youtubeId(block.url)
    if (id) {
      inner = (
        <iframe
          src={`https://www.youtube.com/embed/${id}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        />
      )
    }
  } else if (block.provider === 'vimeo') {
    const id = vimeoId(block.url)
    if (id) {
      inner = (
        <iframe
          src={`https://player.vimeo.com/video/${id}`}
          allow="autoplay; fullscreen"
          allowFullScreen
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        />
      )
    }
  } else {
    inner = (
      <video
        src={block.url} controls
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: '#000', borderRadius: 8 }}
      />
    )
  }

  return (
    <figure style={{ margin: 0 }}>
      <div style={{ position: 'relative', width: '100%', paddingBottom: padding, borderRadius: 8, overflow: 'hidden', background: '#000' }}>
        {inner || (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#aaa', fontSize: themeRem(0.85),
          }}>Could not parse video URL</div>
        )}
      </div>
      {block.caption && (
        <figcaption style={{ marginTop: 6, fontSize: themeRem(0.78), color: 'var(--pg-text-secondary)', textAlign: 'center' }}>
          {s(block.caption)}
        </figcaption>
      )}
    </figure>
  )
}

// ─── Map ─────────────────────────────────────────────

// Approx degrees-per-pixel at a given zoom (Web Mercator at the equator).
// Used to build an OSM bbox around a marker for the iframe embed.
function degreesPerPixel(zoom: number): number {
  return 360 / (256 * Math.pow(2, Math.max(1, Math.min(20, zoom))))
}

function osmEmbedUrl(lat: number, lng: number, zoom: number, viewportPx = { w: 600, h: 400 }): string {
  const dpp = degreesPerPixel(zoom)
  const halfW = (viewportPx.w / 2) * dpp
  const halfH = (viewportPx.h / 2) * dpp
  const minLon = lng - halfW
  const maxLon = lng + halfW
  const minLat = lat - halfH
  const maxLat = lat + halfH
  return (
    `https://www.openstreetmap.org/export/embed.html` +
    `?bbox=${minLon}%2C${minLat}%2C${maxLon}%2C${maxLat}` +
    `&layer=mapnik&marker=${lat}%2C${lng}`
  )
}

// Geocode a free-form address to (lat, lng) via OSM Nominatim. Cached
// per-address in module scope so re-renders don't refetch. Nominatim
// asks callers to identify themselves; the runtime user-agent suffices
// for a designer preview, but production sites should self-host or
// switch to a paid geocoder.
const geocodeCache = new Map<string, { lat: number; lng: number } | null>()

async function geocodeOsm(address: string): Promise<{ lat: number; lng: number } | null> {
  const key = address.trim().toLowerCase()
  if (!key) return null
  if (geocodeCache.has(key)) return geocodeCache.get(key) ?? null
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`,
      { headers: { Accept: 'application/json' } }
    )
    if (!res.ok) {
      geocodeCache.set(key, null)
      return null
    }
    const data = await res.json() as Array<{ lat: string; lon: string }>
    if (!Array.isArray(data) || data.length === 0) {
      geocodeCache.set(key, null)
      return null
    }
    const hit = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }
    geocodeCache.set(key, hit)
    return hit
  } catch {
    geocodeCache.set(key, null)
    return null
  }
}

async function geocodeMapbox(address: string, token: string): Promise<{ lat: number; lng: number } | null> {
  const key = `mb:${address.trim().toLowerCase()}`
  if (!address.trim() || !token) return null
  if (geocodeCache.has(key)) return geocodeCache.get(key) ?? null
  try {
    const url =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json` +
      `?access_token=${encodeURIComponent(token)}&limit=1`
    const res = await fetch(url)
    if (!res.ok) {
      geocodeCache.set(key, null)
      return null
    }
    const data = await res.json() as { features?: Array<{ center: [number, number] }> }
    const feat = data.features?.[0]
    if (!feat) {
      geocodeCache.set(key, null)
      return null
    }
    const hit = { lat: feat.center[1], lng: feat.center[0] }
    geocodeCache.set(key, hit)
    return hit
  } catch {
    geocodeCache.set(key, null)
    return null
  }
}

export function MapRenderer({ block }: { block: MapBlock }) {
  const s = useS()
  const provider = block.provider || 'leaflet'
  const zoom = block.zoom ?? 14
  const height = block.height ?? 400
  const needsKey = provider === 'google' || provider === 'mapbox'
  const apiKey = block.apiKey?.trim() || ''

  // Geocoded coordinates when locationMode === 'address'. For Google we
  // skip geocoding because its embed URL accepts an address directly.
  const [resolved, setResolved] = useState<{ lat: number; lng: number } | null>(null)
  const [geoLoading, setGeoLoading] = useState(false)
  const lastQueryRef = useRef<string>('')
  // The interactive maps (Leaflet/OSM, Google) render inside an iframe, which
  // otherwise traps two-finger / wheel scroll and zooms the map. A transparent
  // click-to-activate overlay lets scroll scroll the PAGE until the user clicks
  // the map; leaving the map re-arms the overlay. Pinch-zoom inside the iframe
  // still works once activated.
  const [mapActive, setMapActive] = useState(false)
  const usesIframe = provider !== 'mapbox'

  useEffect(() => {
    if (block.locationMode !== 'address') {
      setResolved(null)
      return
    }
    if (provider === 'google') {
      // Google embed handles address itself.
      setResolved(null)
      return
    }
    const addr = (block.address || '').trim()
    if (!addr) {
      setResolved(null)
      return
    }
    const queryKey = `${provider}::${addr}`
    if (lastQueryRef.current === queryKey && resolved) return
    lastQueryRef.current = queryKey
    setGeoLoading(true)
    const run = provider === 'mapbox'
      ? geocodeMapbox(addr, apiKey)
      : geocodeOsm(addr)
    let cancelled = false
    run.then((hit) => {
      if (cancelled) return
      setResolved(hit)
      setGeoLoading(false)
    })
    return () => { cancelled = true }
  }, [block.locationMode, block.address, provider, apiKey, resolved])

  // ── Validate inputs and render placeholders for incomplete configs ──
  if (needsKey && !apiKey) {
    return (
      <div style={mapPlaceholderStyle}>
        📍 {provider === 'google' ? 'Add a Google Maps API key' : 'Add a Mapbox access token'} in the property panel
      </div>
    )
  }
  if (block.locationMode === 'coordinates') {
    if (block.latitude == null || block.longitude == null) {
      return <div style={mapPlaceholderStyle}>📍 Set latitude and longitude in the property panel</div>
    }
  } else {
    if (!(block.address || '').trim()) {
      return <div style={mapPlaceholderStyle}>📍 Add an address in the property panel</div>
    }
  }

  // ── Build the embed source per provider ──
  let embed: React.ReactNode = null

  if (provider === 'google') {
    const query = block.locationMode === 'coordinates'
      ? `${block.latitude},${block.longitude}`
      : (block.address || '')
    const src =
      `https://www.google.com/maps/embed/v1/place` +
      `?key=${encodeURIComponent(apiKey)}&q=${encodeURIComponent(query)}&zoom=${zoom}`
    embed = (
      <iframe
        src={src}
        title={block.title || 'Map'}
        loading="lazy"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        style={{ width: '100%', height: '100%', border: 0 }}
      />
    )
  } else {
    // Resolve the marker coordinates (geocode if needed)
    let lat: number | null = null
    let lng: number | null = null
    if (block.locationMode === 'coordinates') {
      lat = block.latitude!
      lng = block.longitude!
    } else if (resolved) {
      lat = resolved.lat
      lng = resolved.lng
    }

    if (lat == null || lng == null) {
      embed = (
        <div style={{ ...mapPlaceholderStyle, height: '100%', border: 'none' }}>
          {geoLoading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : '📍 Could not locate address'}
        </div>
      )
    } else if (provider === 'mapbox') {
      // Static map preview — interactive Mapbox needs the JS SDK; for
      // the designer preview a static image is honest and keyless-cost.
      const w = 800
      const h = Math.round(w * (height / Math.max(200, height + 200)))  // rough preview ratio
      const src =
        `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/` +
        `pin-l+ff3b30(${lng},${lat})/${lng},${lat},${zoom},0/${w}x${h}@2x` +
        `?access_token=${encodeURIComponent(apiKey)}`
      embed = (
        <img
          src={src}
          alt={block.title || 'Map'}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )
    } else {
      // Leaflet / OSM iframe — interactive, keyless
      embed = (
        <iframe
          src={osmEmbedUrl(lat, lng, zoom)}
          title={block.title || 'Map'}
          loading="lazy"
          style={{ width: '100%', height: '100%', border: 0 }}
        />
      )
    }
  }

  return (
    <figure style={{ margin: 0 }}>
      <div
        onMouseLeave={() => setMapActive(false)}
        style={{
          position: 'relative',
          width: '100%',
          height,
          borderRadius: 8,
          overflow: 'hidden',
          background: 'var(--pg-bg)',
          border: '1px solid var(--pg-border)',
        }}
      >
        {embed}
        {usesIframe && !mapActive && (
          <div
            onClick={() => setMapActive(true)}
            title="Click to interact with the map"
            style={{ position: 'absolute', inset: 0, zIndex: 1, cursor: 'pointer', background: 'transparent' }}
          />
        )}
      </div>
      {block.caption && (
        <figcaption style={{ marginTop: 6, fontSize: themeRem(0.78), color: 'var(--pg-text-secondary)', textAlign: 'center' }}>
          {s(block.caption)}
        </figcaption>
      )}
    </figure>
  )
}

const mapPlaceholderStyle: React.CSSProperties = {
  padding: '24px 18px',
  background: 'var(--pg-bg)',
  border: '1px dashed var(--pg-border)',
  borderRadius: 8,
  color: 'var(--pg-text-secondary)',
  fontSize: themeRem(0.85),
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
}

// ─── Gallery ─────────────────────────────────────────

/**
 * Image gallery.
 *
 * Two layouts: a responsive grid of equal tiles (the original), and
 * `masonry` — CSS columns, so tall and wide images keep their own
 * proportions instead of being cropped to a uniform box.
 *
 * The lightbox is progressive enhancement: each tile is authored as a real
 * `<a href="<full image>">`, so a click opens the image even with the shared
 * script blocked. `lightboxScript` intercepts that click and shows the image
 * in an overlay with arrow-key paging instead.
 */
export function GalleryRenderer({ block }: { block: GalleryBlock }) {
  const s = useS()
  const gap = block.gap ?? 12
  const masonry = block.layout === 'masonry'
  const ratio = aspectRatioCss(block.aspect)
  const lb = block.lightbox ? `g${block.id}` : null
  const hoverCls = block.hoverEffect && block.hoverEffect !== 'none' ? `pg-gal-${block.id}` : ''

  const wrapStyle: React.CSSProperties = masonry
    ? {
        columnCount: Math.max(1, block.columns || 3),
        ['--pg-gal-gap' as never]: `${gap}px`,
      }
    : {
        display: 'grid',
        gridTemplateColumns: responsiveGrid(block.columns, 160, gap),
        gap,
      }

  return (
    <div className={masonry ? 'pg-gallery-masonry' : undefined} style={wrapStyle}>
      {hoverCls ? <style dangerouslySetInnerHTML={{ __html: imageHoverCss(hoverCls, block.hoverEffect) }} /> : null}
      {(block.items || []).map((it) => {
        const img = it.url ? (
          <img
            src={it.url}
            alt={it.caption || ''}
            {...imgLoadingProps(false)}
            style={{
              width: '100%',
              // Masonry deliberately does NOT constrain height — that is the
              // whole point of the layout.
              ...(masonry
                ? { height: 'auto' }
                : ratio
                  ? { aspectRatio: ratio, height: 'auto', objectFit: 'cover' as const }
                  : { height: 160, objectFit: 'cover' as const }),
              borderRadius: block.rounded ? 8 : 0,
              display: 'block',
            }}
          />
        ) : (
          <div style={{
            width: '100%', height: 160, background: 'var(--pg-bg)',
            border: '1px dashed var(--pg-border)', borderRadius: block.rounded ? 8 : 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--pg-text-secondary)', fontSize: themeRem(0.8),
          }}>Image URL…</div>
        )
        return (
          <figure key={it.id} className={[ITEM_CLASS, hoverCls].filter(Boolean).join(' ')} style={{ margin: 0, position: 'relative' }}>
            {lb && it.url
              ? <a href={it.url} data-pg-lb={lb} data-pg-cap={it.caption || undefined} style={{ display: 'block' }}>{img}</a>
              : img}
            {it.caption && (
              <figcaption style={{ marginTop: 4, fontSize: themeRem(0.72), color: 'var(--pg-text-secondary)', textAlign: 'center' }}>
                {s(it.caption)}
              </figcaption>
            )}
          </figure>
        )
      })}
    </div>
  )
}

// ─── Button / Button group ───────────────────────────

export function ButtonRenderer({ block }: { block: ButtonBlock }) {
  const justify = block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'flex-start'
  return (
    <div style={{ display: 'flex', justifyContent: justify }}>
      <PageButton btn={buttonSpecOf(block)} />
    </div>
  )
}

export function ButtonGroupRenderer({ block }: { block: ButtonGroupBlock }) {
  const justify = block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'flex-start'
  const buttons = (block.buttons || []).filter(Boolean)
  return (
    <div style={{ display: 'flex', gap: block.gap ?? 8, justifyContent: justify, flexWrap: 'wrap' }}>
      {buttons.map((b, i) => (
        <span key={b.id || i} className={ITEM_CLASS} style={{ display: 'inline-flex' }}><PageButton btn={b} /></span>
      ))}
    </div>
  )
}

// ─── Newsletter ──────────────────────────────────────

export function NewsletterRenderer({ block }: { block: NewsletterBlock }) {
  const s = useS()
  const runtime = usePageRuntime()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const align = block.align || 'center'
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const destination = block.destination ?? (block.submitAction ? 'action' : 'submissions')
    const store = destination !== 'action' ? runtime.submitForm : undefined
    if (!block.submitAction && !store) return  // designer canvas — nothing wired
    setBusy(true)
    setError('')
    try {
      runtime.setInput('email', email)         // expose the address as {{email}}
      if (block.submitAction) await runtime.runActionByName(block.submitAction)
      if (store) {
        await store({ block_id: block.id, form_label: block.heading || 'Newsletter',
                      values: { email }, honeypot_name: '_pg_hp', min_seconds: 0 })
      }
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not subscribe')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section style={{
      ...backgroundStyle(block.background),
      padding: 28, borderRadius: 12,
      display: 'flex', flexDirection: 'column', gap: 12,
      alignItems: justify, textAlign: align,
    }}>
      {block.heading && (
        <h2 style={{ margin: 0, fontSize: themeRem(1.4), fontWeight: 800, color: 'var(--pg-heading, var(--pg-text))' }}>{s(block.heading)}</h2>
      )}
      {block.subheading && (
        <p style={{ margin: 0, fontSize: themeRem(0.9), color: 'var(--pg-muted, var(--pg-text-secondary))', maxWidth: 520 }}>
          {s(block.subheading)}
        </p>
      )}
      <form
        onSubmit={onSubmit}
        style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 480, flexWrap: 'wrap' }}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          placeholder={s(block.placeholder || 'you@example.com')}
          style={{
            flex: 1, minWidth: 200, padding: '10px 14px', fontSize: themeRem(0.9),
            border: '1px solid var(--pg-border)', borderRadius: 8,
            background: 'var(--pg-surface-muted, var(--pg-bg))', color: 'var(--pg-text)', outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={busy}
          style={{
            padding: '10px 18px', fontSize: themeRem(0.9), fontWeight: 600,
            background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', border: 'none',
            borderRadius: 8, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
          }}
        >{s(block.buttonLabel || 'Subscribe')}</button>
      </form>
      {done && (
        <p role="status" style={{ margin: 0, fontSize: themeRem(0.88), color: 'var(--pg-success, #16a34a)' }}>
          {s(block.success?.message || 'Thanks — you are subscribed.')}
        </p>
      )}
      {error && (
        <p role="alert" style={{ margin: 0, fontSize: themeRem(0.88), color: 'var(--pg-error, #dc2626)' }}>{error}</p>
      )}
    </section>
  )
}

// ─── Contact form ────────────────────────────────────

export function ContactFormRenderer({ block }: { block: ContactFormBlock }) {
  const s = useS()
  const runtime = usePageRuntime()
  const [values, setValues] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const align = block.align || 'left'
  const setField = (name: string, v: string) => setValues((p) => ({ ...p, [name]: v }))

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const destination = block.destination ?? (block.submitAction ? 'action' : 'submissions')
    const store = destination !== 'action' ? runtime.submitForm : undefined
    if (!block.submitAction && !store) return  // designer canvas — nothing wired
    setBusy(true)
    setError('')
    try {
      for (const f of block.fields) runtime.setInput(f.name, values[f.name] ?? '')
      if (block.submitAction) await runtime.runActionByName(block.submitAction)
      if (store) {
        await store({ block_id: block.id, form_label: block.heading || 'Contact',
                      values: Object.fromEntries(block.fields.map((f) => [f.name, values[f.name] ?? ''])),
                      honeypot_name: '_pg_hp', min_seconds: 0 })
      }
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <section role="status" style={{ padding: 20, border: '1px solid var(--pg-border)', borderRadius: 12 }}>
        <strong>{s(block.success?.title || 'Thank you')}</strong>
        <p style={{ margin: '6px 0 0' }}>{s(block.success?.message || 'We have your message.')}</p>
      </section>
    )
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {(block.heading || block.subheading) && (
        <header style={{ textAlign: align, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {block.heading && (
            <h2 style={{ margin: 0, fontSize: themeRem(1.4), fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>{s(block.heading)}</h2>
          )}
          {block.subheading && (
            <p style={{ margin: 0, fontSize: themeRem(0.85), color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{s(block.subheading)}</p>
          )}
        </header>
      )}
      <form
        onSubmit={onSubmit}
        style={{
          display: 'flex', flexDirection: 'column', gap: 12,
          padding: 20, background: 'var(--pg-surface)',
          border: '1px solid var(--pg-border)', borderRadius: 12,
        }}
      >
        {(block.fields || []).map((f) => (
          <div key={f.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: themeRem(0.78), fontWeight: 600, color: 'var(--pg-muted, var(--pg-text-secondary))' }}>
              {s(f.label)}{f.required && <span style={{ color: 'var(--pg-error)', marginLeft: 2 }}>*</span>}
            </label>
            {f.type === 'textarea' ? (
              <textarea
                rows={4}
                placeholder={s(f.placeholder)}
                required={f.required ?? false}
                value={values[f.name] ?? ''}
                onChange={(e) => setField(f.name, e.currentTarget.value)}
                style={{
                  padding: '8px 12px', fontSize: themeRem(0.88),
                  border: '1px solid var(--pg-border)', borderRadius: 6,
                  background: 'var(--pg-surface-muted, var(--pg-bg))', color: 'var(--pg-text)', outline: 'none',
                  resize: 'vertical', fontFamily: 'inherit',
                }}
              />
            ) : (
              <input
                type={f.type}
                placeholder={s(f.placeholder)}
                required={f.required ?? false}
                value={values[f.name] ?? ''}
                onChange={(e) => setField(f.name, e.currentTarget.value)}
                style={{
                  padding: '8px 12px', fontSize: themeRem(0.88),
                  border: '1px solid var(--pg-border)', borderRadius: 6,
                  background: 'var(--pg-surface-muted, var(--pg-bg))', color: 'var(--pg-text)', outline: 'none',
                }}
              />
            )}
          </div>
        ))}
        <button
          type="submit"
          disabled={busy}
          style={{
            alignSelf: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
            padding: '10px 20px', fontSize: themeRem(0.9), fontWeight: 600,
            background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', border: 'none',
            borderRadius: 8, cursor: 'pointer',
          }}
        >{s(block.buttonLabel)}</button>
        {error && <p role="alert" style={{ margin: 0, fontSize: themeRem(0.85), color: 'var(--pg-error, #dc2626)' }}>{error}</p>}
      </form>
    </section>
  )
}

// ─── Chatbot ─────────────────────────────────────────
// End-user chatbot grounded on the page + (RBAC-gated) project data. The
// actual grounding + LLM call happens server-side; this renderer just manages
// the conversation UI and delegates each turn to the host's `onSend` adapter
// (the standalone runtime POSTs to /app/<project>/api/chat). Without an
// adapter (e.g. static SSR / a preview with no backend) it shows a notice.

export interface ChatTurn { role: 'user' | 'assistant'; content: string }

export function ChatbotRenderer({ block, onSend }: {
  block: ChatbotBlock
  onSend?: (text: string, history: ChatTurn[]) => Promise<string>
}) {
  const s = useS()
  const [messages, setMessages] = useState<ChatTurn[]>(
    () => (block.greeting ? [{ role: 'assistant', content: block.greeting }] : []),
  )
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const height = block.height || 420

  const send = async (text: string) => {
    const q = text.trim()
    if (!q || busy) return
    setError(null)
    const history = messages
    setMessages((m) => [...m, { role: 'user', content: q }])
    setInput('')
    setBusy(true)
    try {
      if (!onSend) throw new Error('The chatbot is not available here.')
      const reply = await onSend(q, history)
      setMessages((m) => [...m, { role: 'assistant', content: reply }])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chat failed')
    } finally {
      setBusy(false)
    }
  }

  const hasUserMsg = messages.some((m) => m.role === 'user')

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height,
      border: '1px solid var(--pg-border)', borderRadius: 12,
      background: 'var(--pg-surface)', overflow: 'hidden',
    }}>
      {block.title && (
        <div style={{
          padding: '10px 14px', borderBottom: '1px solid var(--pg-border)',
          fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))', fontSize: themeRem(0.95),
        }}>{s(block.title)}</div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m, i) => {
          const bubble = {
            alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
            maxWidth: '85%', padding: '8px 12px', borderRadius: 12, fontSize: themeRem(0.88),
            lineHeight: 1.5,
            background: m.role === 'user' ? 'var(--pg-primary)' : 'var(--pg-surface-muted, var(--pg-bg))',
            color: m.role === 'user' ? 'var(--pg-on-primary, #fff)' : 'var(--pg-text)',
          } as CSSProperties
          // The visitor typed plain text; the bot answers in markdown.
          return m.role === 'user'
            ? <div key={i} style={{ ...bubble, whiteSpace: 'pre-wrap' }}>{m.content}</div>
            : <MarkdownText key={i} text={m.content} style={bubble} />
        })}
        {busy && <div style={{ alignSelf: 'flex-start', fontSize: themeRem(0.8), color: 'var(--pg-muted, var(--pg-text-secondary))' }}>…thinking</div>}
        {error && <div style={{ fontSize: themeRem(0.8), color: 'var(--pg-error, #dc2626)' }}>{error}</div>}

        {!hasUserMsg && (block.suggestedQuestions || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {(block.suggestedQuestions || []).map((sq, i) => (
              <button key={i} onClick={() => send(sq)} disabled={busy} style={{
                padding: '5px 10px', fontSize: themeRem(0.78), borderRadius: 999,
                border: '1px solid var(--pg-border)', background: 'transparent',
                color: 'var(--pg-link, var(--pg-primary))', cursor: 'pointer',
              }}>{s(sq)}</button>
            ))}
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(input) }}
        style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid var(--pg-border)' }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          placeholder="Ask a question…"
          style={{
            flex: 1, padding: '8px 12px', fontSize: themeRem(0.88), outline: 'none',
            border: '1px solid var(--pg-border)', borderRadius: 8,
            background: 'var(--pg-bg)', color: 'var(--pg-text)',
          }}
        />
        <button type="submit" disabled={busy || !input.trim()} style={{
          padding: '8px 16px', fontSize: themeRem(0.88), fontWeight: 600, borderRadius: 8,
          border: 'none', background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)',
          cursor: busy ? 'wait' : 'pointer', opacity: busy || !input.trim() ? 0.6 : 1,
        }}>Send</button>
      </form>
    </div>
  )
}

// ─── Auth form ───────────────────────────────────────
// A branded sign-in / sign-up form placed on a smart page. The deployed
// runtime supplies `onSubmit` (wired to the real auth flow) + `twastaSignin`;
// in the designer/SSR no adapter is given, so it renders as a visual form that
// doesn't submit. Theme-token styled so it matches the page palette.

export interface AuthSubmit {
  (mode: 'login' | 'register', values: {
    username: string; password: string; email?: string; phone?: string
    /** Display name from the sign-up form's name fields (`nameFields`). */
    name?: string
  }): Promise<void>
}

type AuthTab = 'login' | 'register'

/** The tab a link asked for: `?mode=register` opens the create-account tab. */
function requestedAuthMode(): AuthTab | null {
  if (typeof window === 'undefined') return null
  try {
    const m = new URLSearchParams(window.location.search).get('mode')
    return m === 'register' ? 'register' : m === 'login' ? 'login' : null
  } catch { return null }
}

export function AuthFormRenderer(props: {
  block: AuthFormBlock
  onSubmit?: AuthSubmit
  twastaSignin?: () => void
  /** Whether the project permits self-registration; gates the sign-up link. */
  allowRegistration?: boolean
  /** Identify-first adapter; defaults to the host's `AuthMenuContext.identify`. */
  identify?: IdentifyAdapter
  /** Host's view of which tab to open (e.g. its own `?mode=` routing). */
  initialMode?: AuthTab
}) {
  const s = useS()
  const menu = useAuthMenu()
  const b = props.block
  const canRegister = props.allowRegistration !== false
  const [tab, setTab] = useState<AuthTab>(() => {
    const want = props.initialMode || requestedAuthMode()
    return want === 'register' && canRegister ? 'register' : 'login'
  })

  const twastaButton = b.showTwasta !== false && props.twastaSignin ? (
    <button type="button" onClick={props.twastaSignin} style={{
      padding: '10px 16px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none',
      background: '#0f172a', color: '#fff', cursor: 'pointer', width: '100%',
    }}>Sign in with Twasta</button>
  ) : undefined

  // `mode: "identify"` — email OR mobile number first, then log in or create
  // the account on the spot (the shared IdentifyStep). Without an adapter
  // (designer / SSR) it renders its first step visual-only.
  const signInFlow = (bare: boolean) => b.mode === 'identify'
    ? (
      <IdentifyStep
        adapter={props.identify ?? menu?.identify ?? null}
        bare={bare}
        labels={{
          // In tabs the heading is drawn above (it belongs to the tab).
          ...(bare ? { heading: '' } : b.heading ? { heading: b.heading } : {}),
          ...(b.subheading ? { intro: b.subheading } : {}),
          ...(b.identifierLabel ? { identifier: b.identifierLabel } : {}),
          ...(b.identifierPlaceholder ? { identifierPlaceholder: b.identifierPlaceholder } : {}),
          ...(b.submitLabel ? { next: b.submitLabel } : {}),
        }}
        footer={twastaButton}
      />
    )
    : <ClassicAuthForm {...props} forcedMode={bare ? 'login' : undefined} bare={bare} />

  if (!b.tabs) {
    return b.mode === 'identify' ? signInFlow(false) : <ClassicAuthForm {...props} />
  }

  // Tabs: one card, two flows. The create-account tab is offered only where
  // the project allows self-registration, so it is never a dead end.
  const tabBtn = (t: AuthTab, label: string): ReactNode => (
    <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
      data-auth-tab={t}
      style={{
        flex: 1, padding: '10px 8px', fontSize: 15, fontWeight: 600, cursor: 'pointer', border: 'none',
        whiteSpace: 'nowrap',
        borderRadius: 10, fontFamily: 'inherit',
        background: tab === t ? 'var(--pg-surface, #fff)' : 'transparent',
        color: tab === t ? 'var(--pg-heading, var(--pg-text))' : 'var(--pg-muted, var(--pg-text-secondary))',
        boxShadow: tab === t ? '0 1px 3px rgba(15,23,42,.12)' : 'none',
      }}>{label}</button>
  )
  const heading = tab === 'register'
    ? (s(b.registerHeading) || 'Create account')
    : (s(b.heading) || 'Sign in')
  const sub = tab === 'register' ? s(b.registerSubheading) : (b.mode === 'identify' ? '' : s(b.subheading))
  return (
    <div data-auth-tabs="" style={{ display: 'grid', gap: 18, width: '100%', color: 'var(--pg-text)' }}>
      {canRegister && (
        <div role="tablist" style={{
          display: 'flex', gap: 4, padding: 4, borderRadius: 14,
          background: 'var(--pg-surface-muted, rgba(100,116,139,.12))',
        }}>
          {tabBtn('login', s(b.signInTabLabel) || 'Sign in')}
          {tabBtn('register', s(b.registerTabLabel) || 'Create account')}
        </div>
      )}
      <div style={{ display: 'grid', gap: 6 }}>
        <h2 style={{ margin: 0, fontSize: themeRem(1.75), fontWeight: 'var(--pg-heading-weight, 700)' as never, fontFamily: 'var(--pg-heading-font, var(--pg-font))', color: 'var(--pg-heading, var(--pg-text))' }}>{heading}</h2>
        {sub && <p style={{ margin: 0, fontSize: 15, color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{sub}</p>}
      </div>
      {tab === 'login'
        ? (
          <>
            {signInFlow(true)}
            {b.signInNote && (
              <p style={{ margin: 0, fontSize: 13, textAlign: 'center', color: 'var(--pg-muted, var(--pg-text-secondary))' }}>
                {s(b.signInNote)}
              </p>
            )}
          </>
        )
        : <ClassicAuthForm {...props} forcedMode="register" bare />}
    </div>
  )
}

/** 0–4: length 8, a number, mixed case, a symbol or 12+ characters. */
export function passwordStrength(pw: string): number {
  if (!pw) return 0
  let n = 0
  if (pw.length >= 8) n++
  if (/\d/.test(pw)) n++
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) n++
  if (/[^A-Za-z0-9]/.test(pw) || pw.length >= 12) n++
  return n
}

/** A number typed without a calling code gets the form's own. */
function withCountryCode(phone: string, code?: string): string {
  const raw = phone.trim()
  if (!raw || !code || raw.startsWith('+')) return raw
  const digits = raw.replace(/[^\d]/g, '').replace(/^0+/, '')
  return digits ? `${code.startsWith('+') ? code : `+${code}`}${digits}` : raw
}

function ClassicAuthForm({ block, onSubmit, twastaSignin, allowRegistration, forcedMode, bare }: {
  block: AuthFormBlock
  onSubmit?: AuthSubmit
  twastaSignin?: () => void
  allowRegistration?: boolean
  /** Locked to one flow (a tab owns the switch). */
  forcedMode?: AuthTab
  /** No card frame and no heading — the tabs supply both. */
  bare?: boolean
}) {
  const s = useS()
  const link = useSiteLink()
  // `mode` is the AUTHORED starting mode; the register link flips it in
  // place. Keeping that in state (rather than navigating) is the whole point
  // of the link — one branded screen serves both flows.
  const [ownMode, setMode] = useState<AuthTab>(block.mode === 'register' ? 'register' : 'login')
  const mode = forcedMode || ownMode
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const contactIdentity = block.registerIdentity === 'contact'
  const nameFields = block.nameFields || 'none'
  const needsConsent = mode === 'register' && !!(block.termsHref || block.privacyHref)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!onSubmit) return            // designer/SSR preview — visual only
    if (mode === 'register') {
      if (nameFields === 'split' && !firstName.trim()) { setError('Enter your first name.'); return }
      if (nameFields === 'single' && !firstName.trim()) { setError('Enter your name.'); return }
      // Registration records BOTH an email (where password links go) and a
      // mobile number; the server enforces the same.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Enter your email address.'); return }
      if (!phone.trim()) { setError('Enter your mobile number.'); return }
      if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
      if (needsConsent && !agreed) { setError('Please agree to the terms to continue.'); return }
    }
    setError(null); setBusy(true)
    const fullPhone = withCountryCode(phone, block.phoneCountryCode)
    const name = [firstName.trim(), nameFields === 'split' ? lastName.trim() : ''].filter(Boolean).join(' ')
    try {
      await onSubmit(mode, {
        // A contact sign-up is keyed by the email.
        username: mode === 'register' && contactIdentity ? email.trim() : username.trim(),
        password,
        email: email.trim() || undefined,
        phone: mode === 'register' ? (fullPhone || undefined) : undefined,
        name: mode === 'register' && name ? name : undefined,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed')
    } finally { setBusy(false) }
  }

  const inp: React.CSSProperties = {
    padding: bare ? '12px 14px' : '10px 12px', border: '1px solid var(--pg-border)', borderRadius: bare ? 10 : 8,
    fontSize: bare ? 15 : 14, width: '100%', boxSizing: 'border-box', outline: 'none',
    background: 'var(--pg-bg)', color: 'var(--pg-text)', fontFamily: 'inherit',
  }
  const lbl: React.CSSProperties = bare
    ? { display: 'grid', gap: 6, fontSize: 14, fontWeight: 600, color: 'var(--pg-heading, var(--pg-text))' }
    : { display: 'grid', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--pg-muted, var(--pg-text-secondary))' }
  const btn: React.CSSProperties = {
    padding: bare ? '14px 16px' : '10px 16px', fontSize: bare ? 16 : 14, fontWeight: 600,
    borderRadius: bare ? 'var(--pg-button-radius, 8px)' : 8, border: 'none',
    background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', cursor: 'pointer', width: '100%',
    fontFamily: 'inherit',
  }
  const strength = passwordStrength(password)
  const linkStyle: React.CSSProperties = { color: 'var(--pg-link, var(--pg-primary))' }

  const frame: React.CSSProperties = bare
    ? { display: 'grid', gap: 16, width: '100%' }
    : {
        display: 'grid', gap: 14, maxWidth: 400, width: '100%', margin: '0 auto', padding: 24,
        border: '1px solid var(--pg-border)', borderRadius: 'var(--pg-radius, 12px)',
        background: 'var(--pg-surface)', color: 'var(--pg-text)',
      }

  return (
    <form onSubmit={submit} style={frame} data-auth-form={mode}>
      {!bare && (
        <h2 style={{ margin: 0, fontSize: themeRem(1.4), fontWeight: 700, fontFamily: 'var(--pg-heading-font, var(--pg-font))', color: 'var(--pg-heading, var(--pg-text))' }}>
          {mode === 'register'
            ? (s(block.registerHeading) || s(block.heading) || 'Create account')
            : (s(block.heading) || 'Sign in')}
        </h2>
      )}
      {!bare && block.subheading && <p style={{ margin: 0, fontSize: 13, color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{s(block.subheading)}</p>}
      {mode === 'register' && nameFields === 'split' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
          <label style={lbl}>First name
            <input style={inp} value={firstName} autoComplete="given-name" onChange={(e) => setFirstName(e.currentTarget.value)} />
          </label>
          <label style={lbl}>Last name
            <input style={inp} value={lastName} autoComplete="family-name" onChange={(e) => setLastName(e.currentTarget.value)} />
          </label>
        </div>
      )}
      {mode === 'register' && nameFields === 'single' && (
        <label style={lbl}>Full name
          <input style={inp} value={firstName} autoComplete="name" onChange={(e) => setFirstName(e.currentTarget.value)} />
        </label>
      )}
      {!(mode === 'register' && contactIdentity) && (
        <label style={lbl}>
          {s(block.identifierLabel) || (mode === 'login' ? 'Email or mobile number' : 'Username')}
          <input style={inp} value={username} autoComplete="username" onChange={(e) => setUsername(e.currentTarget.value)} />
        </label>
      )}
      {mode === 'register' && (
        <label style={lbl}>
          Email
          <input style={inp} type="email" required value={email} autoComplete="email"
            placeholder={s(block.emailPlaceholder) || undefined} onChange={(e) => setEmail(e.currentTarget.value)} />
        </label>
      )}
      {/* Always shown on register: the mobile number is mandatory, so an
          authored `showPhone: false` no longer hides it there. */}
      {mode === 'register' && (
        <label style={lbl}>
          Mobile number
          <span style={{ display: 'flex', gap: 8 }}>
            {block.phoneCountryCode && (
              <span aria-hidden style={{
                ...inp, width: 'auto', flexShrink: 0, display: 'inline-flex', alignItems: 'center',
                background: 'var(--pg-surface-muted, rgba(100,116,139,.08))', fontWeight: 600,
              }}>{block.phoneCountryCode}</span>
            )}
            <input style={inp} type="tel" required value={phone} autoComplete="tel-national"
              placeholder={s(block.phonePlaceholder) || undefined} onChange={(e) => setPhone(e.currentTarget.value)} />
          </span>
        </label>
      )}
      <label style={lbl}>
        Password
        <input style={inp} type="password" value={password}
          autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
          onChange={(e) => setPassword(e.currentTarget.value)} />
      </label>
      {mode === 'register' && (
        <div style={{ display: 'grid', gap: 6, marginTop: -6 }} data-password-strength={strength}>
          <div aria-hidden style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
            {[0, 1, 2, 3].map((i) => (
              <span key={i} style={{
                height: 4, borderRadius: 2,
                background: i < strength
                  ? (strength >= 3 ? 'var(--pg-success, #16a34a)' : strength === 2 ? 'var(--pg-warning, #d97706)' : 'var(--pg-error, #dc2626)')
                  : 'var(--pg-border, #e2e8f0)',
              }} />
            ))}
          </div>
          <span style={{ fontSize: 12, color: 'var(--pg-muted, var(--pg-text-secondary))' }}>
            {s(block.passwordHint) || 'At least 8 characters.'}
          </span>
        </div>
      )}
      {needsConsent && (
        <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, color: 'var(--pg-text)' }}>
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.currentTarget.checked)}
            style={{ width: 18, height: 18, accentColor: 'var(--pg-primary)' }} />
          <span>
            I agree to the{' '}
            {block.termsHref && <a href={link.resolveHref(block.termsHref)} target="_blank" rel="noreferrer" style={linkStyle}>Terms</a>}
            {block.termsHref && block.privacyHref && ' and '}
            {block.privacyHref && <a href={link.resolveHref(block.privacyHref)} target="_blank" rel="noreferrer" style={linkStyle}>Privacy policy</a>}
            .
          </span>
        </label>
      )}
      {error && <div role="alert" style={{ color: 'var(--pg-error, #dc2626)', fontSize: 13 }}>{error}</div>}
      <button type="submit" disabled={busy} style={{ ...btn, opacity: busy ? 0.7 : 1, cursor: busy ? 'wait' : 'pointer' }}>
        {mode === 'register'
          ? (s(block.registerSubmitLabel) || (!forcedMode && s(block.submitLabel)) || 'Create account')
          : (s(block.submitLabel) || 'Sign in')}
      </button>
      {mode === 'register' && block.registerNote && (block.registerNote.title || block.registerNote.body) && (
        <div data-auth-register-note="" style={{
          display: 'flex', gap: 12, alignItems: 'flex-start', padding: 16, borderRadius: 14,
          background: 'var(--pg-surface-muted, rgba(37,99,235,.08))',
        }}>
          {block.registerNote.icon && (
            <span style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', background: 'var(--pg-surface, #fff)',
              color: 'var(--pg-primary)',
            }}><PageIcon name={block.registerNote.icon} size={20} /></span>
          )}
          <span style={{ display: 'grid', gap: 2, fontSize: 14 }}>
            {block.registerNote.title && <strong style={{ color: 'var(--pg-heading, var(--pg-text))' }}>{s(block.registerNote.title)}</strong>}
            {block.registerNote.body && (block.registerNote.href
              ? <a href={link.resolveHref(block.registerNote.href)} style={{ ...linkStyle, textDecoration: 'none' }}>{s(block.registerNote.body)}</a>
              : <span>{s(block.registerNote.body)}</span>)}
          </span>
        </div>
      )}
      {mode === 'login' && !bare && block.showTwasta !== false && twastaSignin && (
        <button type="button" onClick={twastaSignin} style={{ ...btn, background: '#0f172a' }}>Sign in with Twasta</button>
      )}

      {/* Flip between sign-in and sign-up in place. `showRegisterLink` was
          authored (and defaulted true) but read by no renderer, so the field
          did nothing — an author could not offer sign-up from a branded
          login page at all. Suppressed when the project doesn't allow
          self-registration, so the link is never a dead end, and when tabs
          own the switch. */}
      {!forcedMode && block.showRegisterLink !== false && allowRegistration !== false && (
        <button
          type="button"
          onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(null) }}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 13, color: 'var(--pg-link, var(--pg-primary))', textDecoration: 'underline',
          }}
        >
          {s(block.registerLinkLabel)
            || (mode === 'login' ? 'Create an account' : 'Already have an account? Sign in')}
        </button>
      )}

      {mode === 'login' && block.forgotHref && (
        <a
          href={link.resolveHref(block.forgotHref)}
          style={{ fontSize: 12, color: 'var(--pg-muted, var(--pg-text-secondary))', textAlign: 'center' }}
        >
          Forgot your password?
        </a>
      )}
    </form>
  )
}

// ─── Auth menu ───────────────────────────────────────
// Compact auth affordance for a header/footer. Signed out → Log in / Sign up
// buttons; signed in → avatar that opens a dropdown (identity, account panel,
// author links, sign out). Reads AuthMenuContext for the live session; with no
// adapter (designer / SSR) it shows the signed-out buttons as a static preview.

const ALIGN_JUSTIFY: Record<string, CSSProperties['justifyContent']> = {
  left: 'flex-start', center: 'center', right: 'flex-end',
}

export function AuthMenuRenderer({ block }: { block: AuthMenuBlock }) {
  const s = useS()
  const link = useSiteLink()
  const adapter = useAuthMenu()
  const status = adapter?.status ?? 'anonymous'
  const user = adapter?.user ?? null

  const [open, setOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const justify = ALIGN_JUSTIFY[block.align || 'right'] || 'flex-end'
  const wrap: CSSProperties = { display: 'flex', justifyContent: justify, alignItems: 'center', gap: 8 }

  const primaryBtn: CSSProperties = {
    padding: '8px 16px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none',
    background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', cursor: 'pointer',
  }
  const ghostBtn: CSSProperties = {
    padding: '8px 16px', fontSize: 14, fontWeight: 600, borderRadius: 8,
    border: '1px solid var(--pg-border)', background: 'transparent', color: 'var(--pg-text)', cursor: 'pointer',
  }

  // While the session hydrates, hold a placeholder so the header doesn't jump.
  if (status === 'loading') {
    return (
      <div style={wrap}>
        <span style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--pg-surface)', border: '1px solid var(--pg-border)', opacity: 0.6 }} />
      </div>
    )
  }

  // Signed out (or no adapter): Log in / Sign up.
  if (status !== 'authenticated' || !user) {
    const showSignup = block.showSignup !== false && (!adapter || !!adapter.onSignup)
    return (
      <div style={wrap}>
        <button type="button" style={primaryBtn} onClick={() => adapter?.onLogin()}>
          {s(block.loginLabel) || 'Log in'}
        </button>
        {showSignup && (
          <button type="button" style={ghostBtn} onClick={() => (adapter?.onSignup ?? adapter?.onLogin)?.()}>
            {s(block.signupLabel) || 'Sign up'}
          </button>
        )}
      </div>
    )
  }

  // Signed in: avatar + dropdown.
  const initial = (user.username || '?').slice(0, 1).toUpperCase()
  const appRoles = (user.roles || []).filter((r) => r !== 'admin')
  const subtitle = user.isAdmin ? 'Administrator' : (appRoles.length ? appRoles.join(', ') : 'Standard user')
  const extraLinks = (block.links || []).filter((l) => l && l.label)

  const avatar = (size: number) => (
    user.avatarUrl
      ? <img src={user.avatarUrl} alt={user.username}
          style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
      : <span style={{
          width: size, height: size, borderRadius: '50%', background: 'var(--pg-primary)',
          color: 'var(--pg-on-primary, #fff)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: size * 0.42,
        }}>{initial}</span>
  )

  const menuItem: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
    padding: '0.55rem 0.85rem', background: 'none', border: 'none', cursor: 'pointer',
    fontSize: themeRem(0.875), color: 'var(--pg-text)', textDecoration: 'none',
  }

  const doLogout = async () => {
    setOpen(false)
    try { await adapter?.onLogout() } catch { /* idempotent */ }
  }

  return (
    <div style={wrap}>
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          type="button" onClick={() => setOpen((v) => !v)} title={user.username}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: 3, paddingRight: 8,
            background: 'none', border: '1px solid var(--pg-border)', borderRadius: 999, cursor: 'pointer',
          }}
        >
          {avatar(30)}
          <ChevronDown size={15} />
        </button>

        {open && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: 240, zIndex: 200,
            background: 'var(--pg-surface, #fff)', border: '1px solid var(--pg-border)', borderRadius: 12,
            boxShadow: '0 12px 32px rgba(0,0,0,0.14)', overflow: 'hidden', color: 'var(--pg-text)',
          }}>
            <div style={{ padding: '0.85rem', borderBottom: '1px solid var(--pg-border)', display: 'flex', gap: 10, alignItems: 'center' }}>
              {avatar(36)}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: themeRem(0.9), overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.username}</div>
                <div style={{ fontSize: themeRem(0.75), color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{user.email || subtitle}</div>
              </div>
            </div>

            {block.showProfile !== false && (
              <button type="button" style={menuItem} onClick={() => { setOpen(false); setAccountOpen(true) }}>
                <span className="material-icons" style={{ fontSize: 18 }}>manage_accounts</span> My account
              </button>
            )}

            {extraLinks.length > 0 && (
              <>
                <div style={{ borderTop: '1px solid var(--pg-border)' }} />
                {extraLinks.map((l) => (
                  <a key={l.id || l.label} href={link.resolveHref(l.href || '#')} style={menuItem} onClick={() => setOpen(false)}>
                    {l.icon ? <span className="material-icons" style={{ fontSize: 18 }}>{normalizeIconName(l.icon)}</span> : null}
                    {s(l.label)}
                    <AuthMenuBadge link={l} sub={s} />
                  </a>
                ))}
              </>
            )}

            <div style={{ borderTop: '1px solid var(--pg-border)' }} />
            <button type="button" style={{ ...menuItem, color: 'var(--pg-error, #dc2626)' }} onClick={doLogout}>
              <span className="material-icons" style={{ fontSize: 18 }}>logout</span> Sign out
            </button>
          </div>
        )}
      </div>

      {accountOpen && (
        <AuthAccountPanel
          block={block}
          user={user}
          adapter={adapter}
          onClose={() => setAccountOpen(false)}
        />
      )}
    </div>
  )
}

const BADGE_TONES: Record<string, { bg: string; fg: string }> = {
  success: { bg: 'var(--pg-success-bg, #ecfdf5)', fg: 'var(--pg-success, #15803d)' },
  warning: { bg: 'var(--pg-warning-bg, #fffbeb)', fg: 'var(--pg-warning, #b45309)' },
  error:   { bg: 'var(--pg-error-bg, #fef2f2)', fg: 'var(--pg-error, #dc2626)' },
  info:    { bg: 'var(--pg-info-bg, #eff6ff)', fg: 'var(--pg-info, #1d4ed8)' },
  neutral: { bg: 'var(--pg-surface-muted, #f1f5f9)', fg: 'var(--pg-muted, #475569)' },
}

/** A menu link's status chip (`AuthMenuLink.badge`). Nothing while the token
 *  is empty or unresolved — a raw `{{…}}` must never reach the menu. */
function AuthMenuBadge({ link, sub }: { link: AuthMenuLink; sub: (t?: string) => string }) {
  if (!link.badge) return null
  const raw = (sub(link.badge) || '').trim()
  if (!raw || raw.includes('{{')) return null
  const text = link.badgeLabels?.[raw] ?? raw
  const tone = BADGE_TONES[link.badgeTones?.[raw] || 'neutral'] || BADGE_TONES.neutral
  return (
    <span data-pg-menu-badge={raw} style={{
      marginLeft: 'auto', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      lineHeight: 1.6, whiteSpace: 'nowrap', background: tone.bg, color: tone.fg,
    }}>{text}</span>
  )
}

// Self-service account modal for the auth_menu: read-only identity, optional
// profile-image upload, and optional change-password form. Rendered in a
// portal so it escapes the header's overflow. All mutations go through the
// AuthMenuContext adapter — the renderer never calls an API directly.
function AuthAccountPanel({ block, user, adapter, onClose }: {
  block: AuthMenuBlock
  user: AuthMenuUser
  adapter: AuthMenuAdapter | null
  onClose: () => void
}) {
  const [avatarUrl, setAvatarUrl] = useState<string | null | undefined>(user.avatarUrl)
  const [uploading, setUploading] = useState(false)
  const [oldPw, setOldPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const appRoles = (user.roles || []).filter((r) => r !== 'admin')
  const access = user.isAdmin ? 'Administrator' : (appRoles.length ? appRoles.join(', ') : 'Standard user')

  const initial = (user.username || '?').slice(0, 1).toUpperCase()
  const canUpload = block.allowAvatarUpload !== false && !!adapter?.uploadAvatar
  const canChangePw = block.showChangePassword !== false && !!adapter?.changePassword

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !adapter?.uploadAvatar) return
    if (!file.type.startsWith('image/')) { setErr('Please choose an image file.'); return }
    if (file.size > 5 * 1024 * 1024) { setErr('Image must be under 5 MB.'); return }
    setErr(null); setMsg(null); setUploading(true)
    try {
      const url = await adapter.uploadAvatar(file)
      // Cache-bust so the <img> refreshes when the URL path is unchanged.
      setAvatarUrl(url + (url.includes('?') ? '&' : '?') + 't=' + String(Date.now()))
      setMsg('Profile image updated.')
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not upload image.')
    } finally { setUploading(false) }
  }

  const submitPw = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null); setMsg(null)
    if (!adapter?.changePassword) return
    if (newPw.length < 8) { setErr('New password must be at least 8 characters.'); return }
    if (newPw !== confirmPw) { setErr('New passwords do not match.'); return }
    setBusy(true)
    try {
      await adapter.changePassword(oldPw, newPw)
      setOldPw(''); setNewPw(''); setConfirmPw('')
      setMsg('Password changed.')
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not change password.')
    } finally { setBusy(false) }
  }

  const inp: CSSProperties = {
    padding: '10px 12px', border: '1px solid var(--pg-border)', borderRadius: 8, fontSize: 14,
    width: '100%', boxSizing: 'border-box', background: 'var(--pg-bg)', color: 'var(--pg-text)',
  }

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div style={{
        width: '100%', maxWidth: 420, background: 'var(--pg-surface, #fff)', color: 'var(--pg-text)',
        border: '1px solid var(--pg-border)', borderRadius: 14, boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
        padding: 20, display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: themeRem(1.1), fontWeight: 700 }}>My account</h3>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pg-muted, var(--pg-text-secondary))' }}>
            <X size={18} />
          </button>
        </div>

        {/* Profile image */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {avatarUrl
            ? <img src={avatarUrl} alt={user.username} style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
            : <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 22 }}>{initial}</span>}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700 }}>{user.username}</div>
            {user.email ? <div style={{ fontSize: 13, color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{user.email}</div> : null}
            {canUpload && (
              <>
                <input ref={fileRef} type="file" accept="image/*" onChange={onPick} style={{ display: 'none' }} />
                <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()}
                  style={{ marginTop: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: '1px solid var(--pg-border)', background: 'transparent', color: 'var(--pg-text)', cursor: uploading ? 'wait' : 'pointer' }}>
                  {uploading ? 'Uploading…' : 'Change photo'}
                </button>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          <Row label="Username" value={user.username} />
          {user.email ? <Row label="Email" value={user.email} /> : null}
          <Row label="Access" value={access} />
        </div>

        {canChangePw && (
          <form onSubmit={submitPw} style={{ display: 'grid', gap: 10, borderTop: '1px solid var(--pg-border)', paddingTop: 14 }}>
            <div style={{ fontWeight: 700, fontSize: themeRem(0.9) }}>Change password</div>
            <input style={inp} type="password" placeholder="Current password" autoComplete="current-password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} />
            <input style={inp} type="password" placeholder="New password (min 8 chars)" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            <input style={inp} type="password" placeholder="Confirm new password" autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
            <button type="submit" disabled={busy || !oldPw || !newPw}
              style={{ padding: '9px 14px', fontWeight: 600, borderRadius: 8, border: 'none', background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', cursor: busy ? 'wait' : 'pointer', opacity: busy || !oldPw || !newPw ? 0.6 : 1 }}>
              {busy ? 'Saving…' : 'Change password'}
            </button>
          </form>
        )}

        {err && <div style={{ color: 'var(--pg-error, #dc2626)', fontSize: 13 }}>{err}</div>}
        {msg && <div style={{ color: 'var(--pg-success, #16a34a)', fontSize: 13 }}>{msg}</div>}
      </div>
    </div>,
    document.body,
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '3px 0' }}>
      <span style={{ color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  )
}

// ─── Callout ─────────────────────────────────────────
// Variant styles reference theme tokens so each preset can supply its
// own info/success/warning/error palette. The fallback literals only
// fire when the page has not been wrapped with `themeCssVars` (e.g.
// rendered without a theme); they roughly match the original colors so
// pre-theme pages don't regress visually.
const CALLOUT_STYLES: Record<CalloutBlock['variant'], { bg: string; border: string; color: string; icon: string }> = {
  info:    { bg: 'var(--pg-info-bg, rgba(59,130,246,0.08))',    border: 'var(--pg-info, #3b82f6)',    color: 'var(--pg-info, #3b82f6)',    icon: 'ℹ' },
  success: { bg: 'var(--pg-success-bg, rgba(34,197,94,0.08))',  border: 'var(--pg-success, #22c55e)', color: 'var(--pg-success, #22c55e)', icon: '✓' },
  warning: { bg: 'var(--pg-warning-bg, rgba(234,179,8,0.10))',  border: 'var(--pg-warning, #d97706)', color: 'var(--pg-warning, #d97706)', icon: '⚠' },
  error:   { bg: 'var(--pg-error-bg, rgba(239,68,68,0.08))',    border: 'var(--pg-error, #ef4444)',   color: 'var(--pg-error, #ef4444)',   icon: '✕' },
  tip:     { bg: 'var(--pg-info-bg, rgba(0,189,165,0.08))',     border: 'var(--pg-accent)',           color: 'var(--pg-accent)',           icon: '💡' },
}

// Icon size scale — keeps the rendered glyph predictable across the
// three options. Material Icons + emoji glyphs both honor `font-size`.
const CALLOUT_ICON_PX: Record<CalloutIconSize, number> = {
  sm: 16,
  md: 20,
  lg: 28,
}

function renderCalloutIcon(value: string, color: string, size: CalloutIconSize) {
  const px = CALLOUT_ICON_PX[size]
  // Material-icons spans need an explicit width so a missing-glyph
  // fallback (the literal ligature text) doesn't expand the icon column
  // and squeeze the body to a one-word column on narrow screens.
  const baseStyle: React.CSSProperties = {
    fontSize: px,
    width: px,
    height: px,
    lineHeight: 1,
    color,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
  // A NAME resolves through the shared vector set, the same one every other
  // icon surface uses. It used to emit a Material Icons ligature, which is
  // only a glyph where that font is loaded — the platform loads it, a
  // PUBLISHED static site does not, so `icon: "shield"` reached a visitor as
  // the literal word "shield" sitting on top of the callout's title.
  // `PageIcon` needs no font and falls back to a short literal, so an
  // authored glyph ('💡', '→') still passes through.
  if (isVectorIcon(value)) {
    return (
      <span style={baseStyle}>
        <PageIcon name={value} size={px} color={color} />
      </span>
    )
  }
  return <span style={baseStyle}>{normalizeIconName(value)}</span>
}

// ─── Pop-up (modal launcher) ─────────────────────────
// A `popup` SECTION renders a TRIGGER (floating button / inline link / icon
// badge) that opens the section's blocks in a modal dialog. The modal markup mirrors
// the designer's EmbedModalLauncher (themed chrome, backdrop + Escape close)
// so both share one look. Corner-anchored triggers (fab/icon) are portaled to
// document.body so they escape any section overflow; the inline `link` trigger
// renders in the page flow where the pop-up section sits.

type PopupModalSize = 'sm' | 'md' | 'lg'

const POPUP_MODAL_WIDTH: Record<PopupModalSize, number> = {
  sm: 480, md: 720, lg: 960,
}

const POPUP_CORNER: Record<NonNullable<PopupTrigger['position']>, React.CSSProperties> = {
  'bottom-right': { bottom: 'var(--pg-popup-offset)', right: 'var(--pg-popup-offset)' },
  'bottom-left':  { bottom: 'var(--pg-popup-offset)', left: 'var(--pg-popup-offset)' },
  'top-right':    { top: 'var(--pg-popup-offset)', right: 'var(--pg-popup-offset)' },
  'top-left':     { top: 'var(--pg-popup-offset)', left: 'var(--pg-popup-offset)' },
}

// Entrance keyframes for the modal + an attention pulse for the trigger.
// Injected once into <head>; the popup is interactive-surface-only (SSR skips
// it) so this never runs during prerender. Idempotent via the element id.
const POPUP_KEYFRAMES = `
@keyframes pg-pop-backdrop { from { opacity: 0 } to { opacity: 1 } }
@keyframes pg-pop-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes pg-pop-scale { from { opacity: 0; transform: scale(.94) } to { opacity: 1; transform: scale(1) } }
@keyframes pg-pop-up { from { opacity: 0; transform: translateY(24px) } to { opacity: 1; transform: translateY(0) } }
@keyframes pg-pop-down { from { opacity: 0; transform: translateY(-24px) } to { opacity: 1; transform: translateY(0) } }
@keyframes pg-pop-drawer-right { from { transform: translateX(100%) } to { transform: translateX(0) } }
@keyframes pg-pop-drawer-left { from { transform: translateX(-100%) } to { transform: translateX(0) } }
@keyframes pg-pop-pulse { 0% { box-shadow: 0 8px 24px rgba(0,0,0,.22), 0 0 0 0 var(--pg-popup-pulse, rgba(0,0,0,.35)) } 70% { box-shadow: 0 8px 24px rgba(0,0,0,.22), 0 0 0 12px transparent } 100% { box-shadow: 0 8px 24px rgba(0,0,0,.22), 0 0 0 0 transparent } }`

function ensurePopupKeyframes() {
  if (typeof document === 'undefined') return
  if (document.getElementById('pg-popup-keyframes')) return
  const el = document.createElement('style')
  el.id = 'pg-popup-keyframes'
  el.textContent = POPUP_KEYFRAMES
  document.head.appendChild(el)
}

// Resolve the modal's overlay + panel layout for a viewport placement.
// `center`/`top`/`bottom` float a width-capped card; `left`/`right` become
// full-height side drawers. Returns the entrance animation-name too, derived
// from placement when the author picked `slide`.
function popupLayout(
  position: PopupModalPosition,
  size: PopupModalSize,
  animation: PopupAnimation,
): { overlay: React.CSSProperties; panel: React.CSSProperties; anim: string | null } {
  const maxWidth = POPUP_MODAL_WIDTH[size]
  const card: React.CSSProperties = {
    background: 'var(--pg-surface, #fff)', color: 'var(--pg-text, #0f172a)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    border: '1px solid var(--pg-border, #e2e8f0)',
    display: 'flex', flexDirection: 'column',
  }
  let overlay: React.CSSProperties
  let panel: React.CSSProperties
  let slide: string
  switch (position) {
    case 'center':
      overlay = { alignItems: 'center', justifyContent: 'center', padding: '16px' }
      panel = { ...card, width: '100%', maxWidth, maxHeight: '90vh', borderRadius: 12 }
      slide = 'pg-pop-up'
      break
    case 'bottom':
      overlay = { alignItems: 'flex-end', justifyContent: 'center', padding: '0' }
      panel = { ...card, width: '100%', maxWidth, maxHeight: '85vh', borderRadius: '14px 14px 0 0' }
      slide = 'pg-pop-up'
      break
    case 'left':
      overlay = { alignItems: 'stretch', justifyContent: 'flex-start', padding: '0' }
      panel = { ...card, width: '100%', maxWidth, height: '100vh', maxHeight: '100vh', borderRadius: 0 }
      slide = 'pg-pop-drawer-left'
      break
    case 'right':
      overlay = { alignItems: 'stretch', justifyContent: 'flex-end', padding: '0' }
      panel = { ...card, width: '100%', maxWidth, height: '100vh', maxHeight: '100vh', borderRadius: 0 }
      slide = 'pg-pop-drawer-right'
      break
    case 'top':
    default:
      overlay = { alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px' }
      panel = { ...card, width: '100%', maxWidth, maxHeight: '90vh', borderRadius: 12 }
      slide = 'pg-pop-down'
      break
  }
  const anim =
    animation === 'none'  ? null
    : animation === 'fade'  ? 'pg-pop-fade'
    : animation === 'scale' ? 'pg-pop-scale'
    : slide   // 'slide' → placement-derived direction
  return { overlay, panel, anim }
}

// Shared modal dialog. Backdrop click + Escape close; themed via --pg-* tokens.
// `themeVars` carries the pop-up section's color-scheme as a `--pg-*` map: the
// modal is portaled to <body>, so it sits OUTSIDE the section wrapper in the
// DOM and can't inherit the scheme vars set there — we re-apply them on the
// dialog root so the modal surface/text pick up the author's chosen scheme.
// `position` places the dialog (center / top / bottom / left+right drawers) and
// `animation` is its entrance transition when the trigger is clicked.
export function PopupModal({
  title, size, onClose, children, themeVars, position, animation,
}: {
  title?: string
  size?: PopupModalSize
  onClose: () => void
  children: ReactNode
  themeVars?: Record<string, string>
  position?: PopupModalPosition
  animation?: PopupAnimation
}) {
  useEffect(() => {
    ensurePopupKeyframes()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const { overlay, panel, anim } = popupLayout(position || 'top', size || 'md', animation || 'scale')

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex', overflowY: 'auto',
        ...overlay,
        ...(anim ? { animation: 'pg-pop-backdrop .18s ease-out' } : {}),
        ...(themeVars as React.CSSProperties | undefined),
      }}
    >
      <div
        style={{
          ...panel,
          ...(anim ? { animation: `${anim} .26s cubic-bezier(.16,1,.3,1)` } : {}),
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--pg-border, #e2e8f0)',
          flexShrink: 0,
        }}>
          <h3 style={{ margin: 0, fontSize: themeRem(1.1), fontWeight: 700 }}>{title || ''}</h3>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', lineHeight: 0 }}
          >
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>{children}</div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

// Trigger + modal for a pop-up SECTION. The section's own blocks (rendered by
// the host and passed in as `children`) become the modal body; `trigger`
// decides the affordance (floating button / inline link / icon badge) and its
// placement. Used by every interactive surface (in-app preview + standalone
// runtime); the static SSR prerender skips pop-up sections entirely.
// Trigger size presets — padding/font for fab + link, diameter for icon-only,
// and a default glyph size per scale (overridable via `trigger.iconSize`).
const POPUP_FAB_SIZE: Record<NonNullable<PopupTrigger['size']>, { padding: string; fontSize: string; icon: number }> = {
  sm: { padding: '8px 14px',  fontSize: themeRem(0.85), icon: 18 },
  md: { padding: '12px 20px', fontSize: themeRem(0.95), icon: 22 },
  lg: { padding: '15px 26px', fontSize: themeRem(1.05), icon: 26 },
}
const POPUP_ICON_SIZE: Record<NonNullable<PopupTrigger['size']>, { dim: number; icon: number }> = {
  sm: { dim: 46, icon: 20 },
  md: { dim: 56, icon: 24 },
  lg: { dim: 68, icon: 30 },
}

// Apply the author's color overrides (if any) over the variant base. Custom
// background also recolors the border so the fill reads as one solid shape.
function popupTriggerColors(trigger: PopupTrigger): React.CSSProperties {
  const out: React.CSSProperties = {}
  if (trigger.bgColor) { out.background = trigger.bgColor; out.border = `1px solid ${trigger.bgColor}` }
  if (trigger.textColor) out.color = trigger.textColor
  return out
}

// Resolve a trigger's visual look (variant, size, shape, colors, shadow, pulse)
// into a single style object + glyph size — WITHOUT any positioning. The live
// launcher layers fixed/corner placement on top; the designer canvas preview
// uses it inline as-is, so both surfaces stay pixel-identical.
function popupTriggerLook(trigger: PopupTrigger): { style: React.CSSProperties; glyph: number } {
  const variant = trigger.variant || 'primary'
  const size = trigger.size || 'md'
  const colorOverride = popupTriggerColors(trigger)
  const fabSz = POPUP_FAB_SIZE[size]
  const iconSz = POPUP_ICON_SIZE[size]

  if (trigger.style === 'link') {
    return {
      glyph: trigger.iconSize ?? fabSz.icon,
      style: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        borderRadius: trigger.shape === 'rounded' ? 10 : 8, fontWeight: 600, cursor: 'pointer',
        textDecoration: variant === 'link' ? 'underline' : 'none',
        ...BTN_VARIANT_STYLE[variant], padding: fabSz.padding, fontSize: fabSz.fontSize,
        ...colorOverride,
      },
    }
  }

  const isIcon = trigger.style === 'icon'
  const showShadow = trigger.shadow !== false
  return {
    glyph: trigger.iconSize ?? (isIcon ? iconSz.icon : fabSz.icon),
    style: {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      cursor: 'pointer', fontWeight: 700,
      boxShadow: showShadow ? '0 8px 24px rgba(0,0,0,0.22)' : 'none',
      ...BTN_VARIANT_STYLE[variant],
      ...(isIcon
        ? { width: iconSz.dim, height: iconSz.dim, borderRadius: trigger.shape === 'rounded' ? 16 : '50%', padding: 0 }
        : { borderRadius: trigger.shape === 'rounded' ? 14 : 999, padding: fabSz.padding, fontSize: fabSz.fontSize }),
      ...colorOverride,
      ...(trigger.pulse ? { animation: 'pg-pop-pulse 2.2s ease-out infinite' } : {}),
    },
  }
}

// Inline, non-interactive render of a trigger exactly as it will look at
// runtime — used by the designer canvas so authors can fine-tune the look
// without entering preview/run mode. No portal, no fixed positioning, no
// click handler (the modal never opens here).
export function PopupTriggerPreview({ trigger }: { trigger: PopupTrigger }) {
  const s = useS()
  const label = s(trigger.label) || ''
  const icon = trigger.icon ? resolveIcon(trigger.icon) : ''
  const isIcon = trigger.style === 'icon'
  const { style, glyph } = popupTriggerLook(trigger)
  useEffect(() => { if (trigger.pulse) ensurePopupKeyframes() }, [trigger.pulse])
  return (
    <span
      role="img"
      aria-label={`Pop-up trigger preview${label ? `: ${label}` : ''}`}
      style={{ ...style, cursor: 'default', position: 'relative' }}
    >
      {icon && <span style={{ lineHeight: 1, fontSize: glyph }}>{icon}</span>}
      {!isIcon && label && <span>{label}</span>}
    </span>
  )
}

export function PopupLauncher({
  trigger, modalTitle, modalSize, children, themeVars, modalPosition, animation,
}: {
  trigger: PopupTrigger
  modalTitle?: string
  modalSize?: PopupModalSize
  children?: ReactNode
  themeVars?: Record<string, string>
  modalPosition?: PopupModalPosition
  animation?: PopupAnimation
}) {
  const s = useS()
  const [open, setOpen] = useState(false)
  const label = s(trigger.label) || ''
  const icon = trigger.icon ? resolveIcon(trigger.icon) : ''
  const isIcon = trigger.style === 'icon'
  const { style: lookStyle, glyph } = popupTriggerLook(trigger)
  // Pulse animation needs the shared keyframes present before the modal opens.
  useEffect(() => { if (trigger.pulse) ensurePopupKeyframes() }, [trigger.pulse])

  const body = open && (
    <PopupModal
      title={s(modalTitle) || label}
      size={modalSize}
      themeVars={themeVars}
      position={modalPosition}
      animation={animation}
      onClose={() => setOpen(false)}
    >
      {children}
    </PopupModal>
  )

  // Inline link/button — renders in the page flow.
  if (trigger.style === 'link') {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} style={lookStyle}>
          {icon && <span style={{ fontSize: glyph, lineHeight: 1 }}>{icon}</span>}
          {label && <span>{label}</span>}
        </button>
        {body}
      </>
    )
  }

  // Floating (fab) / icon-only — fixed to a screen corner via a portal so they
  // sit above content regardless of where the section is placed.
  const position = trigger.position || 'bottom-right'
  const fab = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label={label || 'Open'}
      style={{
        position: 'fixed', zIndex: 900,
        ['--pg-popup-offset' as string]: `${trigger.offset ?? 24}px`,
        ...POPUP_CORNER[position],
        transition: 'transform .15s ease, box-shadow .15s ease',
        ...lookStyle,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)' }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)' }}
    >
      {icon && <span style={{ lineHeight: 1, fontSize: glyph }}>{icon}</span>}
      {!isIcon && label && <span>{label}</span>}
    </button>
  )

  return (
    <>
      {createPortal(fab, document.body)}
      {body}
    </>
  )
}

export function CalloutRenderer({ block }: { block: CalloutBlock }) {
  const sub = useS()
  const variant = CALLOUT_STYLES[block.variant]
  const layout = calloutLayout(block)
  const iconNode = layout.iconPosition !== 'none' && (
    <div style={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
      {renderCalloutIcon(block.icon || variant.icon, variant.color, layout.iconSize)}
    </div>
  )
  return (
    <aside style={{
      display: 'flex',
      flexDirection: layout.iconPosition === 'top' ? 'column' : 'row',
      alignItems: layout.iconPosition === 'top' && layout.align === 'center' ? 'center' : 'flex-start',
      gap: layout.gap, padding: layout.padding,
      background: variant.bg,
      borderLeft: `3px solid ${variant.border}`,
      borderRadius: 8,
    }}>
      {iconNode}
      {/* min-width: 0 lets the text column shrink below its longest
          word — without it, an unbreakable token like
          {{create_quick_task.status}} would push the column wider than
          the parent and squeeze the icon side. */}
      <div style={{ flex: 1, minWidth: 0, textAlign: layout.align }}>
        {block.title && (
          <div style={{
            fontWeight: 700,
            color: 'var(--pg-heading, var(--pg-text))',
            marginBottom: 4, fontSize: themeRem(0.92),
            wordBreak: 'break-word', overflowWrap: 'anywhere',
          }}>
            {sub(block.title)}
          </div>
        )}
        <div style={{
          fontSize: themeRem(0.88), color: 'var(--pg-text)', lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word', overflowWrap: 'anywhere',
        }}>
          {sub(block.body)}
        </div>
      </div>
    </aside>
  )
}

// Resolve a callout's effective layout (defaults filled in). Shared by
// both the runtime CalloutRenderer and the canvas-side CalloutCanvas
// so the editor and the runtime never disagree on placement.
export function calloutLayout(block: CalloutBlock): {
  iconPosition: CalloutIconPosition
  iconSize: CalloutIconSize
  align: Align
  padding: number
  gap: number
} {
  return {
    iconPosition: block.iconPosition || 'left',
    iconSize: block.iconSize || 'md',
    align: block.align || 'left',
    padding: block.padding ?? 14,
    gap: block.gap ?? 12,
  }
}

// ─── Custom HTML / Custom code (sandboxed) ───────────────────────────
// Shared renderer of `custom_html` AND `custom_code` — the CUSTOM COMPONENT
// escape hatch — on every surface that runs React: the in-app site viewer and
// Run view (RuntimeBlockHost), the downloadable runtime (BlockHost) and the
// designer canvas (`inert`). The static site renders the same document via
// renderSite + customFrameHostScript. When `sandbox !== false` the component
// runs in a `sandbox="allow-scripts"` iframe (opaque origin: no cookies, no
// parent DOM) whose document comes from customFrame.buildCustomFrameDoc: it
// fits its content height, wears the page theme, may load allow-listed CDN
// libraries (`libs`), and may be a Claude Design component rendered verbatim
// (`component`). `sandbox === false` renders the HTML inline with <script>
// tags and inline handlers stripped.

export interface RawHtmlBlockLike {
  id: string
  html?: string
  css?: string
  js?: string
  sandbox?: boolean
  bridge?: CustomCodeBridgeConfig
  libs?: string[]
  component?: CustomComponentSpec
  height?: number
}

// Defense-in-depth for the inline (non-sandboxed) path: drop <script>
// elements, on*= handlers, and javascript: URLs before innerHTML-ing.
export function stripScripts(html: string): string {
  return (html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/javascript:/gi, '')
}

export function CustomHtmlRenderer({ block, inert }: {
  block: RawHtmlBlockLike
  /** Designer canvas: the component runs and shows, but clicks go to the
   *  canvas (selecting the block), not into the frame. */
  inert?: boolean
}) {
  const s = useS()
  const { resolveHref } = useSiteLink()
  const frameRef = useRef<HTMLIFrameElement | null>(null)
  const sandboxed = block.sandbox !== false
  // Substitute {{vars}} then rewrite site-internal links to this surface's
  // base path so "/page-slug" anchors navigate correctly everywhere.
  const html = rewriteInternalHrefs(s(block.html ?? ''), resolveHref)
  // Opt-in page bridge: capability-scoped window.twasta inside the sandbox.
  // Caps come from the surface (CustomCodeBridgeHostContext); a surface
  // without a provider renders the block bridge-less.
  const bridgeCaps = useCustomCodeBridgeCaps()
  const bridgeActive = useCustomCodeBridge(frameRef, sandboxed ? block.bridge : undefined, bridgeCaps)
  const doc = useMemo(() => buildCustomFrameDoc({
    id: block.id, html, css: block.css, js: block.js, libs: block.libs,
    component: block.component, sdk: bridgeActive ? bridgeSdkScript() : undefined,
  }), [block.id, html, block.css, block.js, block.libs, block.component, bridgeActive])
  const height = useCustomFrame(frameRef, { id: block.id, doc, fixedHeight: block.height })

  if (sandboxed) {
    return (
      <iframe
        ref={frameRef}
        // allow-top-navigation-by-user-activation lets a link the visitor
        // clicks navigate the parent (the resolved /base/slug href), instead
        // of trying to load the whole page inside this small iframe.
        sandbox="allow-scripts allow-top-navigation-by-user-activation"
        srcDoc={doc}
        title={block.component ? `Custom component (block ${block.id})` : `Custom HTML (block ${block.id})`}
        style={{
          width: '100%', border: 0, display: 'block', background: 'transparent',
          height: height ?? undefined, minHeight: height ? undefined : 60,
          pointerEvents: inert ? 'none' : undefined,
        }}
      />
    )
  }
  const inline = `<style>${block.css ?? ''}</style>${stripScripts(html)}`
  return <div className="pg-custom-inline" style={{ contain: 'content' }} dangerouslySetInnerHTML={{ __html: inline }} />
}

// ─── Tier-6 marketing blocks ──────────────────────────────────────────
// Implemented in a sibling module (this file is already large) but
// re-exported here so every surface keeps ONE import site for block
// renderers. Re-exported at the bottom because `marketingRenderers` imports
// helpers from this module — with the exports at the end, both halves are
// initialised before either is called.
export {
  BentoGridRenderer, MarqueeRenderer, CounterRenderer, BeforeAfterRenderer,
  CarouselRenderer, TeamRenderer, StepsRenderer, ComparisonTableRenderer,
  SocialLinksRenderer, LottieRenderer, CookieConsentRenderer, FloatingButtonRenderer,
  PageChromeRenderer, AnnouncementRenderer, DarkModeToggle,
} from './marketingRenderers'
